// @ts-nocheck

import type { WorkspaceStateResponse } from './types/workspaceState';
import type { ApiError } from './apiErrors';
import {
    CHANGE_PASSWORD_SUCCESS_USER_MESSAGE,
    MOVED_TO_KEYCLOAK_USER_MESSAGE,
    SESSION_EXPIRED_USER_MESSAGE,
    changePasswordApiError,
    changePasswordRequestError,
    impersonationApiError,
    movedToKeycloakError,
    passwordRecoveryApiError,
    signupActivationApiError,
    terminalAuthRefreshError,
} from './apiErrors';
import {
    OIDC_SESSION_MARKER,
    configureOidc,
    isKeycloakMode,
    refreshKeycloakTokens,
} from './auth/oidc';
import { isMovedToKeycloak, reloadForKeycloak } from './auth/movedToKeycloak';
import { isImpersonatingTab, readAuthToken } from './auth/impersonationTab';
export type { ApiError, ApiErrorCode } from './apiErrors';

// =============================================================================
// ADMIN / RBAC / ENTITLEMENTS / IMPERSONATION TYPES (BAR-135)
// =============================================================================

/** An org's usage terms and use. The allowance is in tokens, each priced at the
 *  Opus input price (`shared.authz.usage_terms`); `allowance_usd` is the same
 *  allowance in billed USD at the org's markup. `null` allowance: unlimited. */
export interface OrganizationUsage {
    allowance_tokens: number | null;
    allowance_usd: number | null;
    allowance_source: 'plan_default' | 'org';
    markup_pct: number;
    tool_call_usd: number;
    billed_usd: number;
    cost_usd: number;
    llm_calls: number;
    tool_calls: number;
    remaining_usd: number | null;
    used_tokens: number;
    remaining_tokens: number | null;
    usage_updated_at: string | null;
}

export interface OrganizationUsagePatch {
    allowance_tokens?: number | null;
    allowance_default?: boolean;
    markup_pct?: number;
    tool_call_usd?: number;
}

/** `GET/PATCH /admin/usage-settings`: the free plan's default allowance. */
export interface UsageSettings {
    free_allowance_tokens: number;
    source: 'admin' | 'default';
    default_tokens: number;
    updated_at: string | null;
    updated_by: string | null;
}

/** One organization row from `GET /admin/organizations` (auth_service).
 *  `modules` is the PARTIAL override map exactly as stored on the org doc
 *  (absent key ⇒ registry default); `effective_modules` is the full computed
 *  per-module map (registry default overlaid with the override) that the
 *  admin panel renders toggles from. Shape mirrors the plan §3.1 org doc plus
 *  the §5.5 computed fields. */
export interface OrganizationSummary {
    org_id: string;                          // doc id = slugify(company_claim)
    company_claim: string;                   // exact legal-entity claim (join key)
    display_name: string;
    sso_domains: string[];
    status: 'active' | 'suspended';
    modules: Record<string, boolean>;        // partial override map (as stored)
    effective_modules: Record<string, boolean>; // full computed map
    user_count: number;
    updated_at?: string | null;
    updated_by?: string | null;
    /** BAR-1528: the commercial plan. `free` is written only by self-serve MCP
     *  signup; an older auth_service omits the field, and a missing plan reads
     *  as `full`. */
    plan?: 'free' | 'full';
    /** Absent from an auth_service older than organization usage allowances. */
    usage?: OrganizationUsage;
}

export interface OrganizationsResponse {
    organizations: OrganizationSummary[];
}

export interface ModuleRegistry {
    defaults: Record<string, boolean>;
    web_modules: string[];
    mcp_access_module: string;
}

/** Body for `POST /admin/organizations`. `company_claim` is the EXACT claim the
 *  org's accounts carry — it is the join key, and `slugify_company(claim)` is
 *  both the doc id and the first segment of every project storage path, so a
 *  typo here makes a tenant nobody resolves to. `modules` is a PARTIAL override
 *  map; absent keys keep their registry default. */
export interface OrganizationCreate {
    company_claim: string;
    display_name?: string;
    sso_domains?: string[];
    modules?: Record<string, boolean>;
    status?: 'active' | 'suspended';
}

/** Patch body for `PATCH /admin/organizations/{org_id}`. All fields optional;
 *  `modules` writes are the super_admin-gated billing surface (§5.5). */
export interface OrganizationPatch {
    modules?: Record<string, boolean>;
    display_name?: string;
    sso_domains?: string[];
    status?: 'active' | 'suspended';
    /** BAR-1528: super_admin only in auth_service, audited as `update_org_plan`. */
    plan?: 'free' | 'full';
    usage?: OrganizationUsagePatch;
}

/** BAR-1528: one organization's MCP usage over a period, from the analytics
 *  dashboard's `GET /api/mcp/orgs` (BigQuery `tool_calls`). */
export interface McpUsageOrg {
    org_id: string;
    calls: number;
    active_people: number;
    /** Calls refused because the usage allowance was used up, including legacy
     *  free-plan refusals in historical rows. */
    limit_refusals: number;
    failures: number;
    failure_rate: number | null;
    first_call: string | null;
    last_call: string | null;
}

export interface McpUsageOrgsResponse {
    period: '7d' | '30d';
    orgs: McpUsageOrg[];
    /** Calls from before per-person tracking, attributed to no organization. */
    unattributed_calls: number;
}

/** BAR-1528: one person of an organization, from `GET /api/mcp/people`. The
 *  email is present only for viewers on the dashboard's identity-reveal list;
 *  everyone else gets `person` ("Person 1", ...). */
export interface McpUsagePerson {
    person: string;
    email?: string;
    org_id: string | null;
    company_name: string | null;
    role: string | null;
    signed_up_at: string | null;
    activated_at: string | null;
    signup_status: string | null;
    calls: number;
    /** Calls refused because the organization's usage allowance was used up. */
    limit_refusals: number;
    last_call: string | null;
    /** The MCP clients this person called from (`claude_ai`, `codex`, ...),
     *  most calls first; `not_recorded` for calls from before the client was
     *  logged. Absent from a dashboard older than the field. */
    clients?: { client: string; calls: number }[];
}

export interface McpUsagePeopleResponse {
    period: '7d' | '30d';
    org_id: string | null;
    revealed: boolean;
    people: McpUsagePerson[];
}

/** One row of the admin audit log (`GET /admin/audit-log`), mirroring the
 *  auth_service `AuditLogEvent` model. `timestamp` is optional to tolerate
 *  malformed legacy rows. */
export interface AdminAuditLogEvent {
    id: string;
    timestamp?: string | null;
    actor_uid?: string | null;
    actor_email?: string | null;
    action?: string | null;
    target_uid?: string | null;
    target_email?: string | null;
    metadata?: Record<string, any>;
    ip_address?: string | null;
    user_agent?: string | null;
}

export interface AdminAuditLogResponse {
    events: AdminAuditLogEvent[];
    next_cursor?: string | null;
}

/** Metadata block returned by `POST /admin/impersonation` (no refresh token — D5). */
export interface ImpersonationMintMeta {
    session_id: string;
    expires_at: string;        // ISO-8601; session ceiling (mint + 60 min)
    actor_email: string;       // the real super_admin
}

/** Full response of `POST /admin/impersonation` — the target's short-lived
 *  ID token, the target UserResponse, and the impersonation metadata.
 *  There is deliberately NO `refresh_token` field (D5). */
export interface ImpersonationMintResponse {
    access_token: string;
    expires_in: number;
    user: Record<string, any>;   // UserResponse of the TARGET
    impersonation: ImpersonationMintMeta;
}

/**
 * The session `POST /signup/activate` adds when auth-service could sign the
 * new account in: the same fields `/login` answers with, so the app signs the
 * person in through the same path. Absent when the server could not sign in;
 * the person then signs in on the login page as before.
 */
export interface SignupActivationSession {
    access_token: string;
    refresh_token?: string;
    token_type?: string;
    expires_in?: number;
    user: Record<string, any>;   // UserResponse, the same shape as /login's user
}

/** `POST /signup/activate` 200 body. */
export interface SignupActivationResult {
    status: string;
    email?: string;
    session?: SignupActivationSession;
}

export interface ArtifactPreviewColumn {
  name: string;
  dtype: string;
}

export interface ArtifactPreview {
  artifact_name: string;
  node_id: string;
  model_id: string;
  run_id: string | null;
  row_count: number;
  column_count: number;
  columns: ArtifactPreviewColumn[];
  rows: (string | null)[][];
  preview_row_count: number;
  truncated: boolean;
}

export interface Reference {
  id: string;
  finding_id?: string;  // Links to findings dict for full content lookup
  title: string;
  summary: string;
  detail: string;
  document?: string;
  document_reference?: string;
  reference_type?: string;  // 'article' or 'concept'
  logical_connections?: string[];
}

export interface Report {
  title: string;
  content: string; // This will be the markdown content
  structured_sections: any[];
}

// Workspace operations types (from API responses)
export type WorkspaceOperationType =
  // Project operations
  | 'set_research_query'
  // Reference materials operations
  | 'add_toc_pointer'
  | 'add_rejected_path'
  | 'add_structural_insight'
  | 'add_effective_query'
  | 'add_iteration_reasoning'
  | 'replace_reference_materials'
  | 'update_reference_materials'
  // Evidence board operations
  | 'add_evidence'
  | 'add_connection'
  | 'update_evidence'
  | 'remove_evidence'
  | 'remove_connection'
  // Report operations
  | 'set_report'
  | 'append_to_report'
  | 'edit_report'
  | 'add_online_support'
  | 'add_upload'
  | 'update_upload'
  | 'remove_upload'
  // Control operations
  | 'reload_project'
  // Phase 2 operations (interactive nodes, compartments)
  | 'add_node_option'
  | 'select_node_option'
  | 'set_node_notes'
  | 'add_node_material'
  | 'remove_node_material'
  | 'set_active_compartment'
  | 'update_compartment_status'
  // Production workspace operations
  | 'set_production_document'
  | 'add_artifact'
  | 'remove_artifact'
  | 'set_active_artifact'
  // Output-package workspace operations
  | 'set_doc_file'
  | 'edit_doc_file'
  | 'approve_doc_file'
  | 'output_package_assembled';

export interface WorkspaceOperation {
  type: WorkspaceOperationType;
  data: Record<string, any>;
}

export interface TransformedResponse {
  success: boolean;
  session_id: string | null;
  response_type: 'clarification' | 'report' | 'error' | 'conversation';
  content: any;
  report?: Report;
  references?: Reference[];
  error?: string;
  proposed_plan?: {
    research_focus: string;
    deliverable_type: string;
    key_areas: string[];
    structure_suggestion: string;
  };
  continue_research?: boolean;
  research_query?: string;
  report_instruction?: string;
  report_id?: string;
  clarification_context?: {
    research_id: string;
    clarification_id: string;
  };
  conversation_context?: {
    response_status: string;
    available_actions: string[];
    research_limitations: boolean;
  };
  workspace_operations?: WorkspaceOperation[];
  message_persisted?: boolean;  // True if server persisted the chat messages
}

interface ApiPayload {
    query: string;
    context: {
        research_depth: string;
        available_reports?: Record<string, any>;
        selected_report_context?: any;
        user_name?: string;
        regulatory_settings?: any;
    };
    user_id?: string;
    research_id?: string | null;
    // NEW CONVERSATIONAL FIELDS:
    use_conversational_flow?: boolean;
    session_id?: string;
    conversation_mode?: string;
    parent_research_id?: string;
    selected_report_id?: string;
    skip_query_refinement?: boolean;
}

// Streaming event type for WebSocket updates
export interface StreamingEvent {
    type: string;
    data: any;
    event_id?: string;
    message_id?: string;
    tracking_id?: string;
    project_id?: string;
    sequence?: number;
    worker_id?: string;
}

// Type for streaming callback function
type StreamingCallback = (event: StreamingEvent) => void;

// BAR-962: the MCP connector gateway answers its refusals as plain text
// (Starlette HTTPException) and its successes as JSON; surface whichever
// message it sent so the person sees the real reason.
async function connectorError(response: Response): Promise<ApiError> {
    const text = await response.text().catch(() => '');
    let detail = '';
    try {
        detail = JSON.parse(text)?.detail ?? '';
    } catch {
        detail = text;
    }
    const err = new Error(detail || 'Connector sign-in failed') as ApiError;
    err.status = response.status;
    return err;
}

/**
 * BAR-1264: which gateway a connector hand-off actually belongs to.
 *
 * The app has one gateway configured for the listed connector
 * (`mcpGatewayUrl`) and, where BARK Build runs as a second MCP server, one
 * for that (`mcpBuildGatewayUrl`). Each gateway issues its own OAuth states
 * and only the issuer can validate them — so a sign-in through a PR preview,
 * or through the Build gateway, used to die on "Invalid or expired OAuth
 * state" from the listed gateway. `/oauth/login` now names its own origin in
 * `gateway_origin`; this decides whether to believe it.
 *
 * Believed only for one of this environment's configured gateways, or a
 * Cloud Run PR preview OF THAT EXACT HOST (`preview-pr-<n>-<configured
 * host>`). The pattern is built from each configured host, so it is never a
 * bare `*.run.app`, and a custom-domain gateway (prod) admits no preview at
 * all. Anything else is logged and ignored in favour of the listed gateway.
 * Without a Build gateway configured this is exactly the one-gateway rule.
 * The value is only ever passed to the call that uses it: nothing is stored.
 */
export function resolveConnectorGatewayOrigin(
    configured: string,
    candidate: string | null | undefined,
    configuredBuild = '',
): string {
    if (!candidate) return configured;
    let candidateUrl: URL;
    try {
        candidateUrl = new URL(candidate);
    } catch {
        console.warn('[Auth] Ignoring an unparseable connector gateway origin');
        return configured;
    }
    // An origin and nothing else — no path, query, fragment or credentials.
    if (
        candidateUrl.username ||
        candidateUrl.password ||
        candidateUrl.search ||
        candidateUrl.hash ||
        (candidateUrl.pathname !== '' && candidateUrl.pathname !== '/')
    ) {
        console.warn('[Auth] Ignoring a connector gateway origin that is not a bare origin');
        return configured;
    }
    for (const gateway of [configured, configuredBuild]) {
        const admitted = admittedGatewayOrigin(gateway, candidateUrl);
        if (admitted) return admitted;
    }
    console.warn('[Auth] Ignoring a connector gateway origin outside this environment');
    return configured;
}

/**
 * The origin to use when `candidateUrl` is the configured `gateway` itself
 * or a Cloud Run PR preview of exactly that host; null otherwise, and for a
 * gateway that is not configured (empty) or cannot be parsed.
 */
function admittedGatewayOrigin(gateway: string, candidateUrl: URL): string | null {
    const gatewayUrl = parsedGateway(gateway);
    if (!gatewayUrl) return null;
    if (candidateUrl.origin === gatewayUrl.origin) return gatewayUrl.origin;
    const previewSuffix = `-${gatewayUrl.host}`;
    const isPreviewOfThisGateway =
        candidateUrl.protocol === 'https:' &&
        gatewayUrl.protocol === 'https:' &&
        gatewayUrl.host.endsWith('.run.app') &&
        candidateUrl.host.endsWith(previewSuffix) &&
        /^preview-pr-\d+$/.test(
            candidateUrl.host.slice(0, candidateUrl.host.length - previewSuffix.length),
        );
    return isPreviewOfThisGateway ? candidateUrl.origin : null;
}

/**
 * A configured gateway as a URL, or null when it is not set or is not an
 * http(s) URL: a mistyped value such as `localhost:5070` parses with the
 * opaque origin "null", which must never match a candidate's.
 */
function parsedGateway(gateway: string): URL | null {
    if (!gateway) return null;
    let url: URL;
    try {
        url = new URL(gateway);
    } catch {
        return null;
    }
    return url.protocol === 'https:' || url.protocol === 'http:' ? url : null;
}

/**
 * True when `resolved` is neither configured gateway itself, i.e. it is an
 * allowlisted PR preview. A preview runs pull-request code that nobody has
 * necessarily reviewed yet, so it gets the least the sign-in needs. The
 * Build gateway named in config.json is not a preview: like the listed one,
 * it is this environment's own gateway and keeps the refresh token.
 */
function isPreviewConnectorGateway(
    configured: string,
    resolved: string,
    configuredBuild = '',
): boolean {
    let resolvedOrigin: string;
    try {
        resolvedOrigin = new URL(resolved).origin;
    } catch {
        return false;
    }
    return ![configured, configuredBuild].some(
        (gateway) => parsedGateway(gateway)?.origin === resolvedOrigin,
    );
}

const LOOPBACK_REDIRECT_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]']);

/**
 * Whether the app may send the browser to the redirect a gateway returned
 * after a connector sign-in. The gateway only ever registers an HTTPS
 * callback or an exact loopback HTTP callback for a native client (bark-mcp
 * oauth.py: "OAuth redirect URI must use HTTPS or exact loopback HTTP"), so
 * anything else, such as `javascript:`, `data:`, another scheme or a URL
 * carrying credentials, is refused instead of followed.
 */
export function isFollowableConnectorRedirect(value: string): boolean {
    let url: URL;
    try {
        url = new URL(value);
    } catch {
        return false;
    }
    if (url.username || url.password) return false;
    if (url.protocol === 'https:') return Boolean(url.hostname);
    if (url.protocol === 'http:') return LOOPBACK_REDIRECT_HOSTS.has(url.hostname);
    return false;
}

/**
 * The Reporting page (auth_service `GET /internal/reporting-preview`). A 403
 * means the organization does not have the `reporting` module; any other
 * failure (5xx, a cold start, the network) is `unavailable` and worth a retry.
 */
export type ReportingPreviewResult =
    | { success: true; html: string }
    | { success: false; reason: 'not_enabled' | 'unavailable' };

const realApi = {
    _regulatoryResearchUrl: '',
    _ceoTeamServiceUrl: '',
    _authServiceUrl: '',
    _workflowOrchestratorUrl: '',
    _reportLibraryUrl: '',
    // BAR-962: origin of the MCP connector gateway (empty = no connector
    // hand-back configured). Bound from config.json like the service URLs.
    _mcpGatewayUrl: '',
    // Origin of the second MCP server, BARK Build, whose sign-ins the app
    // completes as it does the listed gateway's. Empty = only the listed
    // gateway is trusted, exactly as before. It adds to `_mcpGatewayUrl`;
    // with that one empty the connector hand-back stays off.
    _mcpBuildGatewayUrl: '',
    // BAR-1528: the analytics dashboard's backend, which serves MCP usage to
    // the admin Organizations panel (empty = the panel shows no usage).
    _analyticsDashboardUrl: '',
    _chatFreeServiceUrl: '',
    // Origin of BARK's Library (the regulation browser, served at
    // <origin>/library/). Set: Chat opens EUR-Lex article links in a side
    // panel showing the Library. Empty = the links open EUR-Lex, as before.
    _libraryOrigin: '',
    _initializing: null as Promise<void> | null,

    getWorkflowOrchestratorUrl(): string {
        return this._workflowOrchestratorUrl;
    },

    getReportLibraryUrl(): string {
        return this._reportLibraryUrl;
    },

    /**
     * The MCP gateway origin from config.json (`mcpGatewayUrl`), with no
     * trailing slash. Empty until `initialize()` has read config.json, and in
     * an environment without a gateway. The connect page (`/connect`) builds
     * the server URL that people add to their assistant from it.
     */
    getMcpGatewayUrl(): string {
        return this._mcpGatewayUrl;
    },

    /**
     * BAR-1564: who signs people in, as read from config.json by `initialize()`:
     * `keycloak` (BARK's own, in the EU) or `firebase` (before that, and the
     * answer until config.json has been read).
     */
    getAuthProvider(): 'firebase' | 'keycloak' {
        return isKeycloakMode() ? 'keycloak' : 'firebase';
    },

    /** Base URL of the chat-free service (Chat for free-plan accounts); '' when unset. */
    getChatFreeServiceUrl(): string {
        return this._chatFreeServiceUrl;
    },

    /** Origin of BARK's Library (config.json `libraryOrigin`), no trailing
     *  slash; '' when unset or not an http(s) origin (the panel stays off). */
    getLibraryOrigin(): string {
        return this._libraryOrigin;
    },

    async initialize(): Promise<void> {
        if (this._authServiceUrl && this._workflowOrchestratorUrl) return;
        if (this._initializing) return this._initializing;
        const pending = (async () => {
            try {
                const response = await fetch(`/config.json?_=${Date.now()}`);
                if (!response.ok) throw new Error('Configuration request failed');
                const config = await response.json();
                if (config.authServiceUrl && config.workflowOrchestratorServiceUrl) {
                    this._mcpGatewayUrl = (config.mcpGatewayUrl || '').replace(/\/+$/, '');
                    this._mcpBuildGatewayUrl = (config.mcpBuildGatewayUrl || '').replace(/\/+$/, '');
                    this._analyticsDashboardUrl = (config.analyticsDashboardUrl || '').replace(/\/+$/, '');
                    this._chatFreeServiceUrl = (config.chatFreeServiceUrl || '').replace(/\/+$/, '');
                    const libraryOrigin = String(config.libraryOrigin || '').replace(/\/+$/, '');
                    this._libraryOrigin = /^https?:\/\/[A-Za-z0-9.-]+(?::\d{1,5})?$/.test(libraryOrigin) ? libraryOrigin : '';
                    // BAR-1564: Keycloak sign-in when config.json says so; Firebase otherwise.
                    configureOidc(config);
                    // Legacy services (optional, for backwards compatibility)
                    this._regulatoryResearchUrl = config.regulatoryResearchServiceUrl || '';
                    this._ceoTeamServiceUrl = config.ceoTeamServiceUrl || '';
                    this._reportLibraryUrl = config.reportLibraryServiceUrl || '';
                    this._authServiceUrl = config.authServiceUrl;
                    this._workflowOrchestratorUrl = config.workflowOrchestratorServiceUrl;
                    console.log(`[API] Initialized Auth Service URL: ${this._authServiceUrl}`);
                    console.log(`[API] Initialized Workflow Orchestrator Service URL: ${this._workflowOrchestratorUrl}`);
                    console.log(`[API] Initialized Report Library URL: ${this._reportLibraryUrl}`);
                } else {
                     throw new Error("Service URLs not found in config.json");
                }
            } catch (error) {
                console.error('Could not load config.json. Configuration is required.', error);
                throw new Error('Failed to load configuration: ' + error.message);
            }
        })();
        this._initializing = pending;
        try { await pending; }
        finally { if (this._initializing === pending) this._initializing = null; }
    },

    async login(email: string, password: string) {
        await this.initialize();
        try {
            const response = await fetch(`${this._authServiceUrl}/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password })
            });

            if (!response.ok) {
                const errorData = await response.json();
                if (isMovedToKeycloak(response.status, errorData)) {
                    reloadForKeycloak();
                    throw movedToKeycloakError();
                }
                throw new Error(errorData.detail || 'Login failed');
            }

            const result = await response.json();
            console.log('[API] Login successful:', result.user.email);
            return result;
        } catch (error: any) {
            console.error('Login error:', error);
            throw error;
        }
    },

    /**
     * `signupConsentVersion`: the sign-in page offered a free account and showed
     * that consent wording (see components/auth/signupConsent.ts). auth-service
     * then makes an address with no account a free one instead of refusing it.
     */
    async loginWithSSOToken(idToken: string, refreshToken: string | null, signupConsentVersion?: string) {
        await this.initialize();
        try {
            const response = await fetch(`${this._authServiceUrl}/sso/token`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id_token: idToken,
                    refresh_token: refreshToken,
                    ...(signupConsentVersion ? { signup_consent_version: signupConsentVersion } : {}),
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                if (isMovedToKeycloak(response.status, errorData)) {
                    reloadForKeycloak();
                    throw movedToKeycloakError();
                }
                // Free signup's refusals are `{reason, message}`, and the
                // message is written for the person; every other refusal here
                // is a plain string.
                const detail = errorData.detail;
                const signupMessage = detail && typeof detail === 'object' && typeof detail.message === 'string'
                    ? detail.message
                    : undefined;
                const err = new Error(signupMessage || detail || 'SSO login failed') as ApiError;
                err.status = response.status;
                if (signupMessage) err.userMessage = signupMessage;
                throw err;
            }

            const result = await response.json();
            return result;
        } catch (error: any) {
            console.error('SSO login error:', error);
            throw error;
        }
    },

    /**
     * BAR-1564 (keycloak mode): run BARK's account rules for the person who has
     * just signed in on Keycloak. The same rules `/sso/token` applied to a
     * Firebase sign-in (domain map, invite-only, free signup, disabled): the
     * answer is the BARK user, and `claims_changed` says the token's claims are
     * stale (company or role was just written), so the caller refreshes before
     * any other call. Refusals carry the `/sso/token` detail shapes, so the
     * sign-in page words them the same way.
     */
    async provisionSession(
        accessToken: string,
        signupConsentVersion?: string,
    ): Promise<{ claims_changed: boolean; user: Record<string, any> }> {
        await this.initialize();
        const response = await fetch(`${this._authServiceUrl}/session/provision`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(signupConsentVersion ? { signup_consent_version: signupConsentVersion } : {}),
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const detail = errorData.detail;
            const signupMessage = detail && typeof detail === 'object' && typeof detail.message === 'string'
                ? detail.message
                : undefined;
            const detailText = typeof detail === 'string'
                ? detail
                : (detail && typeof detail === 'object' && typeof detail.error === 'string' ? detail.error : '');
            const err = new Error(signupMessage || detailText || 'Sign-in failed') as ApiError;
            err.status = response.status;
            if (signupMessage) err.userMessage = signupMessage;
            throw err;
        }
        const result = await response.json();
        if (!result || typeof result !== 'object' || !result.user || typeof result.user !== 'object') {
            throw new Error('Sign-in returned no user');
        }
        return { claims_changed: result.claims_changed === true, user: result.user };
    },

    /**
     * BAR-1564 (keycloak mode): after Keycloak's own password form succeeded,
     * have auth-service retire every token issued before now (other devices'
     * included). The caller refreshes its own token right after.
     */
    async notifyPasswordChanged(accessToken: string): Promise<void> {
        await this.initialize();
        const response = await fetch(`${this._authServiceUrl}/session/password-changed`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
            },
        });
        if (!response.ok) {
            const err = new Error('Could not finish the password change') as ApiError;
            err.status = response.status;
            throw err;
        }
    },

    /**
     * BAR-962: describe a pending MCP connector authorization (which client,
     * which BARK permissions, where the person is sent back to) so the app
     * can ask the signed-in owner for consent before handing over a session.
     */
    async getConnectorContext(
        state: string,
        gatewayOrigin?: string | null,
    ): Promise<{ client_name: string; scopes: string[]; redirect_origin: string; preview_gateway: string | null }> {
        await this.initialize();
        if (!this._mcpGatewayUrl) {
            const err = new Error('Connector sign-in is not configured') as ApiError;
            err.status = 0;
            throw err;
        }
        // BAR-1264: ask the gateway that issued this state; no other one holds it.
        const gateway = resolveConnectorGatewayOrigin(
            this._mcpGatewayUrl,
            gatewayOrigin,
            this._mcpBuildGatewayUrl,
        );
        const response = await fetch(
            `${gateway}/oauth/login/context?state=${encodeURIComponent(state)}`,
            { method: 'GET', credentials: 'omit' },
        );
        if (!response.ok) {
            throw await connectorError(response);
        }
        const result = await response.json();
        if (typeof result?.client_name !== 'string' || !Array.isArray(result?.scopes) || typeof result?.redirect_origin !== 'string') {
            throw new Error('Connector sign-in returned an invalid response');
        }
        // The consent dialog names a preview so the person knows their
        // sign-in is going to a test build, not the regular gateway.
        const previewGateway = isPreviewConnectorGateway(this._mcpGatewayUrl, gateway, this._mcpBuildGatewayUrl)
            ? new URL(gateway).host
            : null;
        return { ...result, preview_gateway: previewGateway };
    },

    /**
     * BAR-962: hand the signed-in BARK session to the MCP connector gateway.
     *
     * A connector (Claude, Codex, …) sends the person to the app with
     * `?connect=<state>`; they sign in here exactly as usual and approve the
     * connector; then the app posts its existing session to the gateway's
     * callback, which verifies it with auth_service and returns the
     * connector's redirect. Nothing about how the app itself signs in,
     * stores, or refreshes the session changes.
     */
    async completeConnectorSignIn(
        state: string,
        accessToken: string,
        refreshToken: string | null,
        gatewayOrigin?: string | null,
    ): Promise<{ redirect_url: string }> {
        await this.initialize();
        if (!this._mcpGatewayUrl) {
            const err = new Error('Connector sign-in is not configured') as ApiError;
            err.status = 0;
            throw err;
        }
        // BAR-1264: the same gateway that answered the context call above.
        const gateway = resolveConnectorGatewayOrigin(
            this._mcpGatewayUrl,
            gatewayOrigin,
            this._mcpBuildGatewayUrl,
        );
        const response = await fetch(`${gateway}/oauth/login/callback`, {
            method: 'POST',
            credentials: 'omit',
            headers: {
                'Content-Type': 'application/json',
                'X-BARK-OAuth-Flow': 'bark-app',
            },
            body: JSON.stringify({
                state,
                access_token: accessToken,
                // A preview never receives the long-lived refresh token. The
                // gateway accepts null and the link then lasts as long as the
                // access token, about an hour, instead of renewing itself
                // from pull-request code nobody may have reviewed yet.
                // BAR-1564: with Keycloak no gateway ever gets the app's
                // refresh token; the gateway gets its own from Keycloak.
                refresh_token: isKeycloakMode() || isPreviewConnectorGateway(this._mcpGatewayUrl, gateway, this._mcpBuildGatewayUrl)
                    ? null
                    : refreshToken,
                consent: true,
            }),
        });
        if (!response.ok) {
            throw await connectorError(response);
        }
        const result = await response.json();
        if (typeof result?.redirect_url !== 'string' || !result.redirect_url) {
            throw new Error('Connector sign-in returned no redirect');
        }
        if (!isFollowableConnectorRedirect(result.redirect_url)) {
            throw new Error('Connector sign-in returned a redirect BARK will not follow');
        }
        return result;
    },

    async refreshToken(refreshToken: string): Promise<{ access_token: string; refresh_token: string; expires_in: number }> {
        await this.initialize();
        // BAR-1564: a Keycloak session refreshes through the UserManager; its
        // refresh token never leaves oidc-client-ts's store (`refreshToken` is
        // the OIDC_SESSION_MARKER then). The marker means nothing to Firebase.
        if (isKeycloakMode()) return refreshKeycloakTokens();
        if (refreshToken === OIDC_SESSION_MARKER) {
            throw terminalAuthRefreshError('A Keycloak session cannot refresh on Firebase');
        }
        const response = await fetch(`${this._authServiceUrl}/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: refreshToken }),
        });
        if (!response.ok) {
            // A tab still on Firebase after the switch: reload it onto Keycloak.
            // Terminal either way, so a tab the reload did not switch signs in again.
            if (response.status === 410 && isMovedToKeycloak(response.status, await response.json().catch(() => null))) {
                reloadForKeycloak();
                const error = terminalAuthRefreshError('Sign-in moved to Keycloak');
                error.userMessage = MOVED_TO_KEYCLOAK_USER_MESSAGE;
                throw error;
            }
            const error = new Error('Token refresh failed') as ApiError;
            error.status = response.status;
            throw error;
        }
        return response.json();
    },

    async register(email: string, name: string, password: string, company?: string) {
        await this.initialize();
        try {
            const response = await fetch(`${this._authServiceUrl}/register`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, name, password, company })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Registration failed');
            }

            const result = await response.json();
            console.log('[API] Registration successful:', result.email);
            return result;
        } catch (error: any) {
            console.error('Registration error:', error);
            throw error;
        }
    },

    async logout(token: string) {
        await this.initialize();
        try {
            const response = await fetch(`${this._authServiceUrl}/logout`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                console.warn('Logout request failed, but continuing with local logout');
            }

            console.log('[API] Logout successful');
            return true;
        } catch (error: any) {
            console.warn('Logout error, but continuing with local logout:', error);
            return true; // Always succeed locally even if server call fails
        }
    },

    async getCurrentUser(token: string) {
        await this.initialize();
        try {
            const response = await fetch(`${this._authServiceUrl}/me`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                // The status lets App tell an ended impersonation (401) from a blip.
                const err = new Error('Failed to get user info') as ApiError;
                err.status = response.status;
                throw err;
            }

            const result = await response.json();
            return result;
        } catch (error: any) {
            console.error('Get user error:', error);
            throw error;
        }
    },

    async getReportingPreview(token: string): Promise<ReportingPreviewResult> {
        try {
            await this.initialize();
            const response = await fetch(`${this._authServiceUrl}/internal/reporting-preview`, {
                method: 'GET',
                headers: { Authorization: `Bearer ${token}` },
                cache: 'no-store',
            });
            if (response.status === 403) return { success: false, reason: 'not_enabled' };
            if (!response.ok) return { success: false, reason: 'unavailable' };
            return { success: true, html: await response.text() };
        } catch {
            return { success: false, reason: 'unavailable' };
        }
    },

    async forgotPassword(email: string) {
        await this.initialize();
        try {
            const response = await fetch(`${this._authServiceUrl}/forgot-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw passwordRecoveryApiError(errorData, response.status);
            }

            const result = await response.json();
            console.log('[API] Password reset requested');
            return result;
        } catch (error: any) {
            console.error('Forgot password error:', error);
            throw error;
        }
    },

    async resetPassword(token: string, newPassword: string) {
        await this.initialize();
        try {
            const response = await fetch(`${this._authServiceUrl}/reset-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token, new_password: newPassword })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw passwordRecoveryApiError(errorData, response.status);
            }

            const result = await response.json();
            console.log('[API] Password reset successful');
            return result;
        } catch (error: any) {
            console.error('Reset password error:', error);
            throw error;
        }
    },

    /**
     * BAR-1528: finish a free signup from the emailed activation link.
     *
     * Nothing exists until the person proves they own the mailbox, so this one
     * call creates the organization and the account and sets its password. No
     * bearer: the link's token is the proof. Answers `{status: "activated",
     * email}`, plus `session` when the server could also sign the person in;
     * failures carry `detail.reason` (see signupActivationApiError).
     */
    async activateSignup(token: string, password: string): Promise<SignupActivationResult> {
        await this.initialize();
        try {
            const response = await fetch(`${this._authServiceUrl}/signup/activate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token, password })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw signupActivationApiError(errorData, response.status);
            }

            const result = await response.json();
            console.log('[API] Signup activated');
            return result;
        } catch (error: any) {
            console.error('Signup activation error:', error);
            throw error;
        }
    },

    /**
     * BAR-1294: open the caller's own Stripe Billing Portal.
     *
     * No customer id is sent, and none may be: the server resolves the
     * organisation and the caller's own seat from the bearer token. Anything the
     * browser could name here would be a way to ask for somebody else's card.
     */
    async createBillingPortalSession(authToken: string): Promise<{ url: string }> {
        await this.initialize();
        const resolved = await resolveUsableAuthTokenWithMeta(authToken);
        if (!resolved.token) throw sessionExpiredError();

        const response = await fetch(`${this._authServiceUrl}/billing/portal-session`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${resolved.token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            // The backend's `detail` is written for the customer to read -- a
            // colleague with no seat is told to ask whoever bought the
            // subscription. Surface it rather than a status code.
            const message =
                typeof errorData?.detail === 'string'
                    ? errorData.detail
                    : 'Could not open billing. Please try again.';
            throw new Error(message);
        }

        const body = await response.json();
        if (!body?.url) throw new Error('Billing did not return a portal link.');
        return { url: body.url as string };
    },

    async changePassword(
        authToken: string,
        currentPassword: string,
        newPassword: string,
        alreadyRetried = false,
    ) {
        let response: Response;
        let bearerToken: string;
        let usedRefreshResultInPreflight = false;
        try {
            await this.initialize();
            const resolved = await resolveUsableAuthTokenWithMeta(authToken);
            const resolvedToken = resolved.token;
            if (!resolvedToken) {
                clearStoredAuthState();
                throw sessionExpiredError();
            }
            bearerToken = resolvedToken;
            usedRefreshResultInPreflight = resolved.usedRefreshResult;
            response = await fetch(`${this._authServiceUrl}/change-password`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${bearerToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ current_password: currentPassword, new_password: newPassword })
            });
        } catch (error: unknown) {
            if (isSessionExpiredError(error)) throw error;
            const requestError = changePasswordRequestError(error);
            console.error('Change password request error:', requestError);
            throw requestError;
        }

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            if (isMovedToKeycloak(response.status, errorData)) {
                reloadForKeycloak();
                throw movedToKeycloakError();
            }
            const apiError = changePasswordApiError(errorData, response.status);

            if (apiError.code === 'session_expired') {
                if (!alreadyRetried && !usedRefreshResultInPreflight) {
                    let freshToken: string | undefined;
                    try {
                        freshToken = await refreshAuthToken();
                    } catch (error: unknown) {
                        if (isSessionExpiredError(error)) throw error;
                        const requestError = changePasswordRequestError(error);
                        console.error('Change password refresh error:', requestError);
                        throw requestError;
                    }
                    if (freshToken) {
                        return this.changePassword(
                            freshToken,
                            currentPassword,
                            newPassword,
                            true,
                        );
                    }
                }
                console.error('Change password API error:', {
                    status: response.status,
                    code: apiError.code,
                });
                clearStoredAuthState();
                throw sessionExpiredError();
            }
            console.error('Change password API error:', {
                status: response.status,
                code: apiError.code,
            });
            throw apiError;
        }

        const result = await response.json().catch(() => ({}));
        const resultData = result && typeof result === 'object' && !Array.isArray(result)
            ? result
            : {};
        console.log('[API] Password changed successfully');
        return { ...resultData, message: CHANGE_PASSWORD_SUCCESS_USER_MESSAGE };
    },

    // BAR-126: admin user-management endpoints.

    async listUsers(authToken: string) {
        await this.initialize();
        const response = await fetch(`${this._authServiceUrl}/users`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${authToken}` },
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const err = new Error(errorData.detail || 'Failed to list users') as ApiError;
            err.status = response.status;
            throw err;
        }
        return response.json();
    },

    async inviteUser(authToken: string, payload: { email: string; name: string; company?: string; role?: string }) {
        await this.initialize();
        const response = await fetch(`${this._authServiceUrl}/invite`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                email: payload.email,
                name: payload.name,
                company: payload.company || '',
                role: payload.role || 'user',
            }),
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const err = new Error(errorData.detail || 'Invite failed') as ApiError;
            err.status = response.status;
            throw err;
        }
        return response.json();
    },

    async resendInvite(authToken: string, uid: string) {
        await this.initialize();
        const response = await fetch(`${this._authServiceUrl}/users/${encodeURIComponent(uid)}/resend-invite`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${authToken}` },
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const err = new Error(errorData.detail || 'Resend invite failed') as ApiError;
            err.status = response.status;
            throw err;
        }
        return response.json();
    },

    async patchUser(authToken: string, uid: string, payload: Partial<{ disabled: boolean; role: string; company: string; name: string }>) {
        await this.initialize();
        const response = await fetch(`${this._authServiceUrl}/users/${encodeURIComponent(uid)}`, {
            method: 'PATCH',
            headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const err = new Error(errorData.detail || 'Update failed') as ApiError;
            err.status = response.status;
            throw err;
        }
        return response.json();
    },

    async deleteUser(authToken: string, uid: string) {
        await this.initialize();
        const response = await fetch(`${this._authServiceUrl}/users/${encodeURIComponent(uid)}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${authToken}` },
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const err = new Error(errorData.detail || 'Delete failed') as ApiError;
            err.status = response.status;
            throw err;
        }
        return true;
    },

    async getAuditLog(authToken: string, opts?: { cursor?: string; limit?: number }) {
        await this.initialize();
        const params = new URLSearchParams();
        if (opts?.cursor) params.set('cursor', opts.cursor);
        if (opts?.limit) params.set('limit', String(opts.limit));
        const qs = params.toString();
        const url = `${this._authServiceUrl}/admin/audit-log${qs ? `?${qs}` : ''}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${authToken}` },
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const err = new Error(errorData.detail || 'Failed to load audit log') as ApiError;
            err.status = response.status;
            throw err;
        }
        return response.json();
    },

    // BAR-135: per-client entitlements (organizations) admin endpoints.

    // BAR-1528: `includeFree` maps to `include_free`. Free signups create one
    // organization each (personal or per domain), so the panel asks for them
    // only on request. Omitted, the parameter is not sent and the server
    // default (include them) applies.
    async listOrganizations(
        authToken: string,
        opts?: { includeFree?: boolean },
    ): Promise<OrganizationsResponse> {
        await this.initialize();
        const query = typeof opts?.includeFree === 'boolean'
            ? `?include_free=${opts.includeFree ? 'true' : 'false'}`
            : '';
        const response = await fetch(`${this._authServiceUrl}/admin/organizations${query}`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${authToken}` },
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const err = new Error(errorData.detail || 'Failed to load organizations') as ApiError;
            err.status = response.status;
            throw err;
        }
        // The endpoint returns a BARE ARRAY (response_model=list[OrganizationResponse]).
        // This method was typed as a wrapper object since BAR-135, so the panel
        // read `.organizations` off an array, always got undefined, and rendered
        // "No organizations found." over a seeded environment — leaving the
        // module toggles (including BAR-962's "BARK over MCP", the only
        // enablement path for MCP access) unreachable in the running app.
        // Normalize here so both shapes work if the endpoint ever grows a wrapper.
        const data = await response.json();
        return { organizations: Array.isArray(data) ? data : data?.organizations ?? [] };
    },

    async getModuleRegistry(authToken: string): Promise<ModuleRegistry> {
        await this.initialize();
        const response = await fetch(`${this._authServiceUrl}/admin/modules`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${authToken}` },
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const err = new Error(errorData.detail || 'Failed to load module registry') as ApiError;
            err.status = response.status;
            throw err;
        }
        return response.json();
    },

    // `POST /admin/organizations` shipped in BAR-135 with no caller, so the only
    // way to bring an organization into existence was `seed_organizations.py`
    // with ADC for the target project. That was survivable while every module
    // defaulted ON and a doc only ever took something away. It stopped being
    // survivable when `build_artifact` went default-OFF: a customer now has no
    // access until their doc exists, so creating one has to be something an
    // admin can do from the panel at the moment they sell the module.
    async createOrganization(
        authToken: string,
        payload: OrganizationCreate,
    ): Promise<OrganizationSummary> {
        await this.initialize();
        const response = await fetch(`${this._authServiceUrl}/admin/organizations`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const err = new Error(
                errorData.detail || 'Failed to create organization',
            ) as ApiError;
            err.status = response.status;
            throw err;
        }
        return response.json();
    },

    /** BAR-1528: whether this deployment names an analytics dashboard, i.e.
     *  whether the admin Organizations panel can show MCP usage at all. */
    async hasMcpUsage(): Promise<boolean> {
        await this.initialize();
        return Boolean(this._analyticsDashboardUrl);
    },

    async _mcpUsageGet(authToken: string, path: string): Promise<any> {
        await this.initialize();
        if (!this._analyticsDashboardUrl) {
            throw new Error('MCP usage is not configured for this deployment');
        }
        const response = await fetch(`${this._analyticsDashboardUrl}${path}`, {
            headers: { 'Authorization': `Bearer ${authToken}` },
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const detail = typeof errorData.detail === 'string' ? errorData.detail : '';
            const err = new Error(detail || 'Failed to load MCP usage') as ApiError;
            err.status = response.status;
            throw err;
        }
        return response.json();
    },

    /** Per-organization MCP usage for the admin Organizations panel. */
    async getMcpUsageOrgs(authToken: string, period: '7d' | '30d'): Promise<McpUsageOrgsResponse> {
        return this._mcpUsageGet(authToken, `/api/mcp/orgs?period=${period}`);
    },

    /** The people of one organization, with their MCP usage. */
    async getMcpUsagePeople(
        authToken: string,
        orgId: string,
        period: '7d' | '30d',
    ): Promise<McpUsagePeopleResponse> {
        return this._mcpUsageGet(
            authToken,
            `/api/mcp/people?org_id=${encodeURIComponent(orgId)}&period=${period}`,
        );
    },

    async getUsageSettings(authToken: string): Promise<UsageSettings> {
        await this.initialize();
        const response = await fetch(`${this._authServiceUrl}/admin/usage-settings`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${authToken}` },
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const err = new Error(errorData.detail || 'Failed to load usage settings') as ApiError;
            err.status = response.status;
            throw err;
        }
        return response.json();
    },

    async patchUsageSettings(
        authToken: string,
        freeAllowanceTokens: number,
    ): Promise<UsageSettings> {
        await this.initialize();
        const response = await fetch(`${this._authServiceUrl}/admin/usage-settings`, {
            method: 'PATCH',
            headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ free_allowance_tokens: freeAllowanceTokens }),
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const err = new Error(
                typeof errorData.detail === 'string' ? errorData.detail : 'Failed to update usage settings',
            ) as ApiError;
            err.status = response.status;
            throw err;
        }
        return response.json();
    },

    async patchOrganization(
        authToken: string,
        orgId: string,
        payload: OrganizationPatch,
    ): Promise<OrganizationSummary> {
        await this.initialize();
        const response = await fetch(
            `${this._authServiceUrl}/admin/organizations/${encodeURIComponent(orgId)}`,
            {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            },
        );
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const err = new Error(errorData.detail || 'Failed to update organization') as ApiError;
            err.status = response.status;
            throw err;
        }
        return response.json();
    },

    // BAR-135: super-admin impersonation. Mint returns the TARGET's short-lived
    // ID token with NO refresh token (D5); `end` is authenticated with the
    // REFRESHED real-admin token (§6.4).

    async impersonateUser(
        authToken: string,
        targetUid: string,
        reason?: string,
    ): Promise<ImpersonationMintResponse> {
        await this.initialize();
        const response = await fetch(`${this._authServiceUrl}/admin/impersonation`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ target_uid: targetUid, reason: reason || null }),
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw impersonationApiError(errorData, response.status);
        }
        return response.json();
    },

    async endImpersonation(authToken: string, sessionId: string): Promise<void> {
        await this.initialize();
        const response = await fetch(
            `${this._authServiceUrl}/admin/impersonation/${encodeURIComponent(sessionId)}/end`,
            {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${authToken}` },
            },
        );
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const err = new Error(errorData.detail || 'Failed to end impersonation') as ApiError;
            err.status = response.status;
            throw err;
        }
    },

    async testTool(toolName: string, parameters: Record<string, any>): Promise<any> {
        await this.initialize();
        try {
            console.log('[API] Testing tool:', toolName, 'with parameters:', parameters);
            // BAR-750: /tools/execute is Depends(require_auth) (WOS main.py) — it
            // only ever "worked" tokenless because ENFORCE_AUTH=false demoted the
            // caller to anonymous. Under enforcement this 401s and the Search tab
            // dies with a visible error, so send the (refreshed) bearer token.
            const token = await resolveUsableAuthToken();
            if (!token) {
                // Surface this immediately in dev: under ENFORCE_AUTH the request
                // below will 401 and the catch turns it into a generic failure,
                // which hides that the real cause was token resolution.
                console.warn('[API] testTool: no usable auth token — request will be rejected');
            }
            const response = await fetch(`${this._workflowOrchestratorUrl}/tools/execute`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({ tool_name: toolName, parameters })
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('[API] Tool test error:', errorText);
                throw new Error(`Tool test failed with status: ${response.status}`);
            }

            const result = await response.json();
            console.log('[API] Tool test result:', result);
            return result;
        } catch (error: any) {
            console.error('[API] Tool test error:', error);
            throw error;
        }
    },

    async fetchReference(
        workspaceId: string,
        compartmentId: string,
        documentId: string,
        path: string,
        createFinding: boolean = false,
    ): Promise<{ success: boolean; finding_id?: string; already_existed?: boolean; content?: any; error?: string }> {
        await this.initialize();
        try {
            // BAR-750: fetch-reference is Depends(require_auth) (WOS main.py) —
            // tokenless calls 401 under enforcement, breaking both the citation
            // preview and the create_finding=true WRITE path.
            const token = await resolveUsableAuthToken();
            const response = await fetch(
                `${this._workflowOrchestratorUrl}/workspace/${workspaceId}/compartments/${compartmentId}/fetch-reference`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                    },
                    body: JSON.stringify({
                        document_id: documentId,
                        path,
                        create_finding: createFinding,
                    }),
                }
            );
            if (!response.ok) {
                const errorText = await response.text();
                return { success: false, error: `Request failed: ${response.status} ${errorText}` };
            }
            return await response.json();
        } catch (error: any) {
            console.error('[API] fetchReference error:', error);
            return { success: false, error: error.message || 'Unknown error' };
        }
    },

    async sendMessage(
        payload: ApiPayload,
        token: string,
        serviceType: string = 'regulatory_expert'
    ): Promise<TransformedResponse> {
        await this.initialize();

        // HARDCODED: Use workflow orchestrator if available (for development testing)
        // TODO: Add UI selector to choose between services
        if (this._workflowOrchestratorUrl) {
            console.log('[API] Using Workflow Orchestrator Service (hardcoded for dev)');
            return this.sendToWorkflowOrchestrator(payload, token);
        }

        // Route to appropriate service based on service type
        if (serviceType === 'regulatory_assistant') {
            return this.sendToCeoTeamService(payload, token);
        } else {
            return this.sendToRegulatoryResearchService(payload, token);
        }
    },

    // ── Home Chat Agent ──

    async homeAgentChat(
        query: string,
        history: { role: string; content: string }[],
        token: string
    ): Promise<{ content: string; suggested_actions: HomeAgentAction[] }> {
        await this.initialize();
        // Bound the fetch so a Cloud Run cold start (~15-25s on dev) doesn't
        // leave the user staring at a frozen UI with no feedback. 45s gives
        // a warm pod 95th-percentile headroom and a cold pod ~25s + LLM
        // first-token latency. The caller (HomeChatAgent) inspects
        // ``error.name === 'AbortError'`` or ``[COLD_START_TIMEOUT]`` in
        // the message to classify this as a cold-start (retry-friendly)
        // rather than a real failure.
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 45000);
        try {
            const response = await fetch(
                `${this._workflowOrchestratorUrl}/api/home-agent/chat`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ query, history }),
                    signal: controller.signal,
                }
            );
            if (!response.ok) {
                const detail = await response.text().catch(() => '');
                throw new Error(`Home agent request failed (${response.status}): ${detail}`);
            }
            return response.json();
        } catch (err: any) {
            if (err?.name === 'AbortError') {
                const e = new Error('[COLD_START_TIMEOUT] Home agent request exceeded 45s — service likely warming up.');
                (e as any).isColdStart = true;
                throw e;
            }
            throw err;
        } finally {
            clearTimeout(timeoutId);
        }
    },

    async getOnboardingStatus(token: string): Promise<{ completed: boolean }> {
        await this.initialize();
        const response = await fetch(
            `${this._workflowOrchestratorUrl}/api/home-agent/onboarding-status`,
            {
                headers: { 'Authorization': `Bearer ${token}` },
            }
        );
        if (!response.ok) {
            // Only a string detail is a message. A structured one, such as
            // BAR-1528's {code: "web_access_not_in_plan"}, would otherwise
            // reach the page as "[object Object]".
            let detail = '';
            try {
                const body = await response.json();
                if (typeof body?.detail === 'string') detail = body.detail;
            } catch { /* noop */ }
            throw new Error(detail || `Could not check onboarding status (${response.status})`);
        }
        return response.json();
    },

    async completeOnboarding(token: string): Promise<{ completed: boolean }> {
        await this.initialize();
        const response = await fetch(
            `${this._workflowOrchestratorUrl}/api/home-agent/onboarding-complete`,
            {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            }
        );
        if (!response.ok) {
            // String details only, as in getOnboardingStatus above.
            let detail = '';
            try {
                const body = await response.json();
                if (typeof body?.detail === 'string') detail = body.detail;
            } catch { /* noop */ }
            throw new Error(detail || `Could not save onboarding progress (${response.status})`);
        }
        return response.json();
    },

    // Streaming event callback (set via setStreamingCallback)
    _streamingCallback: null as StreamingCallback | null,

    // Optional token-refresh callback. App.tsx sets this on mount;
    // sendToWorkflowOrchestratorStreaming calls it after a WS close with
    // code 4001 (orchestrator's "Authentication required") so the next
    // reconnect attempt uses a fresh Firebase ID token instead of looping
    // with the dead one. Without this, an in-flight WS would never see
    // tokens refreshed by App.tsx's 50-min proactive interval — the value
    // is captured by closure when sendToWorkflowOrchestratorStreaming runs.
    // BAR-41 / 2026-05-13 incident.
    _tokenRefreshCallback: null as (() => Promise<string>) | null,

    // BAR-135: optional global auth-failure handler. App.tsx registers this
    // ONLY while impersonating (unset otherwise). Any REST 401 that resolves
    // to "session genuinely expired" (via `sessionExpiredError()`) fires it so
    // the impersonation flow can auto-exit-restore the real admin instead of
    // stranding the operator on a dead target token (§6.4). A no-op when unset,
    // so it never changes non-impersonation behaviour.
    _globalAuthFailureHandler: null as (() => void) | null,

    // Active WebSocket connection for sending commands
    _activeWebSocket: null as WebSocket | null,

    // Outbound commands queued while the WS is closed / reconnecting.
    // Drained from ws.onopen so messages sent during the reconnect window
    // (e.g. a user accepting deep_research while the WS is mid-rebalance)
    // are delivered when the connection comes back instead of being
    // silently dropped. Observed during the 2026-05-19 dev v3 burn-in
    // test (BAR-138): first deep-research-accept disappeared into the
    // void mid-reconnect.
    _pendingCommands: [] as Array<{ payload: Record<string, any>; label: string }>,

    // Cap on the pending-commands queue. A stuck-CONNECTING WS combined
    // with rapid user input could otherwise grow the queue unboundedly.
    // 20 is enough headroom for a user spam-clicking through a 5-second
    // reconnect window; beyond that we drop the OLDEST item with a
    // warning so the most-recent intent wins. Review on PR #143.
    _MAX_PENDING_COMMANDS: 20,

    // Try to send a command immediately; queue it only when an active WS
    // is in CONNECTING (reconnect lifecycle is live and ws.onopen WILL
    // fire to drain). Returns false when there is no WS at all, when
    // the WS is CLOSED, or when it's CLOSING — in those states no
    // drain will ever run, so queueing would just leak the command
    // into the next, unrelated session.
    _enqueueOrSendCommand(payload: Record<string, any>, label: string): boolean {
        const ws = this._activeWebSocket;
        if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify(payload));
            console.log(`[API] Sent command: ${label}`);
            return true;
        }
        if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
            // No reconnect lifecycle to ride on — refusing to queue prevents
            // the queued payload from leaking into the next session's
            // ws.onopen (which would deliver it to a different orchestrator
            // run). Caller surfaces this as "no active workflow" rather
            // than pretending the send worked.
            console.warn(`[API] Cannot send command — no live WS to drain into: ${label}`);
            return false;
        }
        // ws.readyState === CONNECTING — a reconnect IS in flight; queue.
        // Cap the queue: drop the oldest entry if at capacity (most-recent
        // intent is more relevant than a stale buffered command).
        if (this._pendingCommands.length >= this._MAX_PENDING_COMMANDS) {
            const dropped = this._pendingCommands.shift();
            console.warn(`[API] Pending command queue at cap (${this._MAX_PENDING_COMMANDS}) — dropping oldest: ${dropped?.label ?? '<unknown>'}`);
        }
        this._pendingCommands.push({ payload, label });
        console.log(`[API] Queued command (WS reconnecting): ${label}`);
        return true;
    },

    // Drain queued commands once the WS comes back. Called from
    // ws.onopen in sendToWorkflowOrchestratorStreaming.
    _drainPendingCommands() {
        const ws = this._activeWebSocket;
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        let drained = 0;
        while (this._pendingCommands.length > 0) {
            const item = this._pendingCommands.shift();
            if (!item) break;
            try {
                ws.send(JSON.stringify(item.payload));
                drained += 1;
                console.log("[API] Drained queued command:", item.label);
            } catch (e) {
                // If send throws (WS closed mid-drain), put the item
                // back at the head and stop — the next reconnect will
                // resume from here.
                this._pendingCommands.unshift(item);
                console.warn("[API] Drain stopped, send failed:", item.label, e);
                break;
            }
        }
        if (drained > 0) {
            console.log(`[API] Drained ${drained} queued command(s)`);
        }
    },

    setStreamingCallback(callback: StreamingCallback | null) {
        this._streamingCallback = callback;
    },

    // Tear down the MAIN streaming session (the orchestrator command WS),
    // NOT the project subscription socket. Closes the active WS, clears the
    // streaming callback, and drops any queued outbound commands so nothing
    // leaks into the next project's session. BAR-53.
    closeActiveSession() {
        try {
            if (this._activeWebSocket && this._activeWebSocket.readyState === WebSocket.OPEN) {
                this._activeWebSocket.close();
            }
        } catch (e) {
            // Teardown is best-effort, but log so a misbehaving WS close is debuggable.
            console.warn('[apiService] closeActiveSession: error closing active WS', e);
        }
        this._activeWebSocket = null;
        this._streamingCallback = null;
        this._pendingCommands = [];
    },

    setTokenRefreshCallback(callback: (() => Promise<string>) | null) {
        this._tokenRefreshCallback = callback;
    },

    // BAR-135: register/clear the global auth-failure handler (see field docs).
    setGlobalAuthFailureHandler(callback: (() => void) | null) {
        this._globalAuthFailureHandler = callback;
    },

    // Cancel is the one command we do NOT want to queue — if the user
    // hits "stop" while the WS is mid-reconnect, the underlying run may
    // already have terminated on the server side. Just drop with a warning.
    sendCancelCommand(): boolean {
        if (this._activeWebSocket && this._activeWebSocket.readyState === WebSocket.OPEN) {
            this._activeWebSocket.send(JSON.stringify({ command: "cancel" }));
            console.log("[API] Sent cancel command");
            return true;
        }
        console.warn("[API] Cannot send cancel - no active WebSocket");
        return false;
    },

    // Send comment/intervention to active workflow. Queues if WS is mid-
    // reconnect so messages sent during the reconnect window are not
    // silently dropped (BAR-138 dev burn-in observation).
    sendCommentCommand(text: string): boolean {
        return this._enqueueOrSendCommand(
            { command: "comment", text },
            `comment: ${text.substring(0, 40)}`,
        );
    },

    // Send validate command — triggers validate_work in the active
    // orchestrator session. Backed by the WS `{command: "validate"}` handler;
    // results stream back via the usual WORKSPACE_UPDATE events.
    sendValidateCommand(mode: 'full' | 'incremental' = 'full'): boolean {
        return this._enqueueOrSendCommand(
            { command: "validate", input: { mode } },
            `validate (mode=${mode})`,
        );
    },

    // Send answer to a pending ask_user_question on the active WebSocket.
    // Queues if WS is mid-reconnect (BAR-138).
    sendQuestionAnswer(query: string, projectId: string, context: Record<string, any>): boolean {
        return this._enqueueOrSendCommand(
            {
                command: "message",
                input: {
                    query,
                    project_id: projectId,
                    conversation_history: [],
                    context,
                },
            },
            `question answer: ${query.substring(0, 40)}`,
        );
    },

    // Check if WebSocket is connected
    isWebSocketConnected(): boolean {
        return this._activeWebSocket !== null && this._activeWebSocket.readyState === WebSocket.OPEN;
    },

    async sendToWorkflowOrchestrator(
        payload: ApiPayload,
        token: string
    ): Promise<TransformedResponse> {
        // Use WebSocket for streaming if callback is set
        if (this._streamingCallback) {
            return this.sendToWorkflowOrchestratorStreaming(payload, token);
        }

        // Fallback to HTTP if no streaming callback
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 1800000); // 30 minute timeout

        try {
            console.log("[API] Sending to Workflow Orchestrator Service (HTTP):", { endpoint: '/invoke', payload });
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
            const response = await fetch(`${this._workflowOrchestratorUrl}/invoke`, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload),
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const errorText = await response.text();
                console.error("Workflow Orchestrator Service Error Response:", errorText);
                throw new Error(`API call failed with status: ${response.status}`);
            }

            const result = await response.json();
            console.log("[API] Raw Workflow Orchestrator Service response:", result);

            // Use the same transform function - the backend returns compatible format
            return this.transformRegulatoryResearchResponse(result);

        } catch (error: any) {
            console.error("Error in sendToWorkflowOrchestrator:", error);
            if (error.name === 'AbortError') {
                return { success: false, session_id: null, response_type: 'error', content: null, error: "Query timeout - the service took longer than 30 minutes to complete." };
            }
            return { success: false, session_id: null, response_type: 'error', content: null, error: error.message };
        }
    },

    async sendToWorkflowOrchestratorStreaming(
        payload: ApiPayload,
        token: string
    ): Promise<TransformedResponse> {
        // Fresh streaming session — discard any commands queued by a
        // previous session that may still be in the buffer. Belt-and-
        // suspenders alongside the ws.onclose clear below; covers the
        // case where the previous session never fired onclose (page
        // reload, hot module replace, etc.). Review comment on PR #143.
        this._pendingCommands = [];
        return new Promise((resolve) => {
            const sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            const wsUrl = this._workflowOrchestratorUrl.replace('http://', 'ws://').replace('https://', 'wss://');

            let finalResponse: TransformedResponse | null = null;
            let accumulatedTokens = '';
            let reconnectAttempts = 0;
            const maxReconnectAttempts = 3;
            // Separate auth-retry counter; capped so a persistent server-side
            // 4001 (despite a fresh token) can't loop forever. Worst-case
            // total WebSocket connections: 1 initial + maxAuthRetries (2) ×
            // (maxReconnectAttempts (3) + 1) = 9. The 30-min outer timeout
            // is the only hard ceiling beyond that; in practice the 8 non-
            // initial connects at ≤5s exp-backoff take well under a minute.
            let authRetries = 0;
            const maxAuthRetries = 2;
            let isCompleted = false;
            let ws: WebSocket | null = null;
            let messageSent = false;
            // Mutable so the WS-close 4001 handler can rebind to a freshly
            // refreshed token before the next reconnect attempt (BAR-41).
            let currentToken = token;

            const timeout = setTimeout(() => {
                isCompleted = true;
                if (ws) ws.close();
                resolve({ success: false, session_id: sessionId, response_type: 'error', content: null, error: "WebSocket timeout after 30 minutes" });
            }, 1800000); // 30 minute timeout

            const connect = () => {
                console.log("[API] Connecting to WebSocket:", `${wsUrl}/ws/${sessionId}`, reconnectAttempts > 0 ? `(reconnect attempt ${reconnectAttempts})` : '');
                // Token via Sec-WebSocket-Protocol header (off the URL — BAR-270).
                ws = currentToken
                    ? new WebSocket(`${wsUrl}/ws/${sessionId}`, ['bearer', currentToken])
                    : new WebSocket(`${wsUrl}/ws/${sessionId}`);

                ws.onopen = () => {
                    console.log("[API] WebSocket connected, sending message (conversational orchestrator)");
                    reconnectAttempts = 0; // Reset on successful connection

                    // Store reference for sending commands
                    this._activeWebSocket = ws;

                    // Deliver any commands the user queued while the WS
                    // was reconnecting. Empty no-op on the initial connect
                    // (queue is freshly empty). BAR-138.
                    this._drainPendingCommands();

                    // Only send message on first connection, not reconnects
                    if (!messageSent) {
                        messageSent = true;
                        ws!.send(JSON.stringify({
                            command: "message",
                            input: {
                                query: payload.query,
                                project_id: payload.project_id || null,  // Pass project_id for project-scoped workspace persistence
                                // No conversation_history: the orchestrator loads it from the chat event log (BAR-1551).
                                context: payload.context  // CRITICAL: Include context with user_id for chat persistence
                            }
                        }));
                    }

                    if (this._streamingCallback) {
                        this._streamingCallback({
                            type: 'system',
                            data: { message: 'connected', session_id: sessionId }
                        });
                    }
                };

                ws.onmessage = (event) => {
                    try {
                        const message = JSON.parse(event.data);
                        console.log("[API] WebSocket message:", message.type, message.data);

                        // Forward to streaming callback (include sequence for chronological ordering)
                        if (this._streamingCallback) {
                            this._streamingCallback({
                                type: message.type || 'unknown',
                                data: message.data || message,
                                event_id: message.event_id,  // Unique event ID for deduplication across paths
                                sequence: message.sequence || 0,  // Backend sequence for true chronological order
                                worker_id: message.worker_id,
                                message_id: message.message_id,
                                tracking_id: message.tracking_id,
                                project_id: message.project_id
                            });
                        }

                        // Handle streaming tokens - accumulate for final response
                        if (message.type === 'token') {
                            const tokenData = message.data?.token || '';
                            accumulatedTokens += tokenData;
                        }

                        // Handle tool calls (orchestrator invoking workers)
                        if (message.type === 'tool_call') {
                            console.log("[API] Tool call:", message.data?.tool, message.data?.args);
                        }

                        // Handle tool results
                        if (message.type === 'tool_result') {
                            console.log("[API] Tool result:", message.data?.tool, message.data?.success);
                        }

                        // Check for completion
                        if (message.type === 'complete' || message.type === 'workflow_complete') {
                            isCompleted = true;
                            clearTimeout(timeout);
                            const result = message.data || message;

                            // Transform the result to match expected format
                            if (result.synthesis_output || result.report) {
                                finalResponse = {
                                    success: true,
                                    session_id: sessionId,
                                    response_type: 'report',
                                    content: result.synthesis_output?.report || result.report?.content || '',
                                    report: result.synthesis_output || result.report,
                                    workspace_operations: result.workspace_operations || []
                                };
                            } else if (accumulatedTokens) {
                                finalResponse = {
                                    success: true,
                                    session_id: sessionId,
                                    response_type: 'conversation',
                                    content: accumulatedTokens
                                };
                            } else {
                                finalResponse = {
                                    success: true,
                                    session_id: sessionId,
                                    response_type: 'conversation',
                                    content: result.message || 'Completed'
                                };
                            }
                            ws!.close();
                        }

                        // Check for error
                        if (message.type === 'error') {
                            isCompleted = true;
                            clearTimeout(timeout);
                            finalResponse = {
                                success: false,
                                session_id: sessionId,
                                response_type: 'error',
                                content: null,
                                error: message.data?.error || message.error || 'Unknown error'
                            };
                            ws!.close();
                        }
                    } catch (e) {
                        console.error("[API] Error parsing WebSocket message:", e);
                    }
                };

                ws.onerror = (error) => {
                    console.error("[API] WebSocket error:", error);
                    // Don't resolve immediately - let onclose handle reconnection
                };

                ws.onclose = async (event) => {
                    // Top-level try/catch so any throw from inside the async
                    // body (including from _tokenRefreshCallback if it raises
                    // synchronously before the awaited promise) doesn't leak
                    // as an unhandled promise rejection into the WS internals.
                    try {
                        console.log("[API] WebSocket closed, code:", event.code, "reason:", event.reason || '<none>', "isCompleted:", isCompleted);

                        // Clear active WebSocket reference
                        this._activeWebSocket = null;

                        // If completed normally, resolve
                        if (isCompleted) {
                            // Discard any commands queued during this session
                            // — there will be no further ws.onopen to drain
                            // them, and they belong to this session not the
                            // next one. Review comment on PR #143.
                            if (this._pendingCommands.length > 0) {
                                console.log(`[API] Discarding ${this._pendingCommands.length} queued command(s) at session end`);
                                this._pendingCommands = [];
                            }
                            clearTimeout(timeout);
                            if (finalResponse) {
                                resolve(finalResponse);
                            } else {
                                resolve({ success: true, session_id: sessionId, response_type: 'conversation', content: accumulatedTokens || 'Completed' });
                            }
                            return;
                        }

                        // 4001 = orchestrator's `await websocket.close(code=4001,
                        // reason="Authentication required")` (workflow_orchestrator_service
                        // app/main.py). The token we sent was rejected — refresh it
                        // before the next attempt instead of looping with the same
                        // dead value. Without this, an in-flight WS keeps using the
                        // token captured in closure even after App.tsx's 50-min
                        // proactive refresh has issued a new one. BAR-41.
                        if (event.code === 4001) {
                            // No refresh callback registered (effect cleanup
                            // ran, user logged out, callback was never set) —
                            // falling through to the generic reconnect path
                            // would just loop with the same stale token, which
                            // is the exact bug this PR exists to fix. Surface
                            // the auth failure now.
                            if (!this._tokenRefreshCallback) {
                                console.error("[API] WS 4001 with no token-refresh callback registered; giving up.");
                                clearTimeout(timeout);
                                resolve({
                                    success: false,
                                    session_id: sessionId,
                                    response_type: 'error',
                                    content: null,
                                    error: "WebSocket authentication required but no refresh handler is available — please sign in again"
                                });
                                return;
                            }

                            // Count this 4001 against the budget BEFORE attempting
                            // refresh — the budget tracks "4001s seen", not
                            // "successful refreshes". If we incremented after
                            // refresh, a refresh that succeeded but failed to
                            // re-auth (server bug, propagation lag) wouldn't
                            // consume a slot, and the loop could go longer than
                            // intended. Bounded: if the server keeps 4001'ing
                            // even with a freshly-refreshed token, the session
                            // is genuinely dead (revoked refresh token, identity
                            // backend down, etc.).
                            authRetries++;
                            if (authRetries > maxAuthRetries) {
                                console.error(`[API] Persistent 4001 after ${maxAuthRetries} token refreshes; giving up.`);
                                clearTimeout(timeout);
                                resolve({
                                    success: false,
                                    session_id: sessionId,
                                    response_type: 'error',
                                    content: null,
                                    error: "WebSocket authentication kept failing after token refresh — please sign in again"
                                });
                                return;
                            }
                            try {
                                currentToken = await this._tokenRefreshCallback();
                                console.log(`[API] Refreshed token after WS 4001 close (auth retry ${authRetries}/${maxAuthRetries})`);
                                // Pre-seed the network-blip retry budget: this
                                // close was auth-recoverable, not a network
                                // drop. Without this, a 4001 would burn one of
                                // the 3 attempts reserved for genuine drops.
                                // (onopen also resets this on successful
                                // re-connect, but that fires AFTER the next
                                // attempt opens — we need the budget reset
                                // BEFORE we schedule the reconnect below in
                                // case the reconnect itself fails before opening.)
                                reconnectAttempts = 0;
                            } catch (e) {
                                console.error("[API] Failed to refresh token after WS 4001:", e);
                                // Reconnecting with the stale token would just
                                // 4001 again and burn the rest of the budget for
                                // nothing. Surface the auth failure now so the
                                // caller can prompt re-auth.
                                clearTimeout(timeout);
                                resolve({
                                    success: false,
                                    session_id: sessionId,
                                    response_type: 'error',
                                    content: null,
                                    error: "Authentication failed and token refresh failed — please sign in again"
                                });
                                return;
                            }
                            // Re-check isCompleted: the 30-min outer timeout
                            // could have fired while we were awaiting the
                            // refresh, resolving the promise out from under us.
                            // Without this guard we'd schedule a zombie WS that
                            // outlives the resolved promise, with no timeout
                            // protection of its own.
                            if (isCompleted) return;
                        }

                        // Try to reconnect if not completed
                        if (reconnectAttempts < maxReconnectAttempts) {
                            reconnectAttempts++;
                            const delay = Math.min(1000 * Math.pow(2, reconnectAttempts - 1), 5000); // Exponential backoff, max 5s
                            console.log(`[API] Attempting reconnect in ${delay}ms...`);

                            if (this._streamingCallback) {
                                this._streamingCallback({
                                    type: 'system',
                                    data: { message: `Reconnecting (attempt ${reconnectAttempts}/${maxReconnectAttempts})...`, session_id: sessionId }
                                });

                                // Restore research progress from Redis after reconnect
                                const projectId = payload.project_id;
                                if (projectId) {
                                    this.getResearchProgress(projectId).then((researchState: any) => {
                                        if (researchState.active && researchState.progress && this._streamingCallback) {
                                            console.log('[API] Restoring research progress after reconnect:', researchState.progress.currentPhase);
                                            this._streamingCallback({
                                                type: 'research_started',
                                                data: { tracking_id: researchState.progress.tracking_id || 'restored', query: '' }
                                            });
                                            // Replay phase changes to rebuild progress box
                                            for (const phase of (researchState.progress.phases || [])) {
                                                this._streamingCallback({
                                                    type: 'phase_change',
                                                    data: { old_phase: '', new_phase: phase.name }
                                                });
                                            }
                                        }
                                    }).catch(() => {});
                                }
                            }

                            setTimeout(connect, delay);
                        } else {
                            // Reconnect attempts exhausted — same belt-and-
                            // suspenders queue clear as the isCompleted
                            // branch. Defense-in-depth so commands enqueued
                            // during the failed reconnect window can't drain
                            // into a future WS session if anything bypasses
                            // sendToWorkflowOrchestratorStreaming's start-
                            // of-session clear. Review on PR #143.
                            if (this._pendingCommands.length > 0) {
                                console.log(`[API] Discarding ${this._pendingCommands.length} queued command(s) after reconnect exhaustion`);
                                this._pendingCommands = [];
                            }
                            clearTimeout(timeout);
                            resolve({
                                success: false,
                                session_id: sessionId,
                                response_type: 'error',
                                content: null,
                                error: "WebSocket connection lost after multiple reconnection attempts"
                            });
                        }
                    } catch (err) {
                        // Defensive catch — should be unreachable since the
                        // only async call above is already wrapped in its own
                        // try/catch. If we get here, something unexpected
                        // happened in the synchronous reconnect code; resolve
                        // the outer promise so the caller doesn't hang.
                        console.error("[API] Unhandled error in WebSocket onclose handler:", err);
                        clearTimeout(timeout);
                        resolve({
                            success: false,
                            session_id: sessionId,
                            response_type: 'error',
                            content: null,
                            error: "WebSocket close handler error"
                        });
                    }
                };
            };

            // Start initial connection
            connect();
        });
    },

    async sendToRegulatoryResearchService(
        payload: ApiPayload,
        token: string
    ): Promise<TransformedResponse> {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 1800000); // 30 minute timeout

        try {
            console.log("[API] Sending to Regulatory Research Service:", { endpoint: '/invoke', payload });
            const response = await fetch(`${this._regulatoryResearchUrl}/invoke`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    // Note: Regulatory research service doesn't use authentication
                },
                body: JSON.stringify(payload),
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const errorText = await response.text();
                console.error("Regulatory Research Service Error Response:", errorText);
                throw new Error(`API call failed with status: ${response.status}`);
            }

            const result = await response.json();
            console.log("[API] Raw Regulatory Research Service response:", result);
            
            return this.transformRegulatoryResearchResponse(result);

        } catch (error: any) {
            console.error("Error in sendToRegulatoryResearchService:", error);
            if (error.name === 'AbortError') {
                return { success: false, session_id: null, response_type: 'error', content: null, error: "Query timeout - the service took longer than 30 minutes to complete." };
            }
            return { success: false, session_id: null, response_type: 'error', content: null, error: error.message };
        }
    },

    async sendToCeoTeamService(
        payload: ApiPayload,
        token: string
    ): Promise<TransformedResponse> {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 1800000); // 30 minute timeout

        try {
            console.log("[API] Sending to CEO Team Service:", { endpoint: '/invoke', payload });
            const response = await fetch(`${this._ceoTeamServiceUrl}/invoke`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    // Note: CEO team service doesn't use authentication
                },
                body: JSON.stringify(payload),
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const errorText = await response.text();
                console.error("CEO Team Service Error Response:", errorText);
                throw new Error(`API call failed with status: ${response.status}`);
            }

            const result = await response.json();
            console.log("[API] Raw CEO Team Service response:", result);
            
            return this.transformCeoTeamResponse(result);

        } catch (error: any) {
            console.error("Error in sendToCeoTeamService:", error);
            if (error.name === 'AbortError') {
                return { success: false, session_id: null, response_type: 'error', content: null, error: "Query timeout - the service took longer than 30 minutes to complete." };
            }
            return { success: false, session_id: null, response_type: 'error', content: null, error: error.message };
        }
    },
    
    async sendToRegulatoryResearch(
        query: string, 
        context: any, 
        userId: string, 
        clarificationContext: any = null, 
        signal?: AbortSignal
    ): Promise<TransformedResponse> {
        try {
            let endpoint = '/invoke';
            let payload: any;

            if (clarificationContext && clarificationContext.research_id) {
                endpoint = `/invoke/${clarificationContext.research_id}/clarify`;
                payload = {
                    request_id: clarificationContext.clarification_id,
                    answer: query,
                };
            } else {
                 payload = {
                    query: query,
                    context: {
                        source: "bark_ui",
                        research_depth: context.research_depth || 60
                    },
                    user_id: userId,
                    max_iterations: 10
                };
            }

            console.log("[API] Sending to Regulatory Research Service:", { endpoint, payload });

            const response = await fetch(`${this._regulatoryResearchUrl}${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                signal: signal
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error("Regulatory Research Service Error Response:", errorText);
                throw new Error(`API call failed with status: ${response.status}`);
            }

            const result = await response.json();
            console.log("[API] Raw Regulatory Research Service response:", result);
            
            return this.transformRegulatoryResearchResponse(result);

        } catch (error: any) {
            console.error("Error calling Regulatory Research Service:", error);
            return { success: false, session_id: null, response_type: 'error', content: null, error: error.message };
        }
    },
    
    transformRegulatoryResearchResponse(response: any): TransformedResponse {
        const { response_status, response_data, requires_user_clarification } = response;

        console.log('[API] Transforming response:', {
            response_status,
            response_data,
            message: response_data?.message
        });

        // Handle new conversational response types
        if (response_status === 'conversation_response' || response_status === 'query_refinement_needed' ||
            response_status === 'report_updated' || response_status === 'context_answer') {

            const transformedResponse = {
                success: true,
                session_id: response_data.session_id || response_data.research_id,
                response_type: 'conversation',
                content: response_data.message,
                report: response_data.report ? this.transformReport(response_data.report) : undefined,
                references: response_data.references ? this.transformReferences(response_data.references) : undefined,
                proposed_plan: response_data.proposed_plan,  // Include proposed_plan from backend
                continue_research: response_data.continue_research,  // Include continue_research flag
                research_query: response_data.research_query,  // Include research_query for auto-continuation
                report_instruction: response_data.report_instruction,  // Include report_instruction
                report_id: response_data.report_id,  // Include actual report_id
                workspace_operations: response_data.workspace_operations,  // Workspace operations to apply
                message_persisted: response_data.message_persisted,  // Server persisted messages
                conversation_context: {
                    response_status: response_status,
                    available_actions: response_data.available_actions || [],
                    research_limitations: response_data.research_limitations_detected || false
                }
            };

            console.log('[API] Transformed conversational response:', transformedResponse);
            return transformedResponse;
        }

        if (response_status === 'clarification_needed' || requires_user_clarification) {
            return {
                success: true,
                session_id: response_data.research_id,
                response_type: 'clarification',
                content: response_data.clarification_request.question,
                clarification_context: {
                    research_id: response_data.research_id,
                    clarification_id: response_data.clarification_request.request_id,
                }
            };
        }

        if (response_status === 'completed') {
            const { report, citations, research_id } = response_data;
            return {
                success: true,
                session_id: research_id,
                response_type: 'report',
                content: report.executive_summary,
                report: {
                    title: report.title,
                    content: report.content,
                    structured_sections: report.structured_sections,
                },
                references: citations.map((c: any) => {
                    console.log('[API] Raw citation object:', c);
                    const mappedRef = {
                        id: c.reference_id || c.id,
                        finding_id: c.finding_id,  // Link to findings dict
                        title: c.title,
                        summary: c.summary,
                        detail: c.detail,
                        document: c.document,
                        document_reference: c.document_reference,
                        reference_type: c.reference_type,
                        logical_connections: c.logical_connections,
                        connection_type: c.connection_type,
                        logical_rationale: c.logical_rationale,
                        ...c.logical_chain_info
                    };
                    console.log('[API] Mapped reference:', mappedRef);
                    return mappedRef;
                }),
            };
        }

        return {
            success: false,
            session_id: response_data.research_id || null,
            response_type: 'error',
            content: null,
            error: response_data.error_message || 'An unknown error occurred.',
        };
    },

    transformReport(reportData: any): Report {
        return {
            title: reportData.title,
            content: reportData.content || reportData.executive_summary,
            structured_sections: reportData.structured_sections || reportData.sections || []
        };
    },

    transformReferences(referencesData: any[]): Reference[] {
        return referencesData.map((c: any) => {
            console.log('[API] Raw citation object:', c);
            const mappedRef = {
                id: c.id || c.reference_id,
                finding_id: c.finding_id,  // Link to findings dict
                title: c.title,
                summary: c.summary,
                detail: c.detail,
                document: c.document,
                document_reference: c.document_reference,
                reference_type: c.reference_type,
                logical_connections: c.logical_connections,
                connection_type: c.connection_type,
                logical_rationale: c.logical_rationale,
                ...c.logical_chain_info
            };
            console.log('[API] Mapped reference:', mappedRef);
            return mappedRef;
        });
    },

    transformCeoTeamResponse(response: any): TransformedResponse {
        // CEO Team Service has a different response format
        // Adapt this based on the actual CEO team service response structure
        const { response_status, response_data, requires_user_clarification } = response;

        if (response_status === 'clarification_needed' || requires_user_clarification) {
            return {
                success: true,
                session_id: response_data.session_id,
                response_type: 'clarification',
                content: response_data.clarification_request.question,
                clarification_context: {
                    research_id: response_data.session_id,
                    clarification_id: response_data.clarification_request.request_id,
                }
            };
        }

        if (response_status === 'completed') {
            const { report, citations, session_id } = response_data;
            return {
                success: true,
                session_id: session_id,
                response_type: 'report',
                content: report.executive_summary,
                report: {
                    title: report.title,
                    content: report.content,
                    structured_sections: report.structured_sections,
                },
                references: (citations || []).map((c: any) => ({
                    id: c.reference_id || c.id,
                    title: c.title,
                    summary: c.summary,
                    detail: c.detail,
                    ...c.logical_chain_info
                })),
            };
        }

        return {
            success: false,
            session_id: response_data.session_id || null,
            response_type: 'error',
            content: null,
            error: response_data.error_message || 'An unknown error occurred.',
        };
    },

    // =============================================================================
    // EVENT SUBSCRIPTION API - Single Source of Truth Architecture
    // Frontend subscribes to project events from Redis storage
    // =============================================================================

    /**
     * Check if a project has an active agent session running.
     * Used to determine if frontend should reconnect to an ongoing stream.
     */
    async checkActiveSession(projectId: string): Promise<{ active: boolean; sessionId?: string; startedAt?: string }> {
        await this.initialize();
        try {
            // BAR-750: this WOS endpoint now requires the caller's token (was
            // unauthenticated). Use the refreshing resolver (not the raw getter)
            // so a near-expiry stored token can't turn into a spurious 401 —
            // these endpoints are hit on reconnect, exactly when a tab has been
            // idle and the cached token is most likely stale.
            const token = await resolveUsableAuthToken();
            const response = await fetch(
                `${this._workflowOrchestratorUrl}/projects/${projectId}/active-session`,
                { headers: token ? { 'Authorization': `Bearer ${token}` } : {} }
            );
            if (!response.ok) {
                console.warn('[API] Failed to check active session:', response.status);
                return { active: false };
            }
            const data = await response.json();
            return {
                active: data.active,
                sessionId: data.session_id,
                startedAt: data.started_at
            };
        } catch (error) {
            console.error('[API] Error checking active session:', error);
            return { active: false };
        }
    },

    /**
     * Get current research progress state for a project.
     * Returns aggregated progress (phases, findings, active state) so
     * the frontend can initialize the research progress box on reconnect.
     */
    async getResearchProgress(projectId: string): Promise<{ active: boolean; completed?: boolean; progress?: any }> {
        await this.initialize();
        try {
            // BAR-750: WOS endpoint now requires the caller's token. Refreshing
            // resolver (see checkActiveSession) — also a reconnect-path call.
            const token = await resolveUsableAuthToken();
            const response = await fetch(
                `${this._workflowOrchestratorUrl}/projects/${projectId}/research-progress`,
                { headers: token ? { 'Authorization': `Bearer ${token}` } : {} }
            );
            if (!response.ok) return { active: false };
            return await response.json();
        } catch (error) {
            console.error('[API] Error fetching research progress:', error);
            return { active: false };
        }
    },

    // Active subscription WebSocket
    _subscriptionWebSocket: null as WebSocket | null,
    _subscriptionCallback: null as ((event: StreamingEvent) => void) | null,

    /**
     * Subscribe to project events from Redis storage.
     *
     * This implements the "Storage as Single Source of Truth" pattern:
     * 1. On connect: receives all existing events from Redis
     * 2. Then receives live events via Redis pub/sub
     * 3. Frontend just appends to local array - no merging needed
     *
     * @param projectId - The project to subscribe to
     * @param callback - Called for each event (existing and live)
     * @returns Unsubscribe function
     *
     * The token is read fresh from getStoredAuthToken() at (re)connect time and
     * sent as the first WS frame — callers don't pass it in.
     */
    subscribeToProjectEvents(projectId: string, callback: (event: StreamingEvent) => void): () => void {
        // Check if workflow orchestrator URL is configured
        if (!this._workflowOrchestratorUrl) {
            console.error('[API] Workflow Orchestrator URL not configured - cannot subscribe to events');
            callback({ type: 'subscription_error', data: { error: 'Workflow Orchestrator URL not configured' } });
            return () => {}; // Return no-op unsubscribe
        }

        const wsUrl = this._workflowOrchestratorUrl.replace('http://', 'ws://').replace('https://', 'wss://');
        const baseSubscribeUrl = `${wsUrl}/ws/subscribe/${projectId}`;

        console.log('[API] Subscribing to project events:', baseSubscribeUrl);

        // Close any existing subscription to prevent orphaned connections
        if (this._subscriptionWebSocket && this._subscriptionWebSocket.readyState === WebSocket.OPEN) {
            console.log('[API] Closing existing subscription before creating new one');
            this._subscriptionWebSocket.close();
        }

        // Reconnection state
        let reconnectAttempts = 0;
        const maxReconnectAttempts = 10;
        const baseReconnectDelay = 1000; // 1 second
        let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
        let isIntentionallyClosed = false;
        let currentWs: WebSocket | null = null;
        let authReconnectAttempts = 0;
        const maxAuthReconnectAttempts = 2;

        const connect = () => {
            if (isIntentionallyClosed) return;

            // Auth: pass the Firebase token via the Sec-WebSocket-Protocol header
            // (["bearer", token]) instead of the URL query string — keeping it out
            // of the URL (which Cloud Run access logs DO capture) and browser
            // history. The handshake header isn't in standard access logs, though a
            // header-logging proxy could still capture it: a strong improvement,
            // not a full token-off-the-wire fix (BAR-270). Read it fresh at connect
            // time so a reconnect after a token refresh picks up the new token; no
            // token => anonymous, same as before.
            const currentToken = getStoredAuthToken();
            if (currentToken && currentToken.length > 4000) {
                // Some proxies / load balancers reject or truncate
                // Sec-WebSocket-Protocol headers over ~4KB; surface an oversized
                // token before it becomes a silent connection failure (BAR-270).
                console.warn(`[API] Subscription auth token is ${currentToken.length} chars (>4KB) — some proxies may reject the Sec-WebSocket-Protocol header.`);
            }
            console.log('[API] Subscription WebSocket connecting:', baseSubscribeUrl,
                        reconnectAttempts > 0 ? `(attempt ${reconnectAttempts})` : '',
                        currentToken ? '(authenticated)' : '(anonymous)');

            const ws = currentToken
                ? new WebSocket(baseSubscribeUrl, ['bearer', currentToken])
                : new WebSocket(baseSubscribeUrl);
            currentWs = ws;
            this._subscriptionWebSocket = ws;
            this._subscriptionCallback = callback;

            ws.onopen = () => {
                console.log('[API] Subscription WebSocket connected');
                reconnectAttempts = 0; // Reset on successful connection
                authReconnectAttempts = 0;
                callback({ type: 'subscription_connected', data: { project_id: projectId } });
            };

            ws.onmessage = (event) => {
                try {
                    const message = JSON.parse(event.data);
                    // Forward to callback - events arrive in order, just append
                    callback({
                        type: message.type || 'unknown',
                        data: message.data || message,
                        sequence: message.sequence,
                        event_id: message.event_id,
                        message_id: message.message_id,
                        tracking_id: message.tracking_id,
                        project_id: message.project_id
                    });

                    // Log catchup complete for debugging
                    if (message.type === 'catchup_complete') {
                        console.log('[API] Subscription catchup complete:', message.data?.event_count, 'events');
                    }
                } catch (e) {
                    console.error('[API] Error parsing subscription message:', e);
                }
            };

            ws.onerror = (error) => {
                console.error('[API] Subscription WebSocket error:', error);
                callback({ type: 'subscription_error', data: { error: 'WebSocket error' } });
            };

            ws.onclose = async (event) => {
              // Wrap the whole async body so a throw (e.g. token refresh) can't
              // become an unhandled promise rejection — the browser won't await this.
              try {
                console.log(
                    '[API] Subscription WebSocket closed, intentional:',
                    isIntentionallyClosed,
                    'code:',
                    event.code,
                    'reason:',
                    event.reason || '<none>'
                );
                this._subscriptionWebSocket = null;

                if (!isIntentionallyClosed && event.code === 4001) {
                    authReconnectAttempts++;
                    if (this._tokenRefreshCallback && authReconnectAttempts <= maxAuthReconnectAttempts) {
                        try {
                            await this._tokenRefreshCallback();
                            // + jitter so a mass auth-refresh (e.g. a deploy) doesn't reconnect every client on the same tick.
                            const delay = Math.min(baseReconnectDelay * Math.pow(2, authReconnectAttempts - 1), 5000) + Math.floor(Math.random() * baseReconnectDelay);
                            console.log(`[API] Subscription auth failed; refreshed token and reconnecting in ${delay}ms (auth attempt ${authReconnectAttempts}/${maxAuthReconnectAttempts})`);
                            callback({
                                type: 'subscription_reconnecting',
                                data: { project_id: projectId, attempt: authReconnectAttempts, delay, reason: 'authentication_refresh' }
                            });
                            reconnectTimeout = setTimeout(connect, delay);
                            return;
                        } catch (e) {
                            console.error('[API] Subscription token refresh failed after 4001:', e);
                        }
                    }

                    callback({
                        type: 'subscription_failed',
                        data: {
                            project_id: projectId,
                            reason: 'authentication_required',
                            close_code: event.code,
                            close_reason: event.reason,
                        }
                    });
                    return;
                }

                // Reconnect if not intentionally closed
                if (!isIntentionallyClosed && reconnectAttempts < maxReconnectAttempts) {
                    reconnectAttempts++;
                    // Proactively refresh the token before reconnecting — the cached
                    // localStorage token may have expired since the last connect, so
                    // refresh here to avoid a guaranteed 4001 round-trip (BAR-266 review).
                    if (this._tokenRefreshCallback) {
                        try {
                            await this._tokenRefreshCallback();
                        } catch (e) {
                            console.warn('[API] Token refresh before subscription reconnect failed:', e);
                        }
                    }
                    // Exponential backoff (1s, 2s, 4s… max 30s) + additive jitter
                    // (0–baseReconnectDelay ms) so a mass disconnect — e.g. a deploy
                    // or instance recycle — doesn't make every client reconnect on
                    // the same tick (thundering herd, a storm trigger).
                    const delay = Math.min(baseReconnectDelay * Math.pow(2, reconnectAttempts - 1), 30000) + Math.floor(Math.random() * baseReconnectDelay);
                    console.log(`[API] Subscription reconnecting in ${delay}ms (attempt ${reconnectAttempts}/${maxReconnectAttempts})`);
                    callback({ type: 'subscription_reconnecting', data: { project_id: projectId, attempt: reconnectAttempts, delay } });
                    reconnectTimeout = setTimeout(connect, delay);
                } else if (!isIntentionallyClosed) {
                    console.error('[API] Subscription max reconnect attempts reached');
                    callback({ type: 'subscription_failed', data: { project_id: projectId, reason: 'max_reconnect_attempts' } });
                } else {
                    callback({ type: 'subscription_closed', data: { project_id: projectId } });
                }
              } catch (e) {
                  console.error('[API] Subscription onclose handler error:', e);
              }
            };
        };

        // Initial connection
        connect();

        // Return unsubscribe function
        return () => {
            console.log('[API] Unsubscribing from project events');
            isIntentionallyClosed = true;
            if (reconnectTimeout) {
                clearTimeout(reconnectTimeout);
                reconnectTimeout = null;
            }
            if (currentWs && currentWs.readyState === WebSocket.OPEN) {
                currentWs.close();
            }
            this._subscriptionWebSocket = null;
            this._subscriptionCallback = null;
        };
    },

    /**
     * Fetch project events via REST (alternative to WebSocket subscription).
     * Useful for one-time fetches or polling.
     */
    async getProjectEvents(projectId: string, sinceSequence: number = 0): Promise<{events: any[], count: number}> {
        await this.initialize();
        try {
            // BAR-750: WOS events endpoint now requires the caller's token + ownership.
            const token = await resolveUsableAuthToken();
            const response = await fetch(
                `${this._workflowOrchestratorUrl}/api/projects/${projectId}/events?since_sequence=${sinceSequence}`,
                { method: 'GET', headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }
            );

            if (!response.ok) {
                throw new Error(`Failed to fetch events: ${response.status}`);
            }

            return await response.json();
        } catch (error: any) {
            console.error('[API] Error fetching project events:', error);
            return { events: [], count: 0 };
        }
    },

    // =============================================================================
    // REFERENCE SYSTEM API
    // =============================================================================

    /**
     * Fetch details for a reference marker (for SourcePanel display).
     * Backend endpoint: GET /workspace/{workspace_id}/reference/{marker_id}
     * (owner is derived from the verified token server-side — BAR-750).
     */
    async getReferenceDetails(workspaceId: string, markerId: string): Promise<{
        marker_id: string;
        label: string;
        context: string;
        citation_text: string;
        finding_id: string;
        source_document_name: string;
        source_document_celex: string;
        source_reference: string;
        source_reference_hierarchy: string;
        source_text: string;
        source_snippet: string;
        node_ids: string[];
    }> {
        await this.initialize();

        try {
            console.log('[API] Fetching reference details:', { workspaceId, markerId });
            // BAR-750: owner is now derived from the verified token server-side;
            // the forgeable ?user_id= is dropped and a bearer token is required.
            // Refreshing resolver: this one THROWS on failure (the citation popup
            // surfaces the error), so a stale token would be user-visible.
            const token = await resolveUsableAuthToken();
            const response = await fetch(
                `${this._workflowOrchestratorUrl}/workspace/${workspaceId}/reference/${markerId}`,
                {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) }
                }
            );

            if (!response.ok) {
                const errorText = await response.text();
                console.error('[API] Reference details error:', errorText);
                throw new Error(`Failed to fetch reference details: ${response.status}`);
            }

            const data = await response.json();
            console.log('[API] Reference details response:', data);
            return data;
        } catch (error: any) {
            console.error('[API] Error fetching reference details:', error);
            throw error;
        }
    },

    async getRelatedArticles(celex: string, articleRef: string, sourceType: string = 'eurlex'): Promise<{
        success: boolean;
        outgoing: Array<{ article: string; celex: string; document_name: string }>;
        incoming: Array<{ article: string; celex: string; document_name: string }>;
        concepts: string[];
        linked_qa: Array<{ ref: string; target: string; date?: string; title?: string }>;
        error?: string;
    }> {
        await this.initialize();

        try {
            const params = new URLSearchParams({
                celex,
                article_ref: articleRef,
                source_type: sourceType,
            });
            const response = await fetch(
                `${this._workflowOrchestratorUrl}/api/related-articles?${params}`,
                {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' }
                }
            );

            if (!response.ok) {
                return { success: false, outgoing: [], incoming: [], concepts: [], linked_qa: [], error: `HTTP ${response.status}` };
            }

            return await response.json();
        } catch (error: any) {
            console.error('[API] Error fetching related articles:', error);
            return { success: false, outgoing: [], incoming: [], concepts: [], linked_qa: [], error: error.message };
        }
    },

    // =========================================================================
    // Modeling System API (Phase 11)
    // =========================================================================

    async getModels(projectId: string, token: string, userId?: string, status?: string, workspaceId?: string, _alreadyRetried = false): Promise<any> {
        await this.initialize();
        const params = new URLSearchParams();
        if (status) params.set('status', status);
        if (userId) params.set('user_id', userId);
        if (workspaceId) params.set('workspace_id', workspaceId);
        const response = await fetch(
            `${this._workflowOrchestratorUrl}/api/projects/${projectId}/models?${params}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (response.status === 401 && !_alreadyRetried) {
            // Join the same App-owned exchange used by proactive, WebSocket,
            // and other REST refreshes. Retryable provider failures propagate
            // without deleting a valid session; terminal rejection is typed
            // and cleared by handleAuthRefreshFailure.
            const freshToken = await refreshAuthToken();
            if (freshToken) {
                return this.getModels(
                    projectId,
                    freshToken,
                    userId,
                    status,
                    workspaceId,
                    true,
                );
            }
            clearStoredAuthState();
            throw sessionExpiredError();
        }
        if (response.status === 401) {
            clearStoredAuthState();
            throw sessionExpiredError();
        }
        if (!response.ok) throw new Error(`Failed to load models: ${response.status}`);
        return response.json();
    },

    async uploadNodeData(
        projectId: string,
        modelId: string,
        nodeId: string,
        formData: FormData,
        authToken: string,
    ): Promise<{
        upload_id: string;
        artifact_name: string;
        validation: {
            artifact_name: string;
            node_id: string;
            row_count: number;
            column_count: number;
            passed: boolean;
            errors: string[];
            warnings: string[];
            at: string;
        };
        paths: { raw: string; artifact: string };
        limits: { max_bytes: number };
    }> {
        await this.initialize();
        const response = await fetch(
            `${this._workflowOrchestratorUrl}/api/projects/${encodeURIComponent(projectId)}/models/${encodeURIComponent(modelId)}/nodes/${encodeURIComponent(nodeId)}/upload`,
            {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${authToken}` },
                body: formData,
            },
        );
        if (!response.ok) {
            // Surface the structured detail body so DataUploadPanel can
            // render the per-column validation diff without re-parsing.
            let detail: any = undefined;
            try { detail = (await response.json()).detail; } catch { /* noop */ }
            const err: any = new Error(`Upload failed: ${response.status}`);
            err.detail = detail;
            err.status = response.status;
            throw err;
        }
        return response.json();
    },

    /**
     * BAR-308: import a previously-uploaded report-library file into an
     * INPUT_DATA node. The bytes already landed in report-library via the
     * signed-URL chat-upload flow (`uploadWorkspaceFile` — direct-to-GCS for
     * files over the multipart threshold, bypassing Cloud Run's ~32 MiB
     * buffered-request ceiling that the legacy multipart `uploadNodeData`
     * POST hit). This second step hands WOS the `upload_id` so it pulls the
     * object, validates it against the node's declared schema, and promotes
     * the canonical artifact — returning the SAME body shape `uploadNodeData`
     * did (upload_id, artifact_name, validation, paths, limits, …).
     */
    async importNodeData(
        projectId: string,
        modelId: string,
        nodeId: string,
        uploadId: string,
        authToken: string,
    ): Promise<{
        upload_id: string;
        active_upload_id?: string;
        summary?: string;
        artifact_name: string;
        validation: {
            artifact_name: string;
            node_id: string;
            row_count: number;
            column_count: number;
            passed: boolean;
            errors: string[];
            warnings: string[];
            at: string;
        };
        paths: { raw: string; artifact: string };
        upload?: { upload_id: string; filename: string;[k: string]: unknown };
        limits: { max_bytes: number };
    }> {
        await this.initialize();
        const response = await fetch(
            `${this._workflowOrchestratorUrl}/api/projects/${encodeURIComponent(projectId)}/models/${encodeURIComponent(modelId)}/nodes/${encodeURIComponent(nodeId)}/import-upload`,
            {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ upload_id: uploadId }),
            },
        );
        if (!response.ok) {
            // Mirror uploadNodeData: surface the structured detail body so
            // DataUploadPanel can render the per-column validation diff.
            let detail: any = undefined;
            try { detail = (await response.json()).detail; } catch { /* noop */ }
            const err: any = new Error(`Import failed: ${response.status}`);
            err.detail = detail;
            err.status = response.status;
            throw err;
        }
        return response.json();
    },

    /**
     * BAR-308: the enforced node data-upload size cap, so DataUploadPanel shows
     * the real limit up-front instead of a hardcoded guess. Returns the WOS
     * `data_upload_max_bytes` (256 MiB default; env-overridable).
     */
    async getDataUploadConfig(
        projectId: string,
        authToken: string,
    ): Promise<{ max_bytes: number }> {
        await this.initialize();
        const response = await fetch(
            `${this._workflowOrchestratorUrl}/api/projects/${encodeURIComponent(projectId)}/data-upload-config`,
            { headers: { 'Authorization': `Bearer ${authToken}` } },
        );
        if (!response.ok) {
            throw new Error(`data-upload-config failed: ${response.status}`);
        }
        return response.json();
    },

    async selectNodeInputUpload(
        projectId: string,
        modelId: string,
        nodeId: string,
        payload: { upload_id?: string; filename?: string },
        authToken: string,
    ) {
        await this.initialize();
        const response = await fetch(
            `${this._workflowOrchestratorUrl}/api/projects/${encodeURIComponent(projectId)}/models/${encodeURIComponent(modelId)}/nodes/${encodeURIComponent(nodeId)}/select-upload`,
            {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            },
        );
        if (!response.ok) {
            let detail: any = undefined;
            try { detail = (await response.json()).detail; } catch { /* noop */ }
            const message = typeof detail === 'string'
                ? detail
                : (detail?.error || `Failed to select upload: ${response.status}`);
            const err: any = new Error(message);
            err.detail = detail;
            err.status = response.status;
            throw err;
        }
        return response.json();
    },

    async getJupyterToken(projectId: string, authToken: string): Promise<{
        // New scoped-JWT path (production):
        iframe_token?: string;
        server_url: string;
        initial_path?: string;
        company_slug?: string;
        expires_in?: number;
        // Legacy fallback, present only when the iframe JWT keypair
        // isn't configured (dev without keys):
        jupyter_token?: string;
    }> {
        await this.initialize();
        const response = await fetch(
            `${this._workflowOrchestratorUrl}/api/projects/${projectId}/jupyter-token`,
            { headers: { 'Authorization': `Bearer ${authToken}` } }
        );
        if (!response.ok) throw new Error(`Failed to get Jupyter token: ${response.status}`);
        return response.json();
    },

    async getModel(
        projectId: string,
        modelId: string,
        token: string,
        userId?: string,
        workspaceId?: string,
    ) {
        // Pass `workspace_id` so the backend reads from the canonical
        // workspace event-log derived state. Without it, the endpoint
        // falls back to project-scoped Firestore — which can drift
        // behind the event log if any best-effort dual-write fails.
        // The FE-rendered model graph then shows stale state (missing
        // nodes / edges) compared to what the agent's get_model_status
        // sees; PR-45 round-11 fix.
        await this.initialize();
        const qs = new URLSearchParams();
        if (userId) qs.set('user_id', userId);
        if (workspaceId) qs.set('workspace_id', workspaceId);
        const params = qs.toString() ? `?${qs.toString()}` : '';
        const response = await fetch(
            `${this._workflowOrchestratorUrl}/api/projects/${projectId}/models/${modelId}${params}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (!response.ok) throw new Error(`Failed to load model: ${response.status}`);
        return response.json();
    },

    async getWorkspaceRequirements(
        projectId: string,
        workspaceId: string,
        token: string,
        includeMarkdown: boolean = false,
    ): Promise<{
        workspace_id: string;
        seeded: boolean;
        total: number;
        done: number;
        sections: string[];
        items: Array<{
            req_id: string;
            text: string;
            done: boolean;
            implementation_note: string;
            section: string;
            line_index: number;
        }>;
        markdown?: string | null;
        // First-class SpecRequirement view (workspace-outputs Phase 1):
        // one entry per consumed upstream research spec, joined with this
        // build's status overlay, validator verdicts and human sign-off.
        // Additive — the legacy fields above are kept unchanged.
        specs?: ConsumedSpec[];
    }> {
        await this.initialize();
        const params = new URLSearchParams();
        if (includeMarkdown) params.set('include_markdown', 'true');
        const qs = params.toString();
        const response = await fetch(
            `${this._workflowOrchestratorUrl}/projects/${projectId}/workspaces/${workspaceId}/requirements${qs ? '?' + qs : ''}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (!response.ok) {
            throw await specResolutionError(response);
        }
        return response.json();
    },

    async getDeliverableShape(
        label: string,
        token: string,
    ): Promise<{
        key: string;
        display_name: string;
        summary: string;
        required_sections: Array<{ heading: string; guidance: string }>;
        requirement_line_format: string;
    } | null> {
        await this.initialize();
        const response = await fetch(
            `${this._workflowOrchestratorUrl}/deliverable-shapes/${encodeURIComponent(label)}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (response.status === 404) return null;
        if (!response.ok) throw new Error(`Failed to load deliverable shape: ${response.status}`);
        return response.json();
    },

    async getModelRuns(projectId: string, modelId: string, token: string, userId?: string) {
        await this.initialize();
        const params = userId ? `?user_id=${encodeURIComponent(userId)}` : '';
        const response = await fetch(
            `${this._workflowOrchestratorUrl}/api/projects/${projectId}/models/${modelId}/runs${params}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (!response.ok) throw new Error(`Failed to load model runs: ${response.status}`);
        return response.json();
    },

    async getModelRun(projectId: string, modelId: string, runId: string, token: string, userId?: string) {
        await this.initialize();
        const params = userId ? `?user_id=${encodeURIComponent(userId)}` : '';
        const response = await fetch(
            `${this._workflowOrchestratorUrl}/api/projects/${projectId}/models/${modelId}/runs/${runId}${params}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (!response.ok) throw new Error(`Failed to load model run: ${response.status}`);
        return response.json();
    },

    async getModelDashboard(
        projectId: string,
        modelId: string,
        runId: string | null,
        token: string,
    ): Promise<{ html: string; sha256: string | null; bytes: number | null; run_id: string | null; model_id: string }> {
        await this.initialize();
        const params = runId ? `?run_id=${encodeURIComponent(runId)}` : '';
        const response = await fetch(
            `${this._workflowOrchestratorUrl}/api/projects/${projectId}/models/${modelId}/dashboard${params}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (!response.ok) {
            // 404 = no dashboard generated yet for this model/run — let the
            // caller render the empty state rather than a hard error.
            throw new Error(`Failed to load dashboard: ${response.status}`);
        }
        return response.json();
    },

    async getNodeArtifactPreview(
        projectId: string,
        modelId: string,
        nodeId: string,
        token: string,
        opts?: { artifactName?: string; runId?: string | null; maxRows?: number },
    ): Promise<ArtifactPreview | null> {
        await this.initialize();
        const params = new URLSearchParams();
        if (opts?.artifactName) params.set('artifact_name', opts.artifactName);
        if (opts?.runId) params.set('run_id', opts.runId);
        if (opts?.maxRows) params.set('max_rows', String(opts.maxRows));
        const query = params.toString() ? `?${params.toString()}` : '';
        const response = await fetch(
            `${this._workflowOrchestratorUrl}/api/projects/${projectId}/models/${modelId}/nodes/${nodeId}/artifact-preview${query}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
        );
        // 404 = no artifact written yet (not uploaded / not run). Return null so
        // the caller renders the empty state instead of an error.
        if (response.status === 404) return null;
        if (!response.ok) throw new Error(`Failed to load data preview: ${response.status}`);
        return response.json();
    },

    async executeModel(projectId: string, modelId: string, token: string, userId?: string) {
        await this.initialize();
        const params = userId ? `?user_id=${encodeURIComponent(userId)}` : '';
        const response = await fetch(
            `${this._workflowOrchestratorUrl}/api/projects/${projectId}/models/${modelId}/execute${params}`,
            {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            }
        );
        if (!response.ok) {
            let detail: any = undefined;
            try { detail = (await response.json()).detail; } catch { /* noop */ }
            const message = typeof detail === 'string'
                ? detail
                : (detail?.error || `Failed to execute model: ${response.status}`);
            const err: any = new Error(message);
            err.detail = detail;
            err.status = response.status;
            throw err;
        }
        return response.json();
    },

    async generateDashboard(projectId: string, modelId: string, token: string, userId?: string, runId?: string | null, visualizationPrompt?: string | null) {
        await this.initialize();
        const params = new URLSearchParams();
        if (userId) params.set('user_id', userId);
        if (runId) params.set('run_id', runId);
        const query = params.toString() ? `?${params.toString()}` : '';
        const trimmed = (visualizationPrompt || '').trim();
        const response = await fetch(
            `${this._workflowOrchestratorUrl}/api/projects/${projectId}/models/${modelId}/generate-dashboard${query}`,
            {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                // The visualization subagent turns this free-text ask into authored
                // code; omitted/empty → a sensible default report.
                body: JSON.stringify(trimmed ? { visualization_prompt: trimmed } : {}),
            }
        );
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const err = new Error(errorData.detail || `Failed to generate dashboard: ${response.status}`) as ApiError;
            err.status = response.status;
            throw err;
        }
        return response.json();
    },

    // =========================================================================
    // Handoff Package API
    // =========================================================================

    async getLatestHandoff(projectId: string, token: string) {
        await this.initialize();
        const response = await fetch(
            `${this._workflowOrchestratorUrl}/api/projects/${projectId}/handoffs/latest`,
            { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (response.status === 404) return null;
        if (!response.ok) throw new Error(`Failed to load handoff: ${response.status}`);
        return response.json();
    },

    // =========================================================================
    // Regulatory feed API (BAR-298) — "From regulators this week"
    // =========================================================================

    // Returns [] on any failure so the home page degrades to its seed feed
    // rather than erroring. Proxied via the orchestrator (external_document_
    // service is internal and not browser-reachable).
    async getRegulatoryFeed(token: string, limit = 50): Promise<RegulatoryFeedResult> {
        const empty: RegulatoryFeedResult = { items: [], facets: { sources: [], typeFamilies: [], categories: [] } };
        await this.initialize();
        try {
            const response = await fetch(
                `${this._workflowOrchestratorUrl}/api/home-agent/regulatory-feed?limit=${limit}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) return empty;
            const data = await response.json();
            return {
                items: Array.isArray(data?.items) ? data.items : [],
                facets: {
                    sources: data?.facets?.sources ?? [],
                    typeFamilies: Array.isArray(data?.facets?.type_families) ? data.facets.type_families : [],
                    categories: data?.facets?.categories ?? [],
                },
            };
        } catch {
            return empty;
        }
    },
};

// =============================================================================
// HOME AGENT TYPES
// =============================================================================

export interface HomeAgentAction {
    type: 'regulatory_research' | 'build_artifact' | 'quick_lookup' | 'project_library';
    label: string;
    description: string;
    prefill_query: string;
}

// BAR-298: a single regulator publication in the "From regulators this week"
// feed. Mirrors the backend FeedItem (external_document_service).
export interface RegulatoryFeedItem {
    id: string;
    source: string;
    date: string;            // YYYY-MM-DD
    type: string;
    title: string;
    summary?: string | null;
    link?: string | null;
    regulationId?: string | null;
    categories?: string[];
    question: string;
}

// Full, stable filter vocabularies (the filter is a persistent setting over
// all items, so these are the complete sets — not derived from the window).
export interface FeedTypeFamily {
    name: string;
    types: string[];
}

export interface FeedFacets {
    sources: string[];
    typeFamilies: FeedTypeFamily[];
    categories: string[];
    /** AI-generated "Ask BARK" question (empty until enrichment ran). */
    question?: string | null;
}

export interface RegulatoryFeedResult {
    items: RegulatoryFeedItem[];
    facets: FeedFacets;
}

// =============================================================================
// PROJECT & WORKSPACE TYPES
// =============================================================================

export interface ProjectSummary {
    project_id: string;
    name: string;
    description: string;
    node_count: number;
    edge_count: number;
    message_count: number;
    has_report: boolean;
    /** BAR-540: true when every workspace node is approved (the pill's green). */
    workspace_validated?: boolean;
    status: 'active' | 'archived' | 'archived_visible';
    project_type?: 'research' | 'build';
    active_workspace?: string;
    created_at: string;
    updated_at: string;
}

// Node type categories
export type DynamicNodeType = 'question' | 'conclusion' | 'input' | 'result';
export type EvidenceNodeType =
    | 'article'
    | 'definition'
    | 'requirement'
    | 'option'
    | 'prohibition'
    | 'condition'
    | 'exemption'
    | 'threshold'
    | 'calculation'
    | 'concept';
export type NodeType = DynamicNodeType | EvidenceNodeType;

export interface NodeSourceMetadata {
    source_tool?: string;
    source_query?: string;
    source_document?: string;
    source_reference?: string;
    document_id?: string;
    confidence_score?: number;
}

export interface LogicalNode {
    node_id: string;
    node_type: NodeType;
    label: string;
    relevance_note: string;  // Why this node matters to the research question

    // For EVIDENCE nodes (article, requirement, etc.) - links to findings dict
    finding_id?: string;

    // For DYNAMIC nodes (question, conclusion, input, result) - inline content
    content?: string;

    // For GROUP nodes - nested children (expandable sub-graph)
    children?: LogicalNode[];

    // For END nodes - linked findings to display when selected
    finding_ids?: string[];

    detail?: string | null;  // Full regulatory text or extended content
    reference_ids: string[];
    source_metadata?: NodeSourceMetadata | null;
    created_at?: string;
    updated_at?: string;
}

export interface LogicalEdge {
    source_node_id: string;
    target_node_id: string;
    created_at?: string;
}

export interface LogicalChainGraph {
    nodes: LogicalNode[];
    edges: LogicalEdge[];
}

// Evidence content retrieved during research
// Matches backend workspace_primitives.py Finding model
export interface Finding {
    finding_id: string;

    // Source Document
    source_document_celex: string;  // EU CELEX number, e.g., "32013R0575"
    source_document_name: string;   // Common name, e.g., "CRR"

    // Source Location (hierarchical)
    source_reference_hierarchy: string;  // Path, e.g., "Part Two > Title I > Chapter 4"
    source_reference: string;            // Specific ref, e.g., "Article 92(1)(a)"

    // Content
    source_text: string;       // Full text of the article/section
    source_snippet: string;    // Specific relevant excerpt

    // Provenance
    extracted_at: string;      // ISO 8601 timestamp
    tool_used?: string;        // Which tool found this, e.g., "get_full_article"
    document_version?: string; // Amendment/version info if applicable

    // Enriched source metadata for finding card rendering
    source_type?: string;         // "eurlex", "eba_qa", "concept", "pdf", "workspace", "unknown"
    category?: string;            // "regulatory" (default) | "input" — input = workspace-sourced fact
    source_workspace_id?: string; // set when category === "input"
    document_type?: string;       // "Regulation", "Directive", etc.
    document_full_title?: string; // Full official title
    article_title?: string;       // Article title separate from reference
    source_service?: string;      // "graph" or "pdf"
    source_url?: string;          // Direct URL to source

    // PDF positional data (undefined for graph results)
    chunk_id?: string;        // PDF chunk citation marker
    page_number?: number;     // PDF page number (1-indexed)
    paragraph_id?: string;    // Section identifier (e.g., "§4.3.2")
    bbox?: Record<string, any>; // Bounding box for highlight overlay

    // Graph intelligence (cross-references, concepts, Q&As)
    cross_references?: Array<{ direction: string; article: string; celex: string; document_name: string }>;
    concepts?: string[];
    linked_qa?: Array<{ ref: string; target: string; date?: string; title?: string }>;
    qa_topic?: string;
    qa_legal_act?: string;
    qa_status?: string;
    qa_submitted_date?: string;
    qa_clarifies?: string[];

    // Canonical metadata (PDF service, from #54)
    document_status?: string;
    document_category?: string;
    publication_date?: string;
    regulatory_framework?: string[];
    legal_basis?: string[];
    applies_to?: string[];
    effective_date?: string;
    parent_reference?: string;
    replaces?: string;
    amends_document?: string;
    compliance_deadline?: string;
}

export interface NodePosition {
    x: number;
    y: number;
}

export interface ViewportState {
    x: number;
    y: number;
    zoom: number;
}

export interface WorkspaceUIState {
    node_positions: Record<string, NodePosition>;
    /**
     * Build-view (ProcessDAG) cluster-region positions, keyed by region id.
     * Kept separate from `node_positions` (research/EvidenceBoard) because the
     * two views share one project-level ui_state — a single map would let each
     * view clobber the other's saved layout on drag.
     */
    build_node_positions?: Record<string, NodePosition>;
    viewport: ViewportState;
    selected_node_id: string | null;
}

// Chat history types for conversational agent
export interface ChatMessage {
    message_id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: string;
    metadata?: Record<string, any>;  // For tool calls, report links, etc.
}

// =============================================================================
// REFERENCE MATERIALS (RESEARCH CONTEXT TRACKING)
// =============================================================================

export interface RejectedPath {
    target: string;
    reason: string;
    iteration: number;
}

export interface StructuralInsight {
    type: 'adjacency' | 'cross_reference' | 'alternatives' | string;
    detail: string;
    articles: string[];
}

export interface EffectiveQuery {
    query: string;
    tool: string;
    iteration: number;
}

export interface IterationReasoning {
    iteration: number;
    reasoning: string;
    next_focus: string;
}

export interface ReferenceMaterials {
    toc_pointers: string[];
    rejected_paths: RejectedPath[];
    structural_insights: StructuralInsight[];
    effective_queries: EffectiveQuery[];
    iteration_reasoning: IterationReasoning[];
}

export const createEmptyReferenceMaterials = (): ReferenceMaterials => ({
    toc_pointers: [],
    rejected_paths: [],
    structural_insights: [],
    effective_queries: [],
    iteration_reasoning: []
});

export interface ProjectDocument {
    project_id: string;
    user_id: string;
    name: string;
    description: string;
    workspace: LogicalChainGraph;
    findings: Record<string, Finding>;  // Evidence content indexed by finding_id
    workspace_ui_state: WorkspaceUIState;
    reference_materials: ReferenceMaterials;  // Structured research context tracking
    draft_report: string;
    chat_history: ChatMessage[];
    research_mission?: any;  // Research mission (query, plan, progress) from workflow orchestrator
    version: number;
    linked_report_id: string | null;
    status: 'active' | 'archived' | 'archived_visible';
    project_type?: 'research' | 'build';
    active_workspace?: string;
    created_at: string;
    updated_at: string;
    tags: string[];
}

export interface ProjectListResponse {
    projects: ProjectSummary[];
    total_count: number;
    page: number;
    page_size: number;
    has_more: boolean;
    success: boolean;
}

export interface UpdateWorkspaceResponse {
    success: boolean;
    new_version?: number;
    conflict: boolean;
    server_version?: number;
    updated_at?: string;
}

// =============================================================================
// PROJECT API FUNCTIONS
// =============================================================================

export const getReportLibraryUrl = () => realApi.getReportLibraryUrl() || 'http://localhost:8020';

export const ensureInitialized = async () => {
    await realApi.initialize();
    return getReportLibraryUrl();
};

const AUTH_TOKEN_REFRESH_WINDOW_SECONDS = 120;
type SessionExpiredError = ApiError & { requiresLogin: true };
let inflightAuthRefresh: Promise<string | undefined> | null = null;

// BAR-1564: this tab's impersonation token first (it lives in the tab's
// sessionStorage), else the browser's session in localStorage.
const getStoredAuthToken = (): string | undefined => {
    if (typeof window === 'undefined') return undefined;
    return readAuthToken() || undefined;
};

/**
 * SEC-1: build headers for a report-library call with the logged-in
 * user's Firebase ID token attached as `Authorization: Bearer <token>`.
 *
 * report-library calls currently pass identity as a client-supplied
 * `user_id` in the URL/body, which is spoofable. Attaching the verified
 * token lets the backend derive identity from it instead. This is
 * additive + safe: while `ENFORCE_AUTH=false` the backend ignores a
 * missing token, so behaviour is unchanged until a later flip.
 *
 * `extra` carries any per-call headers (Content-Type, Cache-Control,
 * Idempotency-Key, …); the Authorization header is only added when a
 * token is present, so each call keeps its existing header shape when
 * the user is logged out.
 */
function authedHeaders(extra?: Record<string, string>): Record<string, string> {
    const headers: Record<string, string> = { ...extra };
    // `bark_auth_token` is the short-lived Firebase ID token (the access_token
    // from /sso/token, rotated through the shared refresh owner) — NOT the
    // refresh token (`bark_refresh_token`). report-library verifies it as a
    // Firebase ID token, so this is the correct credential to send. An
    // impersonating tab sends its impersonation token instead (BAR-1564).
    const token = typeof window !== 'undefined'
        ? readAuthToken()
        : null;
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
}

const clearStoredAuthState = () => {
    if (typeof window === 'undefined') return;
    // BAR-1564: localStorage holds the admin's session while a tab
    // impersonates; a dead impersonation must not sign the admin out. The
    // exit (sessionExpiredError -> the global handler) restores the admin.
    if (isImpersonatingTab()) return;
    localStorage.removeItem('bark_auth_token');
    localStorage.removeItem('bark_refresh_token');
    localStorage.removeItem('bark_user_data');
};

// =============================================================================
// SINGLE-FLIGHT REQUEST COALESCING (BAR-248)
// =============================================================================

/**
 * In-flight GETs, keyed by request identity. Used ONLY by the two
 * report-library reads that replay a workspace's whole event log server-side
 * (`/workspaces/{id}/state`, `/projects/{id}/events`).
 *
 * WHY: six independent components fetch the same workspace state with no
 * coordination (PlanTrainTrack, WorkspaceBrief, WorkspaceDetailsOverlay,
 * PlanNodeDetailPanel, processViewerStore, projectStore), so opening one
 * project fired 4-7 identical concurrent requests. Each one cost the backend a
 * COMPLETE replay of the event log on a 1-vCPU container, and they serialized
 * on the GIL — measured 20-50s p99 on prod's largest project, and the direct
 * cause of the `report-library-service p99 latency` alert firing 32 times in
 * 30 days.
 *
 * This is a COALESCER, NOT A CACHE: an entry lives only while its request is
 * in flight and is dropped the moment it settles. A caller arriving after the
 * response lands always issues a fresh request, so no one can observe staler
 * data than they would have without it. That property is what makes this safe
 * to apply to a read whose whole job is to return current state.
 */
const _inFlightGets = new Map<string, Promise<{ ok: boolean; status: number; text: string }>>();

/**
 * Issue `fetch` unless an identical request is already in flight, in which case
 * join that one.
 *
 * Resolves the raw body TEXT rather than a parsed object on purpose. Every
 * joiner then runs its own `JSON.parse`, so each caller still gets a private
 * object graph — exactly as before this coalescer existed. Sharing one parsed
 * object between callers would silently turn independent state into aliased
 * state, and these payloads get pushed straight into zustand and React state.
 * A parse of an 80KB body costs well under a millisecond against a request
 * that can take 20 seconds.
 *
 * Rejections are not shared beyond the joiners already waiting: the entry is
 * removed in `finally`, so the next call retries for real.
 */
async function _coalescedGet(
    key: string,
    url: string,
    headers: Record<string, string>,
): Promise<{ ok: boolean; status: number; text: string }> {
    const existing = _inFlightGets.get(key);
    if (existing) return existing;

    const pending = (async () => {
        const response = await fetch(url, { method: 'GET', headers });
        return { ok: response.ok, status: response.status, text: await response.text() };
    })();

    _inFlightGets.set(key, pending);
    try {
        return await pending;
    } finally {
        _inFlightGets.delete(key);
    }
}

/** Test seam: drop any in-flight entries so cases cannot leak into each other.
 *  No-op outside tests so it cannot be reached from a shipped bundle. */
export const __resetInFlightGetsForTests = () => {
    if (process.env.NODE_ENV !== 'test') return;
    _inFlightGets.clear();
};

const sessionExpiredError = (): SessionExpiredError => {
    // BAR-135: notify the impersonation flow (no-op when not impersonating) so a
    // REST 401 that means the (target) session is genuinely dead triggers
    // auto-exit-restore of the real admin (§6.4).
    try { realApi._globalAuthFailureHandler?.(); } catch { /* handler must never break the throw */ }
    const err = new Error('Session expired — please log in again') as SessionExpiredError;
    err.status = 401;
    err.code = 'session_expired';
    err.requiresLogin = true;
    (err as SessionExpiredError & { userMessage?: string }).userMessage = SESSION_EXPIRED_USER_MESSAGE;
    return err;
};

const isSessionExpiredError = (error: unknown): error is SessionExpiredError => (
    error instanceof Error
    && (error as SessionExpiredError).requiresLogin === true
);

const getJwtExpirySeconds = (token?: string): number | null => {
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length < 2) return null;
    try {
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
        const payload = JSON.parse(atob(padded));
        return typeof payload.exp === 'number' ? payload.exp : null;
    } catch {
        return null;
    }
};

const tokenNeedsRefresh = (token?: string): boolean => {
    const exp = getJwtExpirySeconds(token);
    // If the token cannot be parsed, send it once and let the 401 retry
    // path attempt refresh. That keeps malformed non-JWT test tokens from
    // forcing a refresh before the backend has judged them.
    if (!exp) return false;
    return exp - Math.floor(Date.now() / 1000) <= AUTH_TOKEN_REFRESH_WINDOW_SECONDS;
};

const runStoredAuthRefresh = async (): Promise<string | undefined> => {
    if (typeof window === 'undefined') return undefined;
    // BAR-1564: an impersonation has no refresh token (D5); the one in
    // localStorage is the admin's, whose token must never reach this tab.
    if (isImpersonatingTab()) return undefined;
    const refreshTok = localStorage.getItem('bark_refresh_token');
    if (!refreshTok) return undefined;

    try {
        const fresh = await realApi.refreshToken(refreshTok);
        localStorage.setItem('bark_auth_token', fresh.access_token);
        if (fresh.refresh_token) {
            localStorage.setItem('bark_refresh_token', fresh.refresh_token);
        }
        return fresh.access_token;
    } catch (error: unknown) { handleAuthRefreshFailure(error); }
};

const handleAuthRefreshFailure = (error: unknown): never => {
    const status = error instanceof Error
        ? (error as ApiError).status
        : undefined;
    if (status === 401 || status === 403) {
        clearStoredAuthState();
        throw sessionExpiredError();
    }
    throw error;
};

const runAuthRefresh = async (): Promise<string | undefined> => {
    const appRefresh = realApi._tokenRefreshCallback;
    if (!appRefresh) return runStoredAuthRefresh();
    try {
        return await appRefresh();
    } catch (error: unknown) {
        handleAuthRefreshFailure(error);
    }
};

const refreshAuthToken = async (): Promise<string | undefined> => {
    if (!inflightAuthRefresh) {
        // Mounted-app callers all join the App-owned callback, whose own
        // in-flight promise also deduplicates proactive refreshes. The direct
        // localStorage exchange is only a non-mounted/test fallback.
        inflightAuthRefresh = runAuthRefresh().finally(() => {
            inflightAuthRefresh = null;
        });
    }
    return inflightAuthRefresh;
};

type ResolvedAuthToken = {
    token: string | undefined;
    // True when this call used the result of a refresh, including when it joined
    // an existing in-flight exchange. A subsequent 401 must not spend another
    // endpoint request on the same user action in either case.
    usedRefreshResult: boolean;
};

const resolveUsableAuthTokenWithMeta = async (
    authToken?: string,
): Promise<ResolvedAuthToken> => {
    const storedToken = getStoredAuthToken();
    let token = authToken || storedToken;

    if (authToken && storedToken && tokenNeedsRefresh(authToken) && !tokenNeedsRefresh(storedToken)) {
        token = storedToken;
    }

    if (tokenNeedsRefresh(token)) {
        return {
            token: await refreshAuthToken(),
            usedRefreshResult: true,
        };
    }

    return { token, usedRefreshResult: false };
};

const resolveUsableAuthToken = async (authToken?: string): Promise<string | undefined> => {
    const resolved = await resolveUsableAuthTokenWithMeta(authToken);
    return resolved.token;
};

// The 30-minute turn cap plus the orchestrator's 5-minute stop margin (T3, sign_in_expiry.py).
const TURN_TOKEN_MIN_REMAINING_SECONDS = 35 * 60;

/**
 * The token a new chat turn opens its orchestrator WebSocket with.
 *
 * The orchestrator signs the whole turn with the handshake token and stops the
 * turn 5 minutes before that token expires. So a token with less than
 * TURN_TOKEN_MIN_REMAINING_SECONDS left is refreshed first, through `refresh`
 * (App's shared refreshAccessToken), and the turn gets the fresh token.
 *
 * Never throws and never signs anyone out: if the refresh fails, the turn
 * starts with `token`, as before, and the other refresh paths still own a dead
 * session. A token without a readable `exp` is used as it is, as in
 * tokenNeedsRefresh: the WebSocket's 4001 handling judges it.
 */
export const resolveTurnToken = async (
    token: string,
    refresh: () => Promise<string>,
): Promise<string> => {
    const exp = getJwtExpirySeconds(token);
    if (!exp || exp - Math.floor(Date.now() / 1000) >= TURN_TOKEN_MIN_REMAINING_SECONDS) {
        return token;
    }
    try {
        const freshToken = await refresh();
        console.log('[Auth] Token refreshed before the chat turn');
        return freshToken;
    } catch (error) {
        console.warn('[Auth] Token refresh before the chat turn failed; starting the turn with the current token:', error);
        return token;
    }
};

/**
 * BAR-750: bearer headers for components that call the backends with a bare
 * `fetch` instead of going through apiService.
 *
 * Several components (plan train-track, workspace brief, details overlay) hit
 * report-library directly and sent NO Authorization header. That only worked
 * because ENFORCE_AUTH=false demoted them to anonymous; under enforcement they
 * 401 and — because each swallows the error — the UI silently renders empty.
 * Exported so the token/refresh logic lives in ONE place rather than being
 * re-implemented per component.
 *
 * ASYNC on purpose: it may trigger a token refresh. Callers must `await` it
 * (or `.then(...)`) — passing the un-awaited Promise as fetch headers fails
 * silently, sending no Authorization at all.
 */
export const authHeaders = async (
    extra: Record<string, string> = {},
): Promise<Record<string, string>> => {
    const token = await resolveUsableAuthToken();
    return token ? { ...extra, Authorization: `Bearer ${token}` } : { ...extra };
};

// =============================================================================
// FIRESTORE EVENTS SUBCOLLECTION API
// Flat event stream stored in Firestore for chronological display
// =============================================================================

export interface ChatEventFirestore {
    event_id: string;
    project_id: string;
    message_id?: string;
    // Mirrors backend ChatEventType (report_library_service models.py).
    // waiting_on_data is the BAR-1227 durable park record (JSON: model_id,
    // blocked_on, missing_inputs); deliberately not rendered — see
    // getDisplayHistory in stores/projectStore.ts.
    type: 'user_message' | 'thinking' | 'tool_call' | 'tool_result' | 'response' | 'research_progress' | 'workspace_switch' | 'user_question' | 'waiting_on_data' | 'feedback' | 'sources_used';
    content: string;
    timestamp: string;
    sequence: number;
    metadata?: Record<string, any>;  // user_message events include user_id + user_name here
    /**
     * Server-stamped authorship (BAR-1270). Absent on every event written
     * before it existed, and on every channel that cannot determine an author
     * — treat absent as "use the type-implied identity", i.e. today's
     * rendering. Only 'external_agent' currently changes what is drawn.
     */
    author?: 'user' | 'bark' | 'external_agent';
    /** For author='external_agent': the human whose authority the agent spent. */
    on_behalf_of?: string;
}

/** Per-source contribution for one assistant message (powers the chat source icons). */
export interface SourceFamilyUsage {
    used: boolean;
    count: number;  // number of contributing queries
}

/** Which data-source families informed an assistant answer. Carried by the
 *  `sources_used` event (content is JSON of this shape). `online_search` was
 *  added after the first three; legacy events omit it, so consumers default a
 *  missing family to not-used. */
export interface MessageSourcesUsed {
    graph: SourceFamilyUsage;
    documents: SourceFamilyUsage;
    research: SourceFamilyUsage;
    online_search?: SourceFamilyUsage;
}

export type MessageFeedbackSentiment = 'positive' | 'negative';

export interface SubmitMessageFeedbackRequest {
    target_message_id: string;
    target_event_id?: string;
    sentiment?: MessageFeedbackSentiment | null;
    comment?: string;
    workspace_type?: string;
    project_name?: string;
    session_id?: string;
    target_snippet?: string;
}

export interface SubmitMessageFeedbackResponse {
    success: boolean;
    feedback_id?: string;
    event_id?: string;
    sequence?: number;
    timestamp?: string;
    analytics_recorded?: boolean;
    event?: ChatEventFirestore;
    error?: string;
}

export const submitMessageFeedback = async (
    projectId: string,
    payload: SubmitMessageFeedbackRequest,
    authToken?: string
): Promise<SubmitMessageFeedbackResponse> => {
    try {
        await realApi.initialize();
        const workflowUrl = realApi.getWorkflowOrchestratorUrl();
        if (!workflowUrl) {
            throw new Error('Workflow orchestrator URL is not configured');
        }

        const token = await resolveUsableAuthToken(authToken);
        if (!token) {
            return { success: false, error: 'Authentication required' };
        }
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        headers.Authorization = `Bearer ${token}`;

        const response = await fetch(
            `${workflowUrl}/api/projects/${encodeURIComponent(projectId)}/feedback`,
            {
                method: 'POST',
                headers,
                body: JSON.stringify(payload),
            }
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || `Feedback request failed (${response.status})`);
        }

        return await response.json();
    } catch (error: any) {
        console.error('[submitMessageFeedback] Failed to submit feedback:', error);
        return { success: false, error: error.message || 'Failed to submit feedback' };
    }
};

/**
 * Fetch project events from Firestore subcollection.
 * Returns events ordered by sequence for chronological display.
 *
 * Three shapes, matching the endpoint:
 *  - default: everything after `sinceSequence` (an incremental reload)
 *  - `tail`: the newest `limit` events (opening a project, where there is no
 *    watermark yet — this is what stops a cold open reading the whole log)
 *  - `beforeSequence`: the `limit` events just older than one we already hold
 */
export const getProjectEventsFirestore = async (
    projectId: string,
    userId: string,
    sinceSequence: number = 0,
    options: { tail?: boolean; limit?: number; beforeSequence?: number } = {}
): Promise<{
    success: boolean;
    events?: ChatEventFirestore[];
    count?: number;
    oldestSequence?: number | null;
    latestSequence?: number | null;
    error?: string;
}> => {
    try {
        const baseUrl = await ensureInitialized();
        const { tail, limit, beforeSequence } = options;
        const params = new URLSearchParams({
            user_id: userId,
            since_sequence: String(sinceSequence),
        });
        if (tail) params.set('tail', 'true');
        if (limit) params.set('limit', String(limit));
        if (beforeSequence !== undefined) params.set('before_sequence', String(beforeSequence));

        // Coalesced (BAR-248): the paging args join `since_sequence` in the
        // key, so a tail read is never served an incremental response (or
        // the reverse) from an in-flight request.
        const response = await _coalescedGet(
            `events:${projectId}:${userId}:${sinceSequence}:${tail ? 't' : ''}${limit ?? ''}:${beforeSequence ?? ''}`,
            `${baseUrl}/projects/${encodeURIComponent(projectId)}/events?${params.toString()}`,
            authedHeaders({ 'Content-Type': 'application/json' }),
        );

        if (!response.ok) {
            if (response.status === 404) {
                return { success: true, events: [], count: 0 };
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = JSON.parse(response.text);
        return {
            success: true,
            events: data.events || [],
            count: data.count || 0,
            oldestSequence: data.oldest_sequence ?? null,
            latestSequence: data.latest_sequence ?? null,
        };
    } catch (error: any) {
        console.error('Error fetching project events from Firestore:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Persist a chat event to Firestore via Report Library Service.
 * Used for frontend-originated events (e.g. workspace_switch) that need to survive reloads.
 */
export const persistEvent = async (
    projectId: string,
    userId: string,
    type: string,
    content: string,
    metadata?: Record<string, any>,
    timestamp?: string,
    messageId?: string
): Promise<{ success: boolean; event_id?: string; error?: string }> => {
    try {
        const baseUrl = await ensureInitialized();
        const body: Record<string, any> = { type, content, metadata };
        if (timestamp) {
            body.timestamp = timestamp;
        }
        // BAR-146: group the event into a turn by message_id. For user_message
        // this lets report-library collapse the send-time write and the
        // orchestrator's write into one deterministic document (idempotent).
        if (messageId) {
            body.message_id = messageId;
        }
        const response = await fetch(
            `${baseUrl}/projects/${projectId}/events?user_id=${encodeURIComponent(userId)}`,
            {
                method: 'POST',
                headers: authedHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify(body),
            }
        );
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return { success: true, event_id: data.event_id };
    } catch (error: any) {
        console.error('[persistEvent] Failed to persist event:', error);
        return { success: false, error: error.message };
    }
};

export const createProject = async (
    userId: string,
    name: string,
    description: string = '',
    tags: string[] = [],
    projectType?: 'research' | 'build'
): Promise<{ success: boolean; project?: ProjectDocument; error?: string }> => {
    try {
        const baseUrl = await ensureInitialized();
        const response = await fetch(`${baseUrl}/projects`, {
            method: 'POST',
            headers: authedHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({
                user_id: userId,
                name,
                description,
                tags,
                ...(projectType ? { project_type: projectType } : {})
            })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
        }

        const createResult = await response.json();

        // Create endpoint returns {success, project_id, message}, need to fetch full project
        if (createResult.success && createResult.project_id) {
            const projectResult = await getProject(createResult.project_id, userId);
            return projectResult;
        }

        return { success: false, error: 'Failed to create project' };
    } catch (error: any) {
        console.error('Error creating project:', error);
        return { success: false, error: error.message };
    }
};

export const getProject = async (
    projectId: string,
    userId: string
): Promise<{ success: boolean; project?: ProjectDocument; error?: string }> => {
    try {
        // Add cache-busting timestamp to ensure fresh data on reconnection
        // This prevents browser/proxy caching issues when user returns to project
        const timestamp = Date.now();
        const baseUrl = await ensureInitialized();
        const response = await fetch(
            `${baseUrl}/projects/${projectId}?user_id=${encodeURIComponent(userId)}&_t=${timestamp}`,
            {
                method: 'GET',
                headers: authedHeaders({
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache'
                })
            }
        );

        if (!response.ok) {
            if (response.status === 404) {
                return { success: false, error: 'Project not found' };
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return { success: true, project: data.project };
    } catch (error: any) {
        console.error('Error fetching project:', error);
        return { success: false, error: error.message };
    }
};

export const listProjects = async (
    userId: string,
    page: number = 1,
    pageSize: number = 20,
    status: 'active' | 'archived' | 'archived_visible' | 'all' = 'active',
    light: boolean = false
): Promise<{ success: boolean; data?: ProjectListResponse; error?: string }> => {
    try {
        const params = new URLSearchParams({
            page: page.toString(),
            page_size: pageSize.toString(),
        });
        if (status !== 'all') {
            params.append('status', status);
        }
        if (light) {
            params.append('light', 'true');
        }

        const baseUrl = await ensureInitialized();
        const response = await fetch(
            `${baseUrl}/users/${encodeURIComponent(userId)}/projects?${params}`,
            { method: 'GET', headers: authedHeaders({ 'Content-Type': 'application/json' }) }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: ProjectListResponse = await response.json();
        return { success: true, data };
    } catch (error: any) {
        console.error('Error listing projects:', error);
        return { success: false, error: error.message };
    }
};

export const deleteProject = async (
    projectId: string,
    userId: string
): Promise<{ success: boolean; error?: string }> => {
    try {
        const baseUrl = await ensureInitialized();
        const response = await fetch(
            `${baseUrl}/projects/${projectId}?user_id=${encodeURIComponent(userId)}`,
            { method: 'DELETE', headers: authedHeaders({ 'Content-Type': 'application/json' }) }
        );

        if (!response.ok) {
            if (response.status === 404) {
                return { success: false, error: 'Project not found' };
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return { success: true };
    } catch (error: any) {
        console.error('Error deleting project:', error);
        return { success: false, error: error.message };
    }
};

export const renameProject = async (
    projectId: string,
    userId: string,
    name: string
): Promise<{ success: boolean; error?: string }> => {
    try {
        const baseUrl = await ensureInitialized();
        const response = await fetch(
            `${baseUrl}/projects/${projectId}/name?user_id=${encodeURIComponent(userId)}`,
            {
                method: 'PATCH',
                headers: authedHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ name })
            }
        );

        if (!response.ok) {
            if (response.status === 404) {
                return { success: false, error: 'Project not found' };
            }
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
        }

        return { success: true };
    } catch (error: any) {
        console.error('Error renaming project:', error);
        return { success: false, error: error.message };
    }
};

// BAR-320 follow-up: ask the orchestrator for a short, PII-safe project title
// from the user's opening question (+ optional prior discussion). Best-effort —
// returns '' on any failure so the caller keeps its placeholder name. Used by
// every create-from-chat path (home chat, sidebar draft, New Project form).
export const generateProjectTitle = async (
    query: string,
    history: { role: string; content: string }[] = [],
    // A free-plan Lookup chat is served by chat-free, so its title is too.
    options: { chatFree?: boolean } = {},
): Promise<string> => {
    try {
        await realApi.initialize();
        const baseUrl = options.chatFree && realApi.getChatFreeServiceUrl()
            ? realApi.getChatFreeServiceUrl()
            : realApi.getWorkflowOrchestratorUrl();
        if (!baseUrl) return '';
        const cleanHistory = history
            .filter((m) => (m.role === 'user' || m.role === 'assistant') && m.content && m.content.trim())
            .map((m) => ({ role: m.role, content: m.content.slice(0, 2000) }));
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 20000);
        try {
            const response = await fetch(`${baseUrl}/api/home-agent/title`, {
                method: 'POST',
                headers: authedHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ query: query.slice(0, 2000), history: cleanHistory }),
                signal: controller.signal,
            });
            if (!response.ok) return '';
            const data = await response.json().catch(() => ({}));
            return typeof data.title === 'string' ? data.title.trim() : '';
        } finally {
            clearTimeout(timeoutId);
        }
    } catch {
        return '';  // best-effort — never block project creation on titling
    }
};

export const updateWorkspace = async (
    projectId: string,
    userId: string,
    workspace: LogicalChainGraph,
    findings: Record<string, Finding> | null,
    workspaceUiState: WorkspaceUIState | null,
    referenceMaterials: ReferenceMaterials | null,
    draftReport: string | null,
    chatHistory: ChatMessage[] | null,
    description: string | null,
    expectedVersion: number,
    artifacts?: any[] | null,
    activeWorkspace?: string | null
): Promise<{ success: boolean; data?: UpdateWorkspaceResponse; error?: string }> => {
    try {
        const payload: Record<string, any> = {
            workspace,
            findings,
            workspace_ui_state: workspaceUiState,
            reference_materials: referenceMaterials,
            draft_report: draftReport,
            chat_history: chatHistory,
            description,
            expected_version: expectedVersion
        };
        // Only include artifacts if provided
        if (artifacts !== undefined) {
            payload.artifacts = artifacts;
        }
        // Include active workspace if provided
        if (activeWorkspace) {
            payload.active_workspace = activeWorkspace;
        }
        console.log('[Workspace] Sending update payload:', JSON.stringify(payload, null, 2));

        const baseUrl = await ensureInitialized();
        const response = await fetch(
            `${baseUrl}/projects/${projectId}/workspace?user_id=${encodeURIComponent(userId)}`,
            {
                method: 'PATCH',
                headers: authedHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify(payload)
            }
        );

        if (!response.ok) {
            if (response.status === 409) {
                const data = await response.json();
                return {
                    success: false,
                    data: { ...data, conflict: true },
                    error: 'Version conflict - workspace was modified elsewhere'
                };
            }
            // Log validation errors for debugging
            if (response.status === 422) {
                const errorData = await response.json();
                console.error('Workspace update validation error:', errorData);
                throw new Error(`Validation error: ${JSON.stringify(errorData)}`);
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: UpdateWorkspaceResponse = await response.json();
        // Defense in depth for legacy deployments and intermediaries that may
        // return the structured conflict body with HTTP 200. A conflict can
        // never be classified as a successful save based on status alone.
        if (data.conflict) {
            return {
                success: false,
                data,
                error: 'Version conflict - workspace was modified elsewhere'
            };
        }
        return { success: true, data };
    } catch (error: any) {
        console.error('Error updating workspace:', error);
        return { success: false, error: error.message };
    }
};

export interface DuplicateJobStatus {
    job_id: string;
    status: 'pending' | 'running' | 'completed' | 'failed' | 'failed_corrupt' | 'cached';
    phase?: string;
    new_project_id?: string;
    source_project_id?: string;
    error?: string;
    notebook_paths_with_old_id_references?: { [path: string]: number[] };
    // BAR-153: notebooks present in source at a non-canonical location
    // (not `notebooks/<name>.ipynb`) — duplicate skips them rather than
    // propagating layout drift. User is expected to repair source then
    // re-duplicate.
    notebook_paths_skipped_non_canonical?: string[];
    finalized_at?: string;
    updated_at?: string;
}

export const duplicateProject = async (
    projectId: string,
    userId: string,
    newName?: string,
    idempotencyKey?: string,
): Promise<{ success: boolean; job_id?: string; new_project_id?: string; status?: string; error?: string }> => {
    try {
        const baseUrl = await ensureInitialized();
        const key = idempotencyKey || crypto.randomUUID();
        const body: any = { user_id: userId };
        if (newName) body.new_name = newName;
        const response = await fetch(
            `${baseUrl}/projects/${projectId}/duplicate`,
            {
                method: 'POST',
                headers: authedHeaders({
                    'Content-Type': 'application/json',
                    'Idempotency-Key': key,
                }),
                body: JSON.stringify(body),
            }
        );
        if (!response.ok) {
            if (response.status === 403) return { success: false, error: 'Only the project owner can duplicate' };
            if (response.status === 404) return { success: false, error: 'Project not found' };
            if (response.status === 429) return { success: false, error: 'Too many concurrent duplicates; try again' };
            const text = await response.text().catch(() => '');
            return { success: false, error: `HTTP ${response.status}: ${text}` };
        }
        const data = await response.json();
        return {
            success: true,
            job_id: data.job_id,
            new_project_id: data.new_project_id,
            status: data.status,
        };
    } catch (error: any) {
        console.error('Error starting duplicate:', error);
        return { success: false, error: error.message };
    }
};

export const getDuplicateJob = async (
    jobId: string,
    userId: string,
): Promise<{ success: boolean; job?: DuplicateJobStatus; error?: string }> => {
    try {
        const baseUrl = await ensureInitialized();
        const response = await fetch(
            `${baseUrl}/duplicate_jobs/${jobId}?user_id=${encodeURIComponent(userId)}`,
            { method: 'GET', headers: authedHeaders() }
        );
        if (!response.ok) {
            if (response.status === 404) return { success: false, error: 'Job not found' };
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return { success: true, job: data as DuplicateJobStatus };
    } catch (error: any) {
        console.error('Error polling duplicate job:', error);
        return { success: false, error: error.message };
    }
};

export const archiveProject = async (
    projectId: string,
    userId: string
): Promise<{ success: boolean; message?: string; error?: string }> => {
    try {
        const baseUrl = await ensureInitialized();
        const response = await fetch(
            `${baseUrl}/projects/${projectId}?user_id=${encodeURIComponent(userId)}`,
            { method: 'DELETE', headers: authedHeaders({ 'Content-Type': 'application/json' }) }
        );

        if (!response.ok) {
            if (response.status === 404) {
                return { success: false, error: 'Project not found' };
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return { success: true, message: data.message || 'Project archived successfully' };
    } catch (error: any) {
        console.error('Error archiving project:', error);
        return { success: false, error: error.message };
    }
};

// Move a project to the user-visible Archived FOLDER (status=archived_visible).
// Distinct from deleteProject/archiveProject (the hidden soft-delete). Reversible
// via unarchiveProject.
export const archiveToFolder = async (
    projectId: string,
    userId: string
): Promise<{ success: boolean; message?: string; error?: string }> => {
    try {
        const baseUrl = await ensureInitialized();
        const response = await fetch(
            `${baseUrl}/projects/${projectId}/archive?user_id=${encodeURIComponent(userId)}`,
            { method: 'POST', headers: authedHeaders({ 'Content-Type': 'application/json' }) }
        );
        if (!response.ok) {
            if (response.status === 404) return { success: false, error: 'Project not found' };
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return { success: true, message: data.message };
    } catch (error: any) {
        console.error('Error archiving project to folder:', error);
        return { success: false, error: error.message };
    }
};

// Restore a project from the Archived folder back to active (status=active).
export const unarchiveProject = async (
    projectId: string,
    userId: string
): Promise<{ success: boolean; message?: string; error?: string }> => {
    try {
        const baseUrl = await ensureInitialized();
        const response = await fetch(
            `${baseUrl}/projects/${projectId}/unarchive?user_id=${encodeURIComponent(userId)}`,
            { method: 'POST', headers: authedHeaders({ 'Content-Type': 'application/json' }) }
        );
        if (!response.ok) {
            if (response.status === 404) return { success: false, error: 'Project not found' };
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return { success: true, message: data.message };
    } catch (error: any) {
        console.error('Error restoring project:', error);
        return { success: false, error: error.message };
    }
};

export const getProjectVersions = async (
    projectId: string,
    userId: string,
    limit: number = 10
): Promise<{ success: boolean; versions?: any[]; error?: string }> => {
    try {
        const baseUrl = await ensureInitialized();
        const response = await fetch(
            `${baseUrl}/projects/${projectId}/versions?user_id=${encodeURIComponent(userId)}&limit=${limit}`,
            { method: 'GET', headers: authedHeaders({ 'Content-Type': 'application/json' }) }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return { success: true, versions: data.versions };
    } catch (error: any) {
        console.error('Error fetching project versions:', error);
        return { success: false, error: error.message };
    }
};

// =============================================================================
// REPORT LIBRARY FUNCTIONS
// =============================================================================

// =============================================================================
// WORKSPACE EVENT STORE API (Dict-based state from Report Library)
// =============================================================================

/**
 * Fetch workspace state from Report Library event store.
 * Returns derived state (findings, nodes, edges) computed by replaying events.
 * This is the single source of truth for workspace data.
 *
 * @param workspaceId - Format: ws_{project_id}
 * @param userId - User identifier for access control
 */
export const getWorkspaceState = async (
    workspaceId: string,
    userId: string,
    /**
     * Pass false when this read exists to observe the server's state AFTER a
     * write this client just made (BAR-248). Coalescing is safe for the
     * ordinary render-time reads it was added for, but a refresh-after-write
     * that joins an in-flight request issued BEFORE the write would
     * deterministically read pre-write state — the one case where a joiner is
     * worse off than an independent request.
     */
    coalesce: boolean = true
): Promise<{ success: boolean; state?: WorkspaceStateResponse; error?: string }> => {
    try {
        const baseUrl = await ensureInitialized();
        // Coalesced (BAR-248): several components request the same workspace
        // simultaneously and each request costs a full server-side replay.
        const stateUrl = `${baseUrl}/workspaces/${workspaceId}/state?user_id=${encodeURIComponent(userId)}`;
        const stateHeaders = authedHeaders({
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache',
        });
        const response = coalesce
            ? await _coalescedGet(`state:${workspaceId}:${userId}`, stateUrl, stateHeaders)
            : await (async () => {
                const r = await fetch(stateUrl, { method: 'GET', headers: stateHeaders });
                return { ok: r.ok, status: r.status, text: await r.text() };
            })();

        if (!response.ok) {
            if (response.status === 404) {
                // Workspace not found - return empty state
                return {
                    success: true,
                    state: {
                        success: true,
                        workspace_id: workspaceId,
                        sequence_number: 0,
                        compartment_id: 'default',
                        findings: {},
                        nodes: {},
                        edges: {},
                        markers: {},
                        reports: {},
                        clusters: {},
                        models: {},
                        online_support: {},
                        uploaded_files: {},
                        derived_at: new Date().toISOString(),
                    },
                };
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Parsed per caller, so joiners of a coalesced request never share one
        // mutable object graph (see `_coalescedGet`).
        const data = JSON.parse(response.text);
        console.log('[API] Workspace state loaded:', {
            workspace_id: data.workspace_id,
            sequence_number: data.sequence_number,
            node_count: Object.keys(data.nodes || {}).length,
            finding_count: Object.keys(data.findings || {}).length,
        });

        return { success: data.success, state: data };
    } catch (error: any) {
        console.error('Error fetching workspace state:', error);
        return { success: false, error: error.message };
    }
};

export interface UploadedWorkspaceFile {
    upload_id: string;
    workspace_id: string;
    project_id?: string;
    graph_node_id?: string;
    original_filename: string;
    display_name: string;
    mime_type: string;
    byte_size: number;
    sha256?: string;
    upload_status: string;
    summary?: string | null;
    error_message?: string | null;
    created_at: string;
    updated_at: string;
}

export interface WorkspaceUploadConfig {
    max_bytes: number;
    /**
     * BAR-308: files at or below this size use the single-shot multipart
     * POST; larger files use the signed-URL direct-to-GCS flow (which
     * bypasses the ~32 MiB Cloud Run buffered-request ceiling). The
     * authoritative value comes from `GET /uploads/config`.
     */
    multipart_max_bytes?: number;
    max_files_per_workspace?: number;
    allowed_mime_types: string[];
    allowed_extensions: string[];
}

/**
 * Fallback allow-list used only until `GET /uploads/config` resolves (or
 * if it fails). The authoritative allow-list comes from the backend; this
 * mirror is kept in sync so it stops drifting (BAR-308).
 */
const WORKSPACE_UPLOAD_ALLOWED_EXTENSIONS = new Set([
    'pdf',
    'txt',
    'doc',
    'docx',
    'xls',
    'xlsx',
    'ppt',
    'pptx',
    'csv',
    'png',
    'jpg',
    'jpeg',
    'webp',
]);

// BAR-308: cache of the backend allow-list extensions so the apiService
// extension check stops drifting from `/uploads/config`. Populated on the
// first successful `getWorkspaceUploadConfig` call; falls back to the
// static set above until then.
let workspaceUploadAllowedExtensions: Set<string> | null = null;

const getWorkspaceUploadExtension = (filename: string): string => {
    const extension = filename.split('.').pop();
    return extension ? extension.toLowerCase() : '';
};

const isAllowedWorkspaceUploadExtension = (extension: string): boolean => {
    if (!extension) return false;
    const allowed = workspaceUploadAllowedExtensions ?? WORKSPACE_UPLOAD_ALLOWED_EXTENSIONS;
    return allowed.has(extension);
};

export const getWorkspaceUploadConfig = async (): Promise<{
    success: boolean;
    config?: WorkspaceUploadConfig;
    error?: string;
}> => {
    try {
        const baseUrl = await ensureInitialized();
        const response = await fetch(`${baseUrl}/uploads/config`, {
            method: 'GET',
            headers: authedHeaders({ 'Content-Type': 'application/json' }),
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            const detail = typeof data.detail === 'string' ? data.detail : `HTTP error! status: ${response.status}`;
            return { success: false, error: detail };
        }

        // BAR-308: cache the backend allow-list so the apiService extension
        // check (uploadWorkspaceFile) reads the authoritative list rather than
        // the static mirror, which would otherwise drift.
        const allowedExtensions = data.config?.allowed_extensions;
        if (Array.isArray(allowedExtensions) && allowedExtensions.length > 0) {
            workspaceUploadAllowedExtensions = new Set(
                allowedExtensions.map((ext: string) => ext.toLowerCase()),
            );
        }

        return {
            success: Boolean(data.success),
            config: data.config,
        };
    } catch (error: any) {
        console.error('Error fetching workspace upload config:', error);
        return { success: false, error: error.message };
    }
};

/** BAR-308: response of `POST .../uploads/initiate` (signed-URL flow). */
export interface InitiateWorkspaceUploadResult {
    success: boolean;
    upload_id?: string;
    upload_url?: string;
    required_headers?: Record<string, string>;
    error?: string;
}

/**
 * BAR-308: step 1 of the signed-URL direct-to-GCS upload flow. Mints an
 * `upload_id` and a V4 signed PUT URL the browser uploads bytes to,
 * bypassing the Cloud Run buffered-request ceiling. No Firestore events are
 * written until `finalizeWorkspaceUpload` runs.
 */
export const initiateWorkspaceUpload = async (
    workspaceId: string,
    userId: string,
    projectId: string,
    params: {
        filename: string;
        mimeType: string;
        byteSize: number;
        clientMessageId?: string;
    },
): Promise<InitiateWorkspaceUploadResult> => {
    try {
        const baseUrl = await ensureInitialized();
        const body: Record<string, any> = {
            filename: params.filename,
            mime_type: params.mimeType,
            byte_size: params.byteSize,
            project_id: projectId,
        };
        if (params.clientMessageId) {
            body.client_message_id = params.clientMessageId;
        }

        const response = await fetch(
            `${baseUrl}/workspaces/${workspaceId}/uploads/initiate?user_id=${encodeURIComponent(userId)}`,
            {
                method: 'POST',
                headers: authedHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify(body),
            },
        );

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            const detail = typeof data.detail === 'string' ? data.detail : `HTTP error! status: ${response.status}`;
            return { success: false, error: detail };
        }
        return {
            success: Boolean(data.success),
            upload_id: data.upload_id,
            upload_url: data.upload_url,
            required_headers: data.required_headers,
        };
    } catch (error: any) {
        console.error('Error initiating workspace upload:', error);
        return { success: false, error: error.message };
    }
};

/**
 * BAR-308: step 3 of the signed-URL upload flow. After the browser PUTs the
 * bytes to the signed URL, this stats + MIME-sniffs the object and writes the
 * same Firestore events / returns the same body shape as the multipart
 * endpoint, so callers are unchanged.
 */
export const finalizeWorkspaceUpload = async (
    workspaceId: string,
    uploadId: string,
    userId: string,
    params: {
        filename: string;
        mimeType: string;
        projectId: string;
        displayName?: string;
        clientMessageId?: string;
    },
): Promise<{
    success: boolean;
    uploaded_file?: UploadedWorkspaceFile;
    node?: Record<string, any>;
    events?: Array<Record<string, any>>;
    error?: string;
}> => {
    try {
        const baseUrl = await ensureInitialized();
        const body: Record<string, any> = {
            filename: params.filename,
            mime_type: params.mimeType,
            project_id: params.projectId,
        };
        if (params.displayName) {
            body.display_name = params.displayName;
        }
        if (params.clientMessageId) {
            body.client_message_id = params.clientMessageId;
        }

        const response = await fetch(
            `${baseUrl}/workspaces/${workspaceId}/uploads/${uploadId}/finalize?user_id=${encodeURIComponent(userId)}`,
            {
                method: 'POST',
                headers: authedHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify(body),
            },
        );

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            const detail = typeof data.detail === 'string' ? data.detail : `HTTP error! status: ${response.status}`;
            return { success: false, error: detail };
        }
        return {
            success: Boolean(data.success),
            uploaded_file: data.uploaded_file,
            node: data.node,
            events: data.events,
        };
    } catch (error: any) {
        console.error('Error finalizing workspace upload:', error);
        return { success: false, error: error.message };
    }
};

/**
 * BAR-308: default multipart threshold used when `/uploads/config` has not
 * been fetched (or omits `multipart_max_bytes`). Files at or below this size
 * use the single-shot multipart POST; larger files use the signed-URL flow.
 * Mirrors the backend `upload_multipart_max_bytes` default (8 MiB).
 */
const DEFAULT_WORKSPACE_UPLOAD_MULTIPART_MAX_BYTES = 8 * 1024 * 1024;

export const uploadWorkspaceFile = async (
    workspaceId: string,
    userId: string,
    projectId: string,
    file: File,
    displayName?: string,
    clientMessageId?: string,
    config?: Pick<WorkspaceUploadConfig, 'multipart_max_bytes'>,
): Promise<{
    success: boolean;
    uploaded_file?: UploadedWorkspaceFile;
    node?: Record<string, any>;
    events?: Array<Record<string, any>>;
    error?: string;
}> => {
    const extension = getWorkspaceUploadExtension(file.name);
    if (!isAllowedWorkspaceUploadExtension(extension)) {
        return { success: false, error: `${file.name} is not a supported file type.` };
    }

    const multipartMaxBytes = config?.multipart_max_bytes
        ?? DEFAULT_WORKSPACE_UPLOAD_MULTIPART_MAX_BYTES;

    // BAR-308: small files use the existing single-shot multipart endpoint;
    // larger files use the signed-URL direct-to-GCS flow.
    if (file.size > multipartMaxBytes) {
        return uploadWorkspaceFileViaSignedUrl(
            workspaceId,
            userId,
            projectId,
            file,
            displayName,
            clientMessageId,
        );
    }

    try {
        const baseUrl = await ensureInitialized();
        const form = new FormData();
        form.append('file', file);
        form.append('project_id', projectId);
        if (displayName) {
            form.append('display_name', displayName);
        }
        if (clientMessageId) {
            form.append('client_message_id', clientMessageId);
        }

        const response = await fetch(
            `${baseUrl}/workspaces/${workspaceId}/uploads?user_id=${encodeURIComponent(userId)}`,
            {
                method: 'POST',
                headers: authedHeaders(),
                body: form,
            },
        );

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            const detail = typeof data.detail === 'string' ? data.detail : `HTTP error! status: ${response.status}`;
            return { success: false, error: detail };
        }
        return {
            success: Boolean(data.success),
            uploaded_file: data.uploaded_file,
            node: data.node,
            events: data.events,
        };
    } catch (error: any) {
        console.error('Error uploading workspace file:', error);
        return { success: false, error: error.message };
    }
};

/**
 * BAR-308: signed-URL direct-to-GCS upload (initiate -> PUT -> finalize).
 * Returns the same `UploadedWorkspaceFile` shape as the multipart path so
 * callers are unchanged.
 */
const uploadWorkspaceFileViaSignedUrl = async (
    workspaceId: string,
    userId: string,
    projectId: string,
    file: File,
    displayName?: string,
    clientMessageId?: string,
): Promise<{
    success: boolean;
    uploaded_file?: UploadedWorkspaceFile;
    node?: Record<string, any>;
    events?: Array<Record<string, any>>;
    error?: string;
}> => {
    // The Content-Type the browser sends on the PUT must match what the
    // backend signed the URL for, so derive a single mime value used in both
    // initiate and the PUT header. BAR-308 review: when the browser reports no
    // type, fall back to an extension lookup so the user doesn't upload a large
    // file only to hit a late finalize rejection on an octet-stream default.
    const EXT_MIME: Record<string, string> = {
        pdf: 'application/pdf',
        csv: 'text/csv',
        txt: 'text/plain',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        xls: 'application/vnd.ms-excel',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        doc: 'application/msword',
        png: 'image/png',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        webp: 'image/webp',
    };
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    const mimeType = file.type || EXT_MIME[ext] || 'application/octet-stream';

    const initiated = await initiateWorkspaceUpload(workspaceId, userId, projectId, {
        filename: file.name,
        mimeType,
        byteSize: file.size,
        clientMessageId,
    });
    if (!initiated.success || !initiated.upload_id || !initiated.upload_url) {
        return { success: false, error: initiated.error || `Failed to start upload of ${file.name}.` };
    }

    // BAR-308 review: defense-in-depth — only PUT to a GCS signed URL, so a
    // compromised/misconfigured backend can't redirect the file bytes elsewhere.
    try {
        const putHost = new URL(initiated.upload_url).hostname;
        if (putHost !== 'storage.googleapis.com' && !putHost.endsWith('.storage.googleapis.com')) {
            return { success: false, error: 'Upload URL is not a Google Cloud Storage endpoint.' };
        }
    } catch {
        return { success: false, error: 'Upload URL is malformed.' };
    }

    try {
        const putHeaders: Record<string, string> = initiated.required_headers
            ? { ...initiated.required_headers }
            : { 'Content-Type': mimeType };
        const putResponse = await fetch(initiated.upload_url, {
            method: 'PUT', // BAR-308: initiate no longer echoes the constant method
            headers: putHeaders,
            body: file,
        });
        if (!putResponse.ok) {
            return {
                success: false,
                error: `Failed to upload ${file.name} (status ${putResponse.status}).`,
            };
        }
    } catch (error: any) {
        console.error('Error uploading file bytes to signed URL:', error);
        return { success: false, error: error.message };
    }

    return finalizeWorkspaceUpload(workspaceId, initiated.upload_id, userId, {
        filename: file.name,
        mimeType,
        projectId,
        displayName,
        clientMessageId,
    });
};

export const deleteWorkspaceUpload = async (
    workspaceId: string,
    userId: string,
    uploadId: string,
): Promise<{
    success: boolean;
    upload_id?: string;
    events?: Array<Record<string, any>>;
    error?: string;
}> => {
    try {
        const baseUrl = await ensureInitialized();
        const response = await fetch(
            `${baseUrl}/workspaces/${workspaceId}/uploads/${uploadId}?user_id=${encodeURIComponent(userId)}`,
            {
                method: 'DELETE',
                headers: authedHeaders({ 'Content-Type': 'application/json' }),
            },
        );

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            const detail = typeof data.detail === 'string' ? data.detail : `HTTP error! status: ${response.status}`;
            return { success: false, error: detail };
        }
        return {
            success: Boolean(data.success),
            upload_id: data.upload_id,
            events: data.events,
        };
    } catch (error: any) {
        console.error('Error deleting workspace upload:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Result shape for `exportWorkspaceReportPdf`.
 *
 * On success, the file has already been pushed to the user via a
 * synthetic anchor click (no further work needed in the caller). On
 * failure, `error` holds a human-readable message suitable for a toast.
 */
export interface ExportReportPdfResult {
    success: boolean;
    filename?: string;
    error?: string;
}

const getBrowserExportTimezone = (): string | null => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch {
        return null;
    }
};

const getBrowserExportTimezoneOffsetMinutes = (): string | null => {
    try {
        return String(new Date().getTimezoneOffset());
    } catch {
        return null;
    }
};

const buildWorkspaceReportExportUrl = (
    baseUrl: string,
    workspaceId: string,
    reportId: string,
    extension: 'pdf' | 'docx' | 'pptx',
    userId: string,
): string => {
    const params = new URLSearchParams({ user_id: userId });

    const timezone = getBrowserExportTimezone();
    if (timezone) {
        params.set('timezone', timezone);
    }
    const timezoneOffsetMinutes = getBrowserExportTimezoneOffsetMinutes();
    if (timezoneOffsetMinutes) {
        params.set('timezone_offset_minutes', timezoneOffsetMinutes);
    }

    return `${baseUrl}/workspaces/${encodeURIComponent(
        workspaceId,
    )}/reports/${encodeURIComponent(reportId)}/export.${extension}?${params.toString()}`;
};

const filenameFromContentDisposition = (
    disposition: string,
    fallback: string,
): string => {
    const encoded = /filename\*\s*=\s*UTF-8'[^']*'([^;]+)/i.exec(disposition)?.[1]?.trim();
    if (encoded) {
        try {
            return decodeURIComponent(encoded);
        } catch {
            // Fall through to the server-provided ASCII filename.
        }
    }

    const plain = /filename\s*=\s*(?:"([^"]+)"|([^;]+))/i.exec(disposition);
    return plain?.[1] || plain?.[2]?.trim() || fallback;
};

/**
 * Download a workspace report as a PDF.
 *
 * Calls the report library's `GET /workspaces/{id}/reports/{id}/export.pdf`
 * endpoint, receives a `Blob`, and triggers a browser download via a
 * synthetic anchor element. The endpoint sets
 * `Content-Disposition: attachment; filename=...`, which we surface back
 * in the result for callers that want to display the saved name.
 *
 * @param workspaceId - Workspace identifier (format: ws_{project_id})
 * @param reportId    - Report identifier inside the workspace state
 * @param userId      - User identifier for access control (optional; the
 *                      backend derives identity from the auth token, but
 *                      the query param is still sent for backward compat)
 * @param authToken   - Firebase ID token; sent as `Authorization: Bearer …`
 *                      so the backend `require_auth` dependency can
 *                      verify the caller. Optional only so callers in
 *                      not-yet-authenticated render paths don't crash —
 *                      production calls must always pass it.
 */
export const exportWorkspaceReportPdf = async (
    workspaceId: string,
    reportId: string,
    userId: string,
    authToken?: string,
): Promise<ExportReportPdfResult> => {
    try {
        const baseUrl = await ensureInitialized();
        const url = buildWorkspaceReportExportUrl(baseUrl, workspaceId, reportId, 'pdf', userId);
        let bearerToken = await resolveUsableAuthToken(authToken);

        const headers: Record<string, string> = { Accept: 'application/pdf' };
        if (bearerToken) {
            headers.Authorization = `Bearer ${bearerToken}`;
        }

        let response = await fetch(url, {
            method: 'GET',
            // Export auth is bearer-token based. Do not send browser
            // credentials here: dev report-library can legitimately return
            // `Access-Control-Allow-Origin: *`, which browsers reject when
            // credentials mode is `include`.
            credentials: 'omit',
            headers,
        });

        if (response.status === 401) {
            try {
                bearerToken = await refreshAuthToken();
                if (bearerToken) {
                    response = await fetch(url, {
                        method: 'GET',
                        credentials: 'omit',
                        headers: { ...headers, Authorization: `Bearer ${bearerToken}` },
                    });
                }
            } catch {
                // Keep the original 401 response so the normal error
                // formatting below returns the backend's auth detail.
            }
        }

        if (!response.ok) {
            // Try to surface a useful detail from the JSON error body the
            // service returns; fall through to a generic message otherwise.
            let detail = `HTTP ${response.status}`;
            try {
                const body = await response.json();
                if (body && typeof body.detail === 'string') {
                    detail = body.detail;
                }
            } catch {
                // not JSON — keep the HTTP-status fallback
            }
            return { success: false, error: detail };
        }

        // Pull a filename out of Content-Disposition when present so the
        // browser saves with the title the backend chose. Falls back to a
        // sensible default keyed on report_id.
        const disposition = response.headers.get('content-disposition') || '';
        const filename = filenameFromContentDisposition(
            disposition,
            `report-${reportId}.pdf`,
        );

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);

        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = filename;
        anchor.rel = 'noopener';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);

        // Revoke on next tick — Safari occasionally drops the download if
        // the URL is revoked synchronously inside the click handler.
        setTimeout(() => URL.revokeObjectURL(objectUrl), 0);

        return { success: true, filename };
    } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        console.error('Error exporting report PDF:', error);
        return { success: false, error: message };
    }
};

/**
 * Download a finished output-package workspace as a .zip bundle.
 *
 * Calls the report library's `GET /workspaces/{id}/export.zip`, receives a
 * `Blob`, and triggers a browser download. The archive holds the documentation
 * (.md + .pdf), model_definition.json, a run/ bundle (inputs.template.json +
 * README.md), and manifest.json (model ref, per-doc
 * approvals + dependency links, coverage, provenance, runnable references).
 * Mirrors `exportWorkspaceReportPdf` (bearer auth, 401-refresh, anchor click).
 *
 * @param workspaceId - Output-package workspace id.
 * @param userId      - User id for access control (backward-compat query param).
 * @param authToken   - Firebase ID token; sent as `Authorization: Bearer …`.
 */
export const exportOutputPackageZip = async (
    workspaceId: string,
    userId: string,
    authToken?: string,
): Promise<ExportReportPdfResult> => {
    try {
        const baseUrl = await ensureInitialized();
        // release=true makes the SERVER-SIDE readiness gate authoritative: the
        // endpoint only enforces "every referenced artifact approved + current"
        // when release is set, otherwise it skips the check and returns the zip
        // regardless. The UI already disables Export until `ready`, so a release
        // export is always the intended action; sending it closes the direct-
        // call bypass where an unapproved package could be exported.
        const params = new URLSearchParams({ user_id: userId, release: 'true' });
        const url = `${baseUrl}/workspaces/${encodeURIComponent(workspaceId)}/export.zip?${params.toString()}`;
        let bearerToken = await resolveUsableAuthToken(authToken);

        const headers: Record<string, string> = { Accept: 'application/zip' };
        if (bearerToken) {
            headers.Authorization = `Bearer ${bearerToken}`;
        }

        // Zip generation renders a PDF per document server-side, so it can be
        // slow — but a stalled request must not hang the UI forever. Abort after
        // 60s and surface a friendly timeout rather than spinning indefinitely.
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 60000);
        let response: Response;
        try {
            response = await fetch(url, {
                method: 'GET', credentials: 'omit', headers, signal: controller.signal,
            });

            if (response.status === 401) {
                try {
                    bearerToken = await refreshAuthToken();
                    if (bearerToken) {
                        response = await fetch(url, {
                            method: 'GET',
                            credentials: 'omit',
                            headers: { ...headers, Authorization: `Bearer ${bearerToken}` },
                            signal: controller.signal,
                        });
                    }
                } catch {
                    // Keep the original 401 so the error formatting below surfaces it.
                }
            }
        } catch (err: unknown) {
            if (err instanceof Error && err.name === 'AbortError') {
                return { success: false, error: 'Export timed out after 60s — the package may be large; please retry.' };
            }
            throw err;
        } finally {
            clearTimeout(timeoutId);
        }

        if (!response.ok) {
            let detail = `HTTP ${response.status}`;
            try {
                const body = await response.json();
                if (body && typeof body.detail === 'string') {
                    detail = body.detail;
                } else if (body && body.detail && typeof body.detail === 'object') {
                    const message = typeof body.detail.message === 'string'
                        ? body.detail.message
                        : detail;
                    const blocking = Array.isArray(body.detail.blocking_file_ids)
                        ? body.detail.blocking_file_ids.filter((id: unknown) => typeof id === 'string')
                        : [];
                    detail = blocking.length > 0
                        ? `${message} Blocking files: ${blocking.join(', ')}.`
                        : message;
                }
            } catch {
                // not JSON — keep the HTTP-status fallback
            }
            return { success: false, error: detail };
        }

        const disposition = response.headers.get('content-disposition') || '';
        const filename = filenameFromContentDisposition(
            disposition,
            `output-package-${workspaceId}.zip`,
        );

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);

        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = filename;
        anchor.rel = 'noopener';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);

        setTimeout(() => URL.revokeObjectURL(objectUrl), 0);

        return { success: true, filename };
    } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        console.error('Error exporting output package zip:', error);
        return { success: false, error: message };
    }
};

/**
 * Result shape for `exportWorkspaceReportDocx`.
 *
 * Same contract as `ExportReportPdfResult`: success → file already
 * downloaded via synthetic anchor click; failure → human-readable
 * `error` for a toast or tooltip. Kept as a separate type rather than
 * a shared one so the two callers stay independently typed and obvious.
 */
export interface ExportReportDocxResult {
    success: boolean;
    filename?: string;
    error?: string;
}

/**
 * Download a workspace report as a Word (.docx) document.
 *
 * Mirrors `exportWorkspaceReportPdf` against the
 * `GET /workspaces/{id}/reports/{id}/export.docx` endpoint. We
 * deliberately keep the implementation parallel rather than extracting
 * a shared helper — there are only two callers, and each one's set of
 * MIME / filename / log-message constants is small enough that a
 * "download blob" abstraction would add more indirection than it
 * removes.
 *
 * @param workspaceId - Workspace identifier (format: ws_{project_id})
 * @param reportId    - Report identifier inside the workspace state
 * @param userId      - User identifier for access control (optional from
 *                      the backend's perspective; see the PDF helper).
 * @param authToken   - Firebase ID token; sent as `Authorization: Bearer …`
 *                      so the backend `require_auth` dependency can
 *                      verify the caller.
 */
export const exportWorkspaceReportDocx = async (
    workspaceId: string,
    reportId: string,
    userId: string,
    authToken?: string,
): Promise<ExportReportDocxResult> => {
    try {
        const baseUrl = await ensureInitialized();
        const url = buildWorkspaceReportExportUrl(baseUrl, workspaceId, reportId, 'docx', userId);
        let bearerToken = await resolveUsableAuthToken(authToken);

        const headers: Record<string, string> = {
            Accept:
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        };
        if (bearerToken) {
            headers.Authorization = `Bearer ${bearerToken}`;
        }

        let response = await fetch(url, {
            method: 'GET',
            // Export auth is bearer-token based. Do not send browser
            // credentials here: dev report-library can legitimately return
            // `Access-Control-Allow-Origin: *`, which browsers reject when
            // credentials mode is `include`.
            credentials: 'omit',
            headers,
        });

        if (response.status === 401) {
            try {
                bearerToken = await refreshAuthToken();
                if (bearerToken) {
                    response = await fetch(url, {
                        method: 'GET',
                        credentials: 'omit',
                        headers: { ...headers, Authorization: `Bearer ${bearerToken}` },
                    });
                }
            } catch {
                // Keep the original 401 response so the normal error
                // formatting below returns the backend's auth detail.
            }
        }

        if (!response.ok) {
            // Try to surface a useful detail from the JSON error body the
            // service returns; fall through to a generic message otherwise.
            let detail = `HTTP ${response.status}`;
            try {
                const body = await response.json();
                if (body && typeof body.detail === 'string') {
                    detail = body.detail;
                }
            } catch {
                // not JSON — keep the HTTP-status fallback
            }
            return { success: false, error: detail };
        }

        const disposition = response.headers.get('content-disposition') || '';
        const filename = filenameFromContentDisposition(
            disposition,
            `report-${reportId}.docx`,
        );

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);

        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = filename;
        anchor.rel = 'noopener';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);

        // Same Safari workaround as the PDF helper — revoke on next
        // tick rather than synchronously inside the click handler.
        setTimeout(() => URL.revokeObjectURL(objectUrl), 0);

        return { success: true, filename };
    } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        console.error('Error exporting report DOCX:', error);
        return { success: false, error: message };
    }
};

/**
 * Result shape for `exportWorkspaceReportPptx`.
 *
 * Same contract as the PDF / DOCX export results: success → file already
 * downloaded via a synthetic anchor click; failure → human-readable `error`.
 */
export interface ExportReportPptxResult {
    success: boolean;
    filename?: string;
    error?: string;
}

/**
 * Download a workspace report as a PowerPoint (.pptx) presentation.
 *
 * Mirrors `exportWorkspaceReportDocx` against the
 * `GET /workspaces/{id}/reports/{id}/export.pptx` endpoint. The deck is
 * generated server-side (Claude distils the report into a typed slide IR,
 * which is rendered to a brand-styled 16:9 deck), so this helper only needs
 * to stream the resulting blob to the browser like the other two formats.
 *
 * @param workspaceId - Workspace identifier (format: ws_{project_id})
 * @param reportId    - Report identifier inside the workspace state
 * @param userId      - User identifier for access control (optional; see the PDF helper).
 * @param authToken   - Firebase ID token; sent as `Authorization: Bearer …`.
 */
export const exportWorkspaceReportPptx = async (
    workspaceId: string,
    reportId: string,
    userId: string,
    authToken?: string,
): Promise<ExportReportPptxResult> => {
    try {
        const baseUrl = await ensureInitialized();
        const url = buildWorkspaceReportExportUrl(baseUrl, workspaceId, reportId, 'pptx', userId);
        let bearerToken = await resolveUsableAuthToken(authToken);

        const headers: Record<string, string> = {
            Accept:
                'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        };
        if (bearerToken) {
            headers.Authorization = `Bearer ${bearerToken}`;
        }

        let response = await fetch(url, {
            // Export auth is bearer-token based; do not send browser
            // credentials (dev report-library may return ACAO: *).
            method: 'GET',
            credentials: 'omit',
            headers,
        });

        if (response.status === 401) {
            try {
                bearerToken = await refreshAuthToken();
                if (bearerToken) {
                    response = await fetch(url, {
                        method: 'GET',
                        credentials: 'omit',
                        headers: { ...headers, Authorization: `Bearer ${bearerToken}` },
                    });
                }
            } catch {
                // Keep the original 401 so the error formatting below
                // surfaces the backend's auth detail.
            }
        }

        if (!response.ok) {
            let detail = `HTTP ${response.status}`;
            try {
                const body = await response.json();
                if (body && typeof body.detail === 'string') {
                    detail = body.detail;
                }
            } catch {
                // not JSON — keep the HTTP-status fallback
            }
            return { success: false, error: detail };
        }

        const disposition = response.headers.get('content-disposition') || '';
        const filename = filenameFromContentDisposition(
            disposition,
            `report-${reportId}.pptx`,
        );

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);

        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = filename;
        anchor.rel = 'noopener';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);

        setTimeout(() => URL.revokeObjectURL(objectUrl), 0);

        return { success: true, filename };
    } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        console.error('Error exporting report PPTX:', error);
        return { success: false, error: message };
    }
};

/**
 * Result shape for `updateWorkspaceReportContent`.
 *
 * On success, the orchestrator has emitted a REPORT_SET event with
 * actor="user" and `sequence_number` is the new state sequence. The
 * caller should refetch workspace state (or wait for the websocket
 * event push) to observe the updated report content.
 */
export interface UpdateReportContentResult {
    success: boolean;
    sequence_number?: number;
    error?: string;
}

/**
 * Persist a user-edited report (BAR-60).
 *
 * Calls the orchestrator's
 * `PATCH /workspace/{workspace_id}/reports/{report_id}/content`
 * endpoint, which emits a REPORT_SET event with actor="user". The
 * event log is the change history — no separate revisions table.
 *
 * @param workspaceId - Workspace identifier (format: ws_{project_id}).
 * @param reportId    - Report identifier in workspace state
 *                      (format: report_{compartment_id}).
 * @param content     - Full new markdown content from the editor.
 * @param title       - Optional new title. Omit to preserve the
 *                      existing report title.
 * @param authToken   - Firebase ID token; sent as `Authorization: Bearer …`
 *                      so the backend `require_auth` dependency can
 *                      verify the caller.
 */
export const updateWorkspaceReportContent = async (
    workspaceId: string,
    reportId: string,
    content: string,
    title?: string,
    authToken?: string,
): Promise<UpdateReportContentResult> => {
    try {
        await realApi.initialize();
        const baseUrl = realApi.getWorkflowOrchestratorUrl();
        const url = `${baseUrl}/workspace/${encodeURIComponent(
            workspaceId,
        )}/reports/${encodeURIComponent(reportId)}/content`;
        let bearerToken = await resolveUsableAuthToken(authToken);

        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (bearerToken) {
            headers.Authorization = `Bearer ${bearerToken}`;
        }

        const body: Record<string, unknown> = { content };
        if (title !== undefined) {
            body.title = title;
        }

        let response = await fetch(url, {
            method: 'PATCH',
            credentials: 'include',
            headers,
            body: JSON.stringify(body),
        });

        if (response.status === 401) {
            try {
                bearerToken = await refreshAuthToken();
                if (bearerToken) {
                    response = await fetch(url, {
                        method: 'PATCH',
                        credentials: 'include',
                        headers: { ...headers, Authorization: `Bearer ${bearerToken}` },
                        body: JSON.stringify(body),
                    });
                }
            } catch {
                // Keep the original 401 response so the normal error
                // formatting below returns the backend's auth detail.
            }
        }

        if (!response.ok) {
            let detail = `HTTP ${response.status}`;
            try {
                const errBody = await response.json();
                if (errBody && typeof errBody.detail === 'string') {
                    detail = errBody.detail;
                }
            } catch {
                // not JSON — keep the HTTP-status fallback
            }
            return { success: false, error: detail };
        }

        const data = await response.json();
        return {
            success: true,
            sequence_number: typeof data.sequence_number === 'number' ? data.sequence_number : undefined,
        };
    } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        console.error('Error updating report content:', error);
        return { success: false, error: message };
    }
};

/**
 * Result of a per-document content edit.
 */
export interface UpdateDocFileContentResult {
    success: boolean;
    file_id?: string;
    sequence_number?: number;
    /** True when the save was a no-op (content unchanged) — nothing recorded. */
    unchanged?: boolean;
    error?: string;
}

/**
 * Persist a user-edited output-package documentation file (Fix 6).
 *
 * The documentation surface renders a DocFile through the shared report
 * machinery (formatting + the inline editor) and posts the full edited markdown
 * here. Calls the orchestrator's
 * `PATCH /workspace/{workspace_id}/doc-files/{file_id}/content`, which emits a
 * DOC_FILE_SET (last-write-wins) with actor="user" — the doc-file counterpart
 * of the report editor's REPORT_SET. Every dependency link is carried forward
 * and any prior approval is cleared (the applier keeps the content hash so the
 * doc derives "approved, then edited" staleness). The event log is the change
 * history.
 *
 * @param workspaceId - Workspace identifier (format: ws_{project_id}).
 * @param fileId      - The doc file's file_id (e.g. 'model_methodology').
 * @param content     - Full new markdown content from the editor.
 * @param authToken   - Firebase ID token; sent as `Authorization: Bearer …`.
 */
/** One artifact row of the output-package readiness board (ship-gate Step 3). */
export interface PackageReadinessRow {
    file_id: string;
    title?: string | null;
    home_workspace_id?: string;
    source_kind?: string | null;
    status: 'approved' | 'stale' | 'unapproved' | 'missing';
    // The doc was derived from an OLDER version of its model than the model's
    // current content hash — an "approved · model moved → re-review" signal.
    model_moved?: boolean;
    approved_by?: string | null;
    approver_role?: string | null;
    approved_at?: string | null;
}

export interface PackageReadinessResult {
    success: boolean;
    readiness?: PackageReadinessRow[];
    required_file_ids?: string[];
    models?: Array<Record<string, unknown>>;
    package_archive?: {
        package_hash: string;
        released: boolean;
        release_current: boolean;
        released_by?: string | null;
        released_at?: string | null;
    } | null;
    error?: string;
}

/**
 * Fetch the output package's LIVE readiness (ship-gate Step 3): per-artifact
 * status (approved | stale | unapproved | missing) read from each artifact's
 * HOME workspace via the package's document references. Calls the orchestrator's
 * `GET /workspace/{workspace_id}/readiness`. Read-only.
 */
export const getPackageReadiness = async (
    workspaceId: string,
    authToken?: string,
): Promise<PackageReadinessResult> => {
    try {
        await realApi.initialize();
        const baseUrl = realApi.getWorkflowOrchestratorUrl();
        const url = `${baseUrl}/workspace/${encodeURIComponent(workspaceId)}/readiness`;
        let bearerToken = await resolveUsableAuthToken(authToken);

        const headers: Record<string, string> = {};
        if (bearerToken) headers.Authorization = `Bearer ${bearerToken}`;

        let response = await fetch(url, { method: 'GET', credentials: 'include', headers });
        if (response.status === 401) {
            try {
                bearerToken = await refreshAuthToken();
                if (bearerToken) {
                    response = await fetch(url, {
                        method: 'GET',
                        credentials: 'include',
                        headers: { Authorization: `Bearer ${bearerToken}` },
                    });
                }
            } catch {
                // keep the 401 for the error formatting below
            }
        }

        if (!response.ok) {
            let detail = `HTTP ${response.status}`;
            try {
                const errBody = await response.json();
                if (errBody && typeof errBody.detail === 'string') detail = errBody.detail;
            } catch {
                // not JSON — keep the status fallback
            }
            return { success: false, error: detail };
        }

        const data = await response.json();
        return {
            success: true,
            readiness: Array.isArray(data.readiness) ? data.readiness : [],
            required_file_ids: Array.isArray(data.required_file_ids) ? data.required_file_ids : [],
            models: Array.isArray(data.models) ? data.models : [],
            package_archive: data.package_archive && typeof data.package_archive === 'object'
                ? data.package_archive
                : null,
        };
    } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        console.error('Error fetching package readiness:', error);
        return { success: false, error: message };
    }
};

export const updateDocFileContent = async (
    workspaceId: string,
    fileId: string,
    content: string,
    authToken?: string,
): Promise<UpdateDocFileContentResult> => {
    try {
        await realApi.initialize();
        const baseUrl = realApi.getWorkflowOrchestratorUrl();
        const url = `${baseUrl}/workspace/${encodeURIComponent(
            workspaceId,
        )}/doc-files/${encodeURIComponent(fileId)}/content`;
        let bearerToken = await resolveUsableAuthToken(authToken);

        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (bearerToken) {
            headers.Authorization = `Bearer ${bearerToken}`;
        }

        const body = JSON.stringify({ content });

        let response = await fetch(url, {
            method: 'PATCH',
            credentials: 'include',
            headers,
            body,
        });

        if (response.status === 401) {
            try {
                bearerToken = await refreshAuthToken();
                if (bearerToken) {
                    response = await fetch(url, {
                        method: 'PATCH',
                        credentials: 'include',
                        headers: { ...headers, Authorization: `Bearer ${bearerToken}` },
                        body,
                    });
                }
            } catch {
                // Keep the original 401 response for the error formatting below.
            }
        }

        if (!response.ok) {
            let detail = `HTTP ${response.status}`;
            try {
                const errBody = await response.json();
                if (errBody && typeof errBody.detail === 'string') {
                    detail = errBody.detail;
                }
            } catch {
                // not JSON — keep the HTTP-status fallback
            }
            return { success: false, error: detail };
        }

        const data = await response.json();
        return {
            success: true,
            file_id: typeof data.file_id === 'string' ? data.file_id : undefined,
            sequence_number: typeof data.sequence_number === 'number' ? data.sequence_number : undefined,
            unchanged: data.unchanged === true,
        };
    } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        console.error('Error updating doc file content:', error);
        return { success: false, error: message };
    }
};

/**
 * Result of a per-document approval.
 */
export interface ApproveDocFileResult {
    success: boolean;
    file_id?: string;
    approved_by?: string;
    sequence_number?: number;
    error?: string;
}

/**
 * Record a HUMAN per-document approval of one output-package doc file.
 *
 * Calls the orchestrator's
 * `POST /workspace/{workspace_id}/doc-files/{file_id}/approve`, which emits a
 * DOC_FILE_APPROVED event with actor="user". Approval is human-only by design:
 * there is no agent tool for it, so the orchestrator can never approve its own
 * work — only this user-authenticated call can. A later edit to the file clears
 * its approval (the reducer resets approved_by).
 *
 * Per-document: approve one file at a time (call once per file, or loop for a
 * batch) — the timing is entirely the user's choice.
 *
 * @param workspaceId  - Output-package workspace id.
 * @param fileId       - The doc file's file_id (e.g. 'model_methodology').
 * @param approverRole - Optional role of the approver (e.g. 'model_owner').
 * @param authToken    - Firebase ID token; sent as `Authorization: Bearer …`.
 */
export const approveDocFile = async (
    workspaceId: string,
    fileId: string,
    approverRole?: string,
    authToken?: string,
): Promise<ApproveDocFileResult> => {
    try {
        await realApi.initialize();
        const baseUrl = realApi.getWorkflowOrchestratorUrl();
        const url = `${baseUrl}/workspace/${encodeURIComponent(
            workspaceId,
        )}/doc-files/${encodeURIComponent(fileId)}/approve`;
        let bearerToken = await resolveUsableAuthToken(authToken);

        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (bearerToken) {
            headers.Authorization = `Bearer ${bearerToken}`;
        }

        const body: Record<string, unknown> = {};
        if (approverRole !== undefined) {
            body.approver_role = approverRole;
        }

        let response = await fetch(url, {
            method: 'POST',
            credentials: 'include',
            headers,
            body: JSON.stringify(body),
        });

        if (response.status === 401) {
            try {
                bearerToken = await refreshAuthToken();
                if (bearerToken) {
                    response = await fetch(url, {
                        method: 'POST',
                        credentials: 'include',
                        headers: { ...headers, Authorization: `Bearer ${bearerToken}` },
                        body: JSON.stringify(body),
                    });
                }
            } catch {
                // Keep the original 401 so the error formatting below surfaces
                // the backend's auth detail.
            }
        }

        if (!response.ok) {
            let detail = `HTTP ${response.status}`;
            try {
                const errBody = await response.json();
                if (errBody && typeof errBody.detail === 'string') {
                    detail = errBody.detail;
                }
            } catch {
                // not JSON — keep the HTTP-status fallback
            }
            return { success: false, error: detail };
        }

        const data = await response.json();
        return {
            success: true,
            file_id: typeof data.file_id === 'string' ? data.file_id : fileId,
            approved_by: typeof data.approved_by === 'string' ? data.approved_by : undefined,
            sequence_number: typeof data.sequence_number === 'number' ? data.sequence_number : undefined,
        };
    } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        console.error('Error approving doc file:', error);
        return { success: false, error: message };
    }
};

// =============================================================================
// Model Spec — first-class SpecRequirement payloads (workspace-outputs Phase 1)
// =============================================================================

/** Typed grounding ref from a requirement to a research Finding. */
export interface SpecFindingRef {
    workspace_id: string;
    finding_id: string;
    citation_excerpt?: string | null;
}

/**
 * This build workspace's status-overlay entry for one requirement.
 * `null` on the requirement means the build hasn't touched it yet.
 */
export interface SpecRequirementStatusInfo {
    done: boolean;
    node_id?: string | null;
    impl_note?: string | null;
    actor?: string | null;
    at?: string | null;
    /** Statement changed upstream after the done-flip → "done (stale)". */
    stale?: boolean;
}

export interface SpecRequirementItem {
    requirement_id: string;
    statement: string;
    withdrawn: boolean;
    finding_refs: SpecFindingRef[];
    status: SpecRequirementStatusInfo | null;
    validation_verdict: 'approved' | 'flagged' | 'rejected' | null;
    // Consumer-routed requirements — this consumer workspace's projection,
    // computed server-side relative to the requesting workspace.
    /** Stable consumer ids this requirement is routed to ([] = unrouted). */
    consumers?: string[];
    /** True if routed to THIS workspace, "all", or unrouted (shown-to-all). */
    routed_to_me?: boolean;
    /** True when no routing is set — shown to all consumers as a fail-safe. */
    unrouted?: boolean;
}

export interface SpecSectionPayload {
    section: string;
    requirements: SpecRequirementItem[];
}

/** Human sign-off pin — valid iff approved_version_hash === version_hash. */
export interface SpecApprovalInfo {
    approved_version_hash?: string;
    approved_by?: string;
    approved_at?: string;
}

/** Wire code for a requirements read whose spec resolution did not complete. */
export const SPEC_RESOLUTION_FAILED = 'requirement_spec_resolution_failed';
/** Wire code for an upstream that answered, and answered "no". */
export const SPEC_RESOLUTION_FORBIDDEN = 'requirement_spec_forbidden';

/**
 * A requirements read that FAILED, as opposed to one that found no spec.
 *
 * The backend now fails closed rather than serving the legacy markdown shape
 * over an upstream it could not read (`done_basis: legacy_markdown`,
 * `specs: []`). That fix is only worth anything if the client keeps the
 * distinction: catching the failure and rendering an empty spec list tells the
 * same lie one layer up, which is exactly what `setSpecs([])` did.
 */
export class SpecResolutionError extends Error {
    readonly code: string;
    readonly status: number;
    readonly reason: string;
    readonly workspaces: string[];
    readonly retriable: boolean;

    constructor(init: {
        code: string;
        status: number;
        reason: string;
        workspaces?: string[];
        retriable: boolean;
        message: string;
    }) {
        super(init.message);
        this.name = 'SpecResolutionError';
        this.code = init.code;
        this.status = init.status;
        this.reason = init.reason;
        this.workspaces = init.workspaces ?? [];
        this.retriable = init.retriable;
    }
}

/** Build a typed error from a failed requirements response, detail and all. */
async function specResolutionError(response: Response): Promise<SpecResolutionError> {
    let detail: any = null;
    try {
        const body = await response.json();
        detail = body?.detail ?? body;
    } catch {
        detail = null;
    }
    const code = typeof detail?.code === 'string' ? detail.code : SPEC_RESOLUTION_FAILED;
    return new SpecResolutionError({
        code,
        status: response.status,
        reason: typeof detail?.reason === 'string' ? detail.reason : '',
        workspaces: Array.isArray(detail?.workspaces) ? detail.workspaces : [],
        // Trust the server's own verdict; fall back to "retry a 5xx" only when
        // it did not say. A 403-shaped refusal must never advertise a retry.
        retriable: typeof detail?.retriable === 'boolean'
            ? detail.retriable
            : response.status >= 500 && code !== SPEC_RESOLUTION_FORBIDDEN,
        message: typeof detail?.message === 'string' && detail.message
            ? detail.message
            : `Failed to load requirements: ${response.status}`,
    });
}

/**
 * One consumed Model Spec (per upstream research workspace), as returned in
 * the `specs` array of GET /projects/{pid}/workspaces/{build_ws}/requirements.
 */
export interface ConsumedSpec {
    source_workspace_id: string;
    source_title: string;
    version_hash: string | null;
    approval: SpecApprovalInfo | null;
    /** False when a re-commit changed the spec after sign-off (superseded). */
    approval_current: boolean;
    /** True when the upstream source is wired in the plan but has not yet
     *  committed any `- [ ] [REQ-NNN]` lines — render as a pending group. */
    pending?: boolean;
    progress: { done: number; total: number; stale: number };
    /** Per-consumer routing advisory for the requesting workspace (deterministic;
     *  demote-don't-hide — nothing is filtered out of `sections`). */
    routing?: {
        routed_to_me: number;
        unrouted: number;
        for_others_only: number;
        total: number;
    };
    sections: SpecSectionPayload[];
}

export interface ApproveSpecResult {
    success: boolean;
    approved_version_hash?: string;
    approved_by?: string;
    approved_at?: string;
    error?: string;
}

/**
 * Record HUMAN sign-off of a research workspace's Model Spec.
 *
 * Calls the orchestrator's `POST /workspace/{source_ws_id}/spec/approve`,
 * which emits SPEC_APPROVED into the research workspace's stream. The
 * approval is a version pin: a re-commit that changes the requirement set
 * supersedes it by construction (no reset choreography). Human-only by
 * design — no agent tool exists for this (same pattern as approveDocFile).
 *
 * @param sourceWorkspaceId - The AUTHORING research workspace id (not the build).
 * @param authToken         - Firebase ID token; sent as `Authorization: Bearer …`.
 */
export const approveSpec = async (
    sourceWorkspaceId: string,
    authToken?: string,
): Promise<ApproveSpecResult> => {
    try {
        await realApi.initialize();
        const baseUrl = realApi.getWorkflowOrchestratorUrl();
        const url = `${baseUrl}/workspace/${encodeURIComponent(
            sourceWorkspaceId,
        )}/spec/approve`;
        let bearerToken = await resolveUsableAuthToken(authToken);

        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (bearerToken) {
            headers.Authorization = `Bearer ${bearerToken}`;
        }

        let response = await fetch(url, {
            method: 'POST',
            credentials: 'include',
            headers,
            body: JSON.stringify({}),
        });

        if (response.status === 401) {
            try {
                bearerToken = await refreshAuthToken();
                if (bearerToken) {
                    response = await fetch(url, {
                        method: 'POST',
                        credentials: 'include',
                        headers: { ...headers, Authorization: `Bearer ${bearerToken}` },
                        body: JSON.stringify({}),
                    });
                }
            } catch {
                // Keep the original 401 so the error formatting below surfaces
                // the backend's auth detail.
            }
        }

        if (!response.ok) {
            let detail = `HTTP ${response.status}`;
            try {
                const errBody = await response.json();
                if (errBody && typeof errBody.detail === 'string') {
                    detail = errBody.detail;
                }
            } catch {
                // not JSON — keep the HTTP-status fallback
            }
            return { success: false, error: detail };
        }

        const data = await response.json();
        return {
            success: true,
            approved_version_hash: typeof data.approved_version_hash === 'string' ? data.approved_version_hash : undefined,
            approved_by: typeof data.approved_by === 'string' ? data.approved_by : undefined,
            approved_at: typeof data.approved_at === 'string' ? data.approved_at : undefined,
        };
    } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        console.error('Error approving spec:', error);
        return { success: false, error: message };
    }
};

// BAR-192 Phase E — submit human accept/reject decisions over adjudicated
// grounding verdicts. One POST = one workspace event carrying the whole
// batch; attribution is stamped server-side from the auth context.
export interface GroundingReviewDecisionInput {
    kind: string;
    verdict: string;
    decision:
        | 'accepted'
        | 'rejected'
        | 'accepted_internal'
        | 'fix_requested'
        | 'source_requested'
        | 'good_enough';
    line_sha1?: string;
    marker_id?: string;
}

export const submitGroundingReview = async (
    workspaceId: string,
    userEmail: string,
    decisions: GroundingReviewDecisionInput[],
    compartmentId?: string,
): Promise<{ success: boolean; sequence_number?: number; error?: string }> => {
    try {
        const reportLibraryUrl = await ensureInitialized();
        const response = await fetch(
            `${reportLibraryUrl}/workspaces/${encodeURIComponent(workspaceId)}/grounding-review?user_id=${encodeURIComponent(userEmail)}`,
            {
                method: 'POST',
                headers: authedHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({
                    decisions,
                    ...(compartmentId ? { compartment_id: compartmentId } : {}),
                }),
            },
        );
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return { success: true, sequence_number: data.sequence_number };
    } catch (error) {
        console.error('Error submitting grounding review:', error);
        return { success: false, error: error instanceof Error ? error.message : 'Failed to submit review' };
    }
};

// Full report document type
export interface ReportDocument {
    report_id: string;
    user_id: string;
    title: string;
    query: string;
    service_type: string;
    report_type: string;
    /**
     * Saved reports use either finished markdown (string) or the legacy
     * structured-section document shape.
     */
    report_content: string | {
        title?: string;
        markdown?: string;
        source?: string;
        executive_summary?: string;
        sections?: Array<{
            type: 'heading' | 'paragraph' | 'list' | 'table';
            content: string | string[] | Record<string, any>;
            level?: number;
            ordered?: boolean;
        }>;
    };
    references: Array<{
        id: string;
        title: string;
        summary: string;
        reference_type: string;
        document?: string;
        document_reference?: string;
        detail?: string;
        logical_connections?: string[];
        connection_type?: string;
        chain_position?: string;
    }>;
    tags: string[];
    status: string;
    metadata: {
        created_at: string;
        updated_at: string;
    };
}

export type DisplayableReportDocument =
    Pick<ReportDocument, 'report_id' | 'title' | 'report_content'>
    & Partial<Omit<ReportDocument, 'report_id' | 'title' | 'report_content'>>;

export type ReportOpenFailureReason =
    | 'not_found'
    | 'unauthorized'
    | 'server'
    | 'malformed'
    | 'network';

export type GetReportResult =
    | { success: true; report: DisplayableReportDocument }
    | { success: false; reason: ReportOpenFailureReason; error: string };

function isUsableReportDocument(value: unknown): value is DisplayableReportDocument {
    if (!value || typeof value !== 'object') return false;
    const report = value as Partial<ReportDocument>;
    const content = report.report_content;
    const hasDisplayableContent = typeof content === 'string'
        ? content.trim().length > 0
        : !!content
            && typeof content === 'object'
            && !Array.isArray(content)
            && (
                (typeof content.markdown === 'string' && content.markdown.trim().length > 0)
                || (
                    typeof content.executive_summary === 'string'
                    && content.executive_summary.trim().length > 0
                )
                || (Array.isArray(content.sections) && content.sections.length > 0)
            );

    return (
        typeof report.report_id === 'string'
        && report.report_id.trim().length > 0
        && typeof report.title === 'string'
        && report.title.trim().length > 0
        && hasDisplayableContent
    );
}

export const getReport = async (reportId: string, userEmail: string): Promise<GetReportResult> => {
    try {
        const reportLibraryUrl = await ensureInitialized();
        const response = await fetch(`${reportLibraryUrl}/reports/${encodeURIComponent(reportId)}?user_id=${encodeURIComponent(userEmail)}`, {
            method: 'GET',
            headers: authedHeaders(),
        });

        if (!response.ok) {
            if (response.status === 404) {
                return { success: false, reason: 'not_found', error: 'Report not found' };
            }
            if (response.status === 401 || response.status === 403) {
                return {
                    success: false,
                    reason: 'unauthorized',
                    error: 'You do not have access to this report',
                };
            }
            return {
                success: false,
                reason: 'server',
                error: 'The report service is unavailable',
            };
        }

        let data: any;
        try {
            data = await response.json();
        } catch (error) {
            console.error('Invalid report response:', error);
            return {
                success: false,
                reason: 'malformed',
                error: 'The report data is incomplete',
            };
        }

        if (!isUsableReportDocument(data?.report)) {
            return {
                success: false,
                reason: 'malformed',
                error: 'The report data is incomplete',
            };
        }

        return { success: true, report: data.report };
    } catch (error) {
        console.error('Error fetching report:', error);
        return {
            success: false,
            reason: 'network',
            error: 'Could not reach the report service',
        };
    }
};

// =============================================================================
// PROJECT SHARING / COLLABORATION API
// =============================================================================

export interface ProjectMember {
    user_id: string;
    email: string;
    name: string;
    added_at: string;
    added_by: string;
}

export interface ProjectMembership {
    project_id: string;
    owner_id: string;
    owner_email: string;
    owner_name: string;
    project_name: string;
    members: ProjectMember[];
    created_at: string;
}

export interface SharedProjectSummary {
    project_id: string;
    name: string;
    description: string;
    owner_id: string;
    owner_name: string;
    owner_email: string;
    project_type: string;
    node_count: number;
    edge_count: number;
    status: string;
    created_at: string;
    updated_at: string;
}

export const getProjectMembers = async (
    projectId: string,
    userId: string
): Promise<{ success: boolean; membership?: ProjectMembership; error?: string }> => {
    try {
        const baseUrl = await ensureInitialized();
        const response = await fetch(
            `${baseUrl}/projects/${projectId}/members?user_id=${encodeURIComponent(userId)}`,
            { method: 'GET', headers: authedHeaders({ 'Content-Type': 'application/json' }) }
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        return { success: true, membership: data.membership };
    } catch (error: any) {
        console.error('Error getting project members:', error);
        return { success: false, error: error.message };
    }
};

export const addProjectMember = async (
    projectId: string,
    userId: string,
    email: string
): Promise<{ success: boolean; member?: ProjectMember; error?: string }> => {
    try {
        const baseUrl = await ensureInitialized();
        const response = await fetch(
            `${baseUrl}/projects/${projectId}/members?user_id=${encodeURIComponent(userId)}`,
            {
                method: 'POST',
                headers: authedHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ email }),
            }
        );
        if (!response.ok) {
            const errData = await response.json().catch(() => ({}));
            throw new Error(errData.detail || `HTTP ${response.status}`);
        }
        const data = await response.json();
        return { success: true, member: data.member };
    } catch (error: any) {
        console.error('Error adding project member:', error);
        return { success: false, error: error.message };
    }
};

export const removeProjectMember = async (
    projectId: string,
    userId: string,
    memberUserId: string
): Promise<{ success: boolean; error?: string }> => {
    try {
        const baseUrl = await ensureInitialized();
        const response = await fetch(
            `${baseUrl}/projects/${projectId}/members/${memberUserId}?user_id=${encodeURIComponent(userId)}`,
            { method: 'DELETE', headers: authedHeaders({ 'Content-Type': 'application/json' }) }
        );
        if (!response.ok) {
            const errData = await response.json().catch(() => ({}));
            throw new Error(errData.detail || `HTTP ${response.status}`);
        }
        return { success: true };
    } catch (error: any) {
        console.error('Error removing project member:', error);
        return { success: false, error: error.message };
    }
};

export const listSharedProjects = async (
    userId: string
): Promise<{ success: boolean; projects?: SharedProjectSummary[]; error?: string }> => {
    try {
        const baseUrl = await ensureInitialized();
        const response = await fetch(
            `${baseUrl}/users/${encodeURIComponent(userId)}/shared-projects`,
            { method: 'GET', headers: authedHeaders({ 'Content-Type': 'application/json' }) }
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        return { success: true, projects: data.projects };
    } catch (error: any) {
        console.error('Error listing shared projects:', error);
        return { success: false, error: error.message };
    }
};

// =============================================================================
// USER PREFERENCES (per-user, per-project)
// =============================================================================

export const getUserPrefs = async (
    projectId: string,
    userEmail: string
): Promise<{ success: boolean; prefs?: Record<string, any> }> => {
    try {
        const baseUrl = await ensureInitialized();
        const response = await fetch(
            `${baseUrl}/projects/${projectId}/user-prefs/${encodeURIComponent(userEmail)}`,
            { headers: authedHeaders() }
        );
        if (!response.ok) return { success: false };
        const data = await response.json();
        return { success: true, prefs: data.prefs };
    } catch {
        return { success: false };
    }
};

export const setUserPrefs = async (
    projectId: string,
    userEmail: string,
    prefs: Record<string, any>
): Promise<{ success: boolean }> => {
    try {
        const baseUrl = await ensureInitialized();
        const response = await fetch(
            `${baseUrl}/projects/${projectId}/user-prefs/${encodeURIComponent(userEmail)}`,
            {
                method: 'PUT',
                headers: authedHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify(prefs),
            }
        );
        return { success: response.ok };
    } catch {
        return { success: false };
    }
};

export default realApi;
